/**
 * Intent parser — sends user speech to the Worker API (which proxies to Claude).
 * Phase 2: Uses Cloudflare Worker so the API key stays server-side.
 * Falls back to local regex matching if Worker is unavailable.
 *
 * Fast-query shortcuts bypass the AI entirely for common educational queries,
 * providing instant, deterministic results with zero latency.
 */
import { getR2Url } from '../data/query-builder.js'
import { iso3ToIso2 } from '../utils/iso-codes.js'
import globeSwitcher from '../globe/globe-switcher.js'
import { findLunarLocation } from '../data/lunar-data.js'
import globeContext from '../state/globe-context.js'
import { normalizeCountryName } from '../config.js'
import { resolveRegionToken } from '../utils/geo-utils.js'
import { tryConceptMatch } from './concept-registry.js'
import { classifyTurn } from './conversation-router.js'
import { RASTER_PATTERNS } from './raster-patterns.js'
import { US_STATES } from './ops/clip-to-region.js'
import { licenseHeaders } from '../utils/license.js'
import { classroomHeaders } from '../utils/classroom.js'
import synonymDict from '../../data/admin/synonym_dictionary.json'

const API_URL = import.meta.env.VITE_API_URL || ''

// ── Session history ring buffer (multi-turn context) ─────────────
const MAX_HISTORY = 5 // keep last 5 exchanges
const sessionHistory = [] // { role, content }[]

function addToHistory(role, content) {
  sessionHistory.push({ role, content })
  // Trim to MAX_HISTORY pairs (user + assistant = 2 entries each)
  while (sessionHistory.length > MAX_HISTORY * 2) {
    sessionHistory.shift()
  }
}

// ── Fast-query shortcuts ──────────────────────────────────────────
// Pattern-matched BEFORE the AI call. Instant, deterministic, reliable.
// Each entry: [regex, intent-builder function]
// POI category keywords for fast-query matching (must stay in sync with QueryRouter.POI_KEYWORDS)
const POI_WORDS = [
  'hospitals?',
  'clinics?',
  'medical\\s+centers?',
  'schools?',
  'universit(?:y|ies)',
  'colleges?',
  'airports?',
  'museums?',
  'art\\s+galler(?:y|ies)',
  'librar(?:y|ies)',
  'mosques?',
  'temples?',
  'churches?',
  'synagogues?',
  'parks?',
  'national\\s+parks?',
  'zoos?',
  'embass(?:y|ies)',
  'train\\s+stations?',
  'bus\\s+stations?',
  'stadiums?',
  'ports?',
  'marinas?',
  'caf[eé]s?',
  'coffee\\s+shops?',
  'tea\\s+rooms?',
  'bubble\\s+teas?',
  'restaurants?',
  'hotels?',
  'motels?',
  'hostels?',
  'resorts?',
  'bars?',
  'pubs?',
  'nightclubs?',
  'bake(?:ry|ries)',
  'pharmac(?:y|ies)',
  'drugstores?',
  'banks?',
  'supermarkets?',
  'grocery\\s+stores?',
  'gas\\s+stations?',
  'petrol\\s+stations?',
  'gyms?',
  'fitness\\s+centers?',
  'cinemas?',
  'movie\\s+theaters?',
  'post\\s+offices?',
  'fire\\s+(?:stations?|departments?)',
  'police\\s+(?:stations?|departments?)',
  'beaches?',
  'campgrounds?',
  'camp\\s+sites?',
  'landmarks?',
  'castles?',
  'palaces?',
  'lighthouses?',
  'monuments?',
  'dentists?',
  'doctors?',
  'veterinarians?',
  'vets?',
  'golf\\s+courses?',
  'swimming\\s+pools?',
  'bowling\\s+alleys?',
  'ice\\s+rinks?',
  'amusement\\s+parks?',
  'theme\\s+parks?',
  'water\\s+parks?',
  'aquariums?',
  'ski\\s+resorts?',
  'winer(?:y|ies)',
  'vineyards?',
  'brewer(?:y|ies)',
  'bookstores?',
  'book\\s+stores?',
  'shopping\\s+malls?',
  'botanical\\s+gardens?',
].join('|')

const POI_CATEGORY_MAP = {
  hospital: 'hospital',
  hospitals: 'hospital',
  clinic: 'hospital',
  clinics: 'hospital',
  'medical center': 'hospital',
  'medical centers': 'hospital',
  school: 'school',
  schools: 'school',
  university: 'university',
  universities: 'university',
  college: 'university',
  colleges: 'university',
  airport: 'airport',
  airports: 'airport',
  museum: 'museum',
  museums: 'museum',
  'art gallery': 'museum',
  'art galleries': 'museum',
  library: 'library',
  libraries: 'library',
  mosque: 'mosque',
  mosques: 'mosque',
  temple: 'temple',
  temples: 'temple',
  church: 'church',
  churches: 'church',
  synagogue: 'synagogue',
  synagogues: 'synagogue',
  park: 'park',
  parks: 'park',
  'national park': 'park',
  'national parks': 'park',
  zoo: 'zoo',
  zoos: 'zoo',
  embassy: 'embassy',
  embassies: 'embassy',
  'train station': 'train_station',
  'train stations': 'train_station',
  'bus station': 'bus_station',
  'bus stations': 'bus_station',
  stadium: 'stadium',
  stadiums: 'stadium',
  port: 'port',
  ports: 'port',
  marina: 'marina',
  marinas: 'marina',
  cafe: 'cafe',
  cafes: 'cafe',
  café: 'cafe',
  cafés: 'cafe',
  'coffee shop': 'cafe',
  'coffee shops': 'cafe',
  'tea room': 'tea_room',
  'tea rooms': 'tea_room',
  'bubble tea': 'tea_room',
  'bubble teas': 'tea_room',
  restaurant: 'restaurant',
  restaurants: 'restaurant',
  hotel: 'hotel',
  hotels: 'hotel',
  motel: 'hotel',
  motels: 'hotel',
  hostel: 'hotel',
  hostels: 'hotel',
  resort: 'hotel',
  resorts: 'hotel',
  bar: 'bar',
  bars: 'bar',
  pub: 'bar',
  pubs: 'bar',
  nightclub: 'bar',
  bakery: 'bakery',
  bakeries: 'bakery',
  pharmacy: 'pharmacy',
  pharmacies: 'pharmacy',
  drugstore: 'pharmacy',
  bank: 'bank',
  banks: 'bank',
  supermarket: 'supermarket',
  supermarkets: 'supermarket',
  'grocery store': 'supermarket',
  'grocery stores': 'supermarket',
  'gas station': 'gas_station',
  'gas stations': 'gas_station',
  'petrol station': 'gas_station',
  'petrol stations': 'gas_station',
  gym: 'gym',
  gyms: 'gym',
  'fitness center': 'gym',
  cinema: 'cinema',
  cinemas: 'cinema',
  'movie theater': 'cinema',
  'movie theaters': 'cinema',
  'post office': 'post_office',
  'post offices': 'post_office',
  'fire station': 'fire_department',
  'fire stations': 'fire_department',
  'fire department': 'fire_department',
  'police station': 'police_department',
  'police stations': 'police_department',
  beach: 'beach',
  beaches: 'beach',
  campground: 'campground',
  campgrounds: 'campground',
  'camp site': 'campground',
  landmark: 'landmark',
  landmarks: 'landmark',
  castle: 'castle',
  castles: 'castle',
  palace: 'castle',
  palaces: 'castle',
  lighthouse: 'lighthouse',
  lighthouses: 'lighthouse',
  monument: 'monument',
  monuments: 'monument',
  dentist: 'dentist',
  dentists: 'dentist',
  doctor: 'doctor',
  doctors: 'doctor',
  veterinarian: 'veterinarian',
  veterinarians: 'veterinarian',
  vet: 'veterinarian',
  vets: 'veterinarian',
  'golf course': 'golf_course',
  'golf courses': 'golf_course',
  'swimming pool': 'swimming_pool',
  'swimming pools': 'swimming_pool',
  'bowling alley': 'bowling_alley',
  'bowling alleys': 'bowling_alley',
  'ice rink': 'ice_skating_rink',
  'ice rinks': 'ice_skating_rink',
  'amusement park': 'amusement_park',
  'amusement parks': 'amusement_park',
  'theme park': 'amusement_park',
  'water park': 'water_park',
  'water parks': 'water_park',
  aquarium: 'aquarium',
  aquariums: 'aquarium',
  'ski resort': 'ski_resort',
  'ski resorts': 'ski_resort',
  winery: 'winery',
  wineries: 'winery',
  vineyard: 'winery',
  brewery: 'brewery',
  breweries: 'brewery',
  bookstore: 'bookstore',
  bookstores: 'bookstore',
  'book store': 'bookstore',
  'shopping mall': 'shopping_mall',
  'shopping malls': 'shopping_mall',
  'botanical garden': 'botanical_garden',
  'botanical gardens': 'botanical_garden',
}

const FAST_QUERIES = [
  // ── Reset command ──────────────────────────────────────────────
  [/^(?:reset|clear|start\s+over)$/i, () => ({ type: 'reset', _fastQuery: true })],

  // ── Point-to-point routing (HERE Routing API) ─────────────────
  // MUST be before geocoding and navigation patterns.
  // CL-054: multi-modal — car (default), pedestrian, bicycle, truck, publicTransport
  // "Get directions from X to Y" / "Route from X to Y" / "Drive from X to Y"
  [
    /^(?:get\s+)?directions\s+from\s+(.+?)\s+to\s+(.+)/i,
    (m) => _routeQuery(m[1].trim(), m[2].trim()),
  ],
  [/^route\s+from\s+(.+?)\s+to\s+(.+)/i, (m) => _routeQuery(m[1].trim(), m[2].trim())],
  [
    /^how\s+(?:do\s+I|can\s+I|to)\s+get\s+from\s+(.+?)\s+to\s+(.+?)[\s?]*$/i,
    (m) => _routeQuery(m[1].trim(), m[2].trim()),
  ],
  [/^navigate\s+from\s+(.+?)\s+to\s+(.+)/i, (m) => _routeQuery(m[1].trim(), m[2].trim())],
  [/^drive\s+from\s+(.+?)\s+to\s+(.+)/i, (m) => _routeQuery(m[1].trim(), m[2].trim(), 'car')],
  [/^walk\s+from\s+(.+?)\s+to\s+(.+)/i, (m) => _routeQuery(m[1].trim(), m[2].trim(), 'pedestrian')],
  [/^bike\s+from\s+(.+?)\s+to\s+(.+)/i, (m) => _routeQuery(m[1].trim(), m[2].trim(), 'bicycle')],
  [/^cycle\s+from\s+(.+?)\s+to\s+(.+)/i, (m) => _routeQuery(m[1].trim(), m[2].trim(), 'bicycle')],
  // "How long to walk/drive/bike/cycle from X to Y?"
  [
    /^how\s+(?:long|many\s+(?:minutes|hours|km|kilometers|miles))\s+to\s+walk\s+from\s+(.+?)\s+to\s+(.+?)[\s?]*$/i,
    (m) => _routeQuery(m[1].trim(), m[2].trim(), 'pedestrian'),
  ],
  [
    /^how\s+(?:long|many\s+(?:minutes|hours|km|kilometers|miles))\s+to\s+(?:bike|cycle)\s+from\s+(.+?)\s+to\s+(.+?)[\s?]*$/i,
    (m) => _routeQuery(m[1].trim(), m[2].trim(), 'bicycle'),
  ],
  [
    /^how\s+(?:long|many\s+(?:minutes|hours|km|kilometers|miles))\s+to\s+drive\s+from\s+(.+?)\s+to\s+(.+?)[\s?]*$/i,
    (m) => _routeQuery(m[1].trim(), m[2].trim(), 'car'),
  ],
  // Truck / freight
  [
    /^(?:truck|freight|lorry|HGV)\s+(?:route\s+|directions\s+)?from\s+(.+?)\s+to\s+(.+)/i,
    (m) => _routeQuery(m[1].trim(), m[2].trim(), 'truck'),
  ],
  // Public transit / bus / train / subway / metro / rail
  [
    /^(?:take\s+)?(?:public\s+)?(?:transit|transportation)\s+from\s+(.+?)\s+to\s+(.+)/i,
    (m) => _routeQuery(m[1].trim(), m[2].trim(), 'publicTransport'),
  ],
  [
    /^(?:take\s+(?:a\s+|the\s+)?)?(?:bus|train|subway|metro|rail)\s+from\s+(.+?)\s+to\s+(.+)/i,
    (m) => _routeQuery(m[1].trim(), m[2].trim(), 'publicTransport'),
  ],

  // ── Projection: globe ↔ flat map ─────────────────────────
  // Kept ahead of navigation/geocoding so "make it flat" / "show the globe"
  // aren't read as place names.
  [
    /\b(?:flat\s+map|make\s+it\s+flat|flatten\s+(?:the\s+)?(?:map|globe|earth|world)|unroll\s+(?:the\s+)?(?:globe|earth|world))\b/i,
    () => ({ type: 'projection', mode: 'flat', _fastQuery: true }),
  ],
  [
    /(?:show|make|give\s+me)\s+(?:me\s+)?(?:a|the|it)\s+(?:a\s+)?(?:round\s+)?globe\b|make\s+(?:it|the\s+map)\s+round\b/i,
    () => ({ type: 'projection', mode: 'globe', _fastQuery: true }),
  ],

  // ── Compass rose (cardinal directions) ───────────────────
  [
    /(?:hide|turn\s+off|remove)\s+(?:the\s+)?compass(?:\s+rose)?\b/i,
    () => ({ type: 'compass', action: 'off', _fastQuery: true }),
  ],
  [
    /(?:show|display|give\s+me)\s+(?:me\s+)?(?:the\s+)?(?:compass(?:\s+rose)?|cardinal\s+directions)\b/i,
    () => ({ type: 'compass', action: 'on', _fastQuery: true }),
  ],

  // ── Stack mode (CL-173, roadmap H3.2b) — persistent layer stacking ──
  // Voice parity with the header Layers button and the sidebar.
  [
    /^(?:turn\s+on|enable|start|activate)\s+(?:layer\s+)?stack(?:ing)?\s*(?:mode)?$|^stack\s+mode\s+on$|^keep\s+(?:my|the)\s+layers$/i,
    () => ({ type: 'layer_control', action: 'stack_on', _fastQuery: true }),
  ],
  [
    /^(?:turn\s+off|disable|stop|deactivate)\s+(?:layer\s+)?stack(?:ing)?\s*(?:mode)?$|^stack\s+mode\s+off$/i,
    () => ({ type: 'layer_control', action: 'stack_off', _fastQuery: true }),
  ],

  // ── Coordinate grid / graticule (CL-170, Grade-5 lat/long) ──
  // "coordinate grid", "lat/long grid", "latitude and longitude grid".
  // No collision with the geographic_lines patterns — those require the
  // word "lines" ("Show the latitude lines").
  [
    /(?:hide|turn\s+off|remove)\s+(?:the\s+)?(?:coordinate|lat(?:itude)?\s*(?:\/|&|and|-)?\s*long(?:itude)?)\s+grid\b/i,
    () => ({ type: 'graticule', action: 'off', _fastQuery: true }),
  ],
  [
    /(?:show|display|turn\s+on|draw)\s+(?:me\s+)?(?:the\s+)?(?:coordinate|lat(?:itude)?\s*(?:\/|&|and|-)?\s*long(?:itude)?)\s+grid\b/i,
    () => ({ type: 'graticule', action: 'on', _fastQuery: true }),
  ],
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?graticule\b/i,
    () => ({ type: 'graticule', action: 'on', _fastQuery: true }),
  ],
  [
    /^(?:show|display)\s+(?:me\s+)?latitude\s+and\s+longitude\s*$/i,
    () => ({ type: 'graticule', action: 'on', _fastQuery: true }),
  ],

  // ── Coordinate navigation — "Go to 30 north 31 east" (CL-170) ──
  // MUST precede the geocode block: its address heuristic can eat
  // digit-leading phrases as street addresses (cf. BUG-054).
  [
    /^(?:go|fly|navigate|zoom|take\s+me)\s+to\s+(\d{1,2}(?:\.\d+)?)\s*(?:°|degrees?)?\s*(north|south)\b[,\s]*(?:and\s+)?(\d{1,3}(?:\.\d+)?)\s*(?:°|degrees?)?\s*(east|west)\b/i,
    (m) => {
      const lat = Math.min(90, parseFloat(m[1])) * (m[2].toLowerCase() === 'south' ? -1 : 1)
      const lng = Math.min(180, parseFloat(m[3])) * (m[4].toLowerCase() === 'west' ? -1 : 1)
      return {
        type: 'navigation',
        target: { location: null, coordinates: [lng, lat] },
        _fastQuery: true,
      }
    },
  ],

  // ── Geocoding (HERE API) — street addresses ───────────────────
  // MUST be before navigation so "go to 1600 Pennsylvania Ave" doesn't
  // match as a country name. Each handler null-checks address shape so
  // generic phrases ("find Japan", "where is France") still fall through
  // to the navigation patterns below.
  [/^(?:find|locate)\s+(.+)/i, (m) => _geocodeQueryIfAddress(m[1].trim())],
  [
    /^(?:go|fly|navigate|zoom|take\s+me)\s+to\s+(?:the\s+)?(.+)/i,
    (m) => _geocodeQueryIfAddress(m[1].trim()),
  ],
  [/^(?:show\s+(?:me\s+)?)?(?:the\s+)?address\s+(.+)/i, (m) => _geocodeQuery(m[1].trim())],
  [/^where\s+is\s+(.+?)\??$/i, (m) => _geocodeQueryIfAddress(m[1].trim())],
  // Bare address (e.g. "1600 Pennsylvania Avenue NW, Washington DC")
  [/^(\d+\s+[A-Za-z][^,?]{2,}(?:,[^?]+)?)$/, (m) => _geocodeQueryIfAddress(m[1].trim())],

  // ── Navigation shortcuts ───────────────────────────────────────
  // "Go to Japan" / "Take me to the Democratic Republic of the Congo"
  // MUST be before POI queries to prevent "Take me to France" from being misrouted
  [/^(?:go|fly|navigate|zoom)\s+to\s+(?:the\s+)?(.+)/i, (m) => _navigationQuery(m[1].trim())],
  [/^take\s+me\s+to\s+(?:the\s+)?(.+)/i, (m) => _navigationQuery(m[1].trim())],

  // ── Show/Highlight country ─────────────────────────────────────
  // "Highlight Switzerland" / "Highlight Nigeria"
  [/^highlight\s+(.+)/i, (m) => _showCountryQuery(m[1].trim())],

  // ── Single division by name ─────────────────────────────────────
  // "Show me São Paulo state" / "Lagos State in Nigeria" / "Show me Punjab"
  [
    /(?:show\s+(?:me\s+)?(?:the\s+)?)?(.+?)\s+(?:state|province|region|prefecture|oblast)\s+(?:in|of)\s+(.+)/i,
    (m) => _singleDivisionQuery(m[1].trim(), m[2].trim()),
  ],
  [
    /(?:show\s+(?:me\s+)?(?:the\s+)?)?(.+?)\s+(?:state|province|region|prefecture|oblast)$/i,
    (m) => _singleDivisionQueryInfer(m[1].trim()),
  ],

  // ── Extrusion/3D style via text ────────────────────────────────
  // "Extrude it" / "Now extrude it" / "Make it 3D"
  [
    /^(?:now\s+)?extrude(?:\s+it)?$/i,
    () => ({ type: 'choropleth_style', style: 'extruded', _fastQuery: true }),
  ],

  // ── 3D terrain phrasings (CL-144) ──────────────────────────────
  // "Show 3D Mount Everest" / "Enable 3D mountains" / "Show 3D landscape of
  // New Zealand" — a "3D <subject>" request is a terrain view, not a
  // choropleth-style switch. Buildings/bars/choropleth keep their own routes.
  [
    /^(?:show|display|enable|turn\s+on|activate)\s+(?:me\s+)?(?:the\s+)?(?:a\s+)?3d\s+(?!buildings?\b|choropleth\b|bars?\b|columns?\b|terrain\b|elevation\b|view\b|map\b)(.+?)\s*$/i,
    (m) => ({ type: 'terrain', action: 'on', location: m[1].trim(), _fastQuery: true }),
  ],
  // "Show me the Himalayas in 3D" — anaphoric subjects ("it", "the map")
  // fall through to the choropleth-style extrusion patterns below.
  [
    /^(?:show|display)\s+(?:me\s+)?(?:the\s+)?(.+?)\s+in\s+3d\s*$/i,
    (m) => {
      const subject = m[1].trim()
      if (
        /^(?:it|this|that|them|map|data|choropleth|the\s+(?:map|data|choropleth))$/i.test(subject)
      )
        return null
      return { type: 'terrain', action: 'on', location: subject, _fastQuery: true }
    },
  ],

  // ── H3 density heatmap (MUST be before general POI patterns) ──
  // Pattern 1: "Show [density/heatmap/hex(agon)(s)/hexbin/hex density] of [POI] in [country]"
  // CL-144: also matches plural "hexagons", bare "hex", an "h3" prefix, and the
  // compound "hex/hexagon density" phrasings the golden corpus exercises.
  [
    new RegExp(
      `(?:show\\s+(?:me\\s+)?(?:the\\s+)?)?(?:h3\\s+)?(?:hex(?:agon)?s?|hexbins?|density|heatmap|heat\\s+map)(?:\\s+(?:map|density))?\\s+(?:of\\s+)?(?:all\\s+)?(?:the\\s+)?(${POI_WORDS}|pois?|points?\\s+of\\s+interest)\\s+(?:in|of|for|across)\\s+(?:the\\s+)?(.+)`,
      'i'
    ),
    (m) => _h3DensityQuery(m[1].trim(), m[2].trim(), m.input),
  ],
  // Pattern 1b: worldwide/global scope — "Show hex density of cafes worldwide"
  [
    new RegExp(
      `(?:show\\s+(?:me\\s+)?(?:the\\s+)?)?(?:h3\\s+)?(?:hex(?:agon)?s?|hexbins?|density|heatmap|heat\\s+map)(?:\\s+(?:map|density))?\\s+(?:of\\s+)?(?:all\\s+)?(?:the\\s+)?(${POI_WORDS}|pois?|points?\\s+of\\s+interest)\\s+(?:worldwide|globally|around\\s+the\\s+world|on\\s+earth)`,
      'i'
    ),
    (m) => _h3DensityQuery(m[1].trim(), null, m.input),
  ],
  // Pattern 2: "[POI] [density/heatmap/heat map/hexagon map] in [country]"
  [
    new RegExp(
      `(?:show\\s+(?:me\\s+)?(?:the\\s+)?)?(${POI_WORDS})\\s+(?:density|heatmap|heat\\s+map|hexagon\\s+map|hex\\s+map)\\s+(?:in|of|for|across)\\s+(?:the\\s+)?(.+)`,
      'i'
    ),
    (m) => _h3DensityQuery(m[1].trim(), m[2].trim(), m.input),
  ],
  // Pattern 3: "Show [POI] in [country] as a [hex map/hexagon map/heatmap/hexbin]"
  [
    new RegExp(
      `(?:show\\s+(?:me\\s+)?(?:all\\s+)?(?:the\\s+)?)?(${POI_WORDS})\\s+(?:in|of|for|across)\\s+(?:the\\s+)?(.+?)\\s+as\\s+(?:a\\s+)?(?:hex\\s+map|hexagon\\s+map|heatmap|heat\\s+map|hexbin|hexagons?)`,
      'i'
    ),
    (m) => _h3DensityQuery(m[1].trim(), m[2].trim(), m.input),
  ],
  // Pattern 4: "Density of [POI] in [country]" (leading "density of")
  [
    new RegExp(
      `(?:show\\s+(?:me\\s+)?(?:the\\s+)?)?density\\s+of\\s+(?:all\\s+)?(?:the\\s+)?(${POI_WORDS})\\s+(?:in|of|for|across)\\s+(?:the\\s+)?(.+)`,
      'i'
    ),
    (m) => _h3DensityQuery(m[1].trim(), m[2].trim(), m.input),
  ],

  // ── H3 per-capita queries ───────────────────────────────────────
  // "Show restaurants per capita in France" / "hospitals per 10k people in Japan"
  [
    new RegExp(
      `(?:show\\s+(?:me\\s+)?(?:the\\s+)?)?(${POI_WORDS})\\s+per\\s+(?:capita|person|10k|10,?000|100k|100,?000)(?:\\s+(?:people|residents?))?\\s+(?:in|of|for|across)\\s+(?:the\\s+)?(.+)`,
      'i'
    ),
    (m) => _h3PerCapitaQuery(m[1].trim(), m[2].trim()),
  ],
  // "Per capita restaurants in France" / "Per capita hospitals in Japan"
  [
    /(?:show\s+(?:me\s+)?(?:the\s+)?)?per\s+capita\s+([\w\s]+?)\s+(?:in|of|for|across)\s+(?:the\s+)?(.+)/i,
    (m) => _h3PerCapitaQuery(m[1].trim(), m[2].trim()),
  ],
  // "Show restaurants per capita as hexagons in France"
  [
    new RegExp(
      `(?:show\\s+(?:me\\s+)?(?:the\\s+)?)?(${POI_WORDS})\\s+per\\s+capita\\s+(?:as\\s+)?(?:hexagons?|hex\\s+map|heatmap)\\s+(?:in|of|for|across)\\s+(?:the\\s+)?(.+)`,
      'i'
    ),
    (m) => _h3PerCapitaQuery(m[1].trim(), m[2].trim()),
  ],
  // "How many restaurants per person in France?"
  [
    new RegExp(
      `(?:how\\s+many\\s+)?(${POI_WORDS})\\s+per\\s+(?:person|capita|people|resident)\\s+(?:in|of|for|across)\\s+(?:the\\s+)?(.+?)\\??$`,
      'i'
    ),
    (m) => _h3PerCapitaQuery(m[1].trim(), m[2].trim()),
  ],

  // ── POI clustering → H3 density (CL-144) ────────────────────────
  // "Cluster the restaurants in Rome" / "K-means clustering of restaurants in
  // New York with k=4" / "Density-based clustering of schools in California".
  // Point-clustering of a POI category renders as the H3 density surface.
  [
    new RegExp(
      `^(?:run\\s+)?(?:k[-\\s]?means\\s+|density[-\\s]based\\s+)?cluster(?:ing)?\\s+(?:of\\s+)?(?:the\\s+)?(${POI_WORDS})\\s+(?:in|of|across)\\s+(?:the\\s+)?(.+?)(?:\\s+into\\s+\\d+\\s+(?:groups?|clusters?))?(?:\\s+with\\s+k\\s*=?\\s*\\d+)?\\s*\\??$`,
      'i'
    ),
    (m) => _h3DensityQuery(m[1].trim(), m[2].trim(), m.input),
  ],
  // "Partition airports into 3 clusters worldwide"
  [
    new RegExp(
      `^partition\\s+(?:the\\s+)?(${POI_WORDS})\\s+into\\s+\\d+\\s+clusters?(?:\\s+(?:in|of|across)\\s+(?:the\\s+)?(.+?))?\\s*(?:worldwide|globally)?\\s*\\??$`,
      'i'
    ),
    (m) => _h3DensityQuery(m[1].trim(), m[2] ? m[2].trim() : null, m.input),
  ],

  // ── POI queries (MUST be before boundary patterns to prevent ────
  // ── "hospitals in the United States" matching as boundary)     ──

  // "Show me all the hospitals in [country]" / "Please show me the museums in France"
  // CL-144: \b before the POI group — without it "exPORTS of goods" matched the
  // "ports" POI mid-word and stole trade-indicator queries into the filter op.
  [
    new RegExp(
      `(?:(?:please\\s+)?show\\s+(?:me\\s+)?(?:all\\s+)?(?:the\\s+)?|(?:where\\s+are\\s+(?:the\\s+)?)|(?:find\\s+(?:all\\s+)?(?:the\\s+)?))?(?:every\\s+)?(?:all\\s+(?:the\\s+)?)?\\b(${POI_WORDS})\\s+(?:in|of|for|around|across|within)\\s+(?:the\\s+)?(.+)`,
      'i'
    ),
    (m) => _poiQuery(m[1].trim(), m[2].trim()),
  ],

  // "[country]'s hospitals" / "hospitals of [country]"
  [
    new RegExp(`(?:show\\s+(?:me\\s+)?(?:the\\s+)?)?(.+?)(?:'s)\\s+\\b(${POI_WORDS})\\b`, 'i'),
    (m) => _poiQuery(m[2].trim(), m[1].trim()),
  ],

  // ── River queries (MUST be before boundary patterns to prevent ──
  // ── "rivers of the United States" matching as boundary-only)  ──

  // "Show me the rivers, states, and capitals of [country]" (rivers + boundaries + capitals)
  [
    /(?:show\s+(?:me\s+)?(?:the\s+)?)?rivers?\s*,?\s*(?:states?|provinces?|boundaries?)?\s*,?\s*(?:and|with|&)\s+(?:(?:state\s+)?capitals?|(?:capital\s+)?cities)\s+(?:of|in|for)\s+(.+)/i,
    (m) => _riversWithBoundariesAndCapitals(m[1].trim()),
  ],

  // "Show me the states, rivers, and capitals of [country]"
  [
    /(?:show\s+(?:me\s+)?(?:the\s+)?)?(?:states?|provinces?)\s*,?\s*rivers?\s*,?\s*(?:and|with|&)\s+(?:(?:state\s+)?capitals?|(?:capital\s+)?cities)\s+(?:of|in|for)\s+(.+)/i,
    (m) => _riversWithBoundariesAndCapitals(m[1].trim()),
  ],

  // "Show me the rivers and states of [country]" (rivers + boundaries)
  [
    /(?:show\s+(?:me\s+)?(?:the\s+)?)?rivers?\s+(?:and|with|&)\s+(?:states?|provinces?|boundaries?)\s+(?:of|in|for)\s+(.+)/i,
    (m) => _riversWithBoundaries(m[1].trim()),
  ],
  [
    /(?:show\s+(?:me\s+)?(?:the\s+)?)?(?:states?|provinces?)\s+(?:and|with|&)\s+rivers?\s+(?:of|in|for)\s+(.+)/i,
    (m) => _riversWithBoundaries(m[1].trim()),
  ],

  // "Show me the rivers of [country/continent]" / "[country] rivers"
  // Negative lookahead rejects question-form queries like "Why do people live near rivers?"
  [
    /(?!^(?:why|how|what|who|when|where|tell)\b)(?:show\s+(?:me\s+)?(?:the\s+)?)?(?:major\s+)?rivers?\s+(?:of|in|for)\s+(.+)/i,
    (m) => _riversOnly(m[1].trim()),
  ],
  [
    /(?!^(?:why|how|what|who|when|where|tell)\b)(?:show\s+(?:me\s+)?(?:the\s+)?)?(.+?)(?:'s)?\s+(?:major\s+)?rivers/i,
    (m) => _riversOnly(m[1].trim()),
  ],

  // "Show me the world's rivers" / "Show rivers"
  [
    /(?!^(?:why|how|what|who|when|where|tell)\b)(?:show\s+(?:me\s+)?(?:the\s+)?)?(?:world(?:'s)?\s+)?(?:major\s+)?rivers$/i,
    () => _riversOnly(null),
  ],

  // ── Choropleth + capitals combos ───────────────────────────
  // "Show me the population of [country]'s states and capitals"
  [
    /(?:show\s+(?:me\s+)?(?:the\s+)?)?(?:population\s+(?:of\s+)?)?(?:states?|provinces?|regions?|divisions?)\s+(?:population\s+)?(?:and|with|&)\s+(?:(?:state\s+)?capitals?|(?:capital\s+)?cities)\s+(?:by\s+population\s+)?(?:of|in|for)\s+(.+)/i,
    (m) => _choroplethAndCapitals(m[1].trim()),
  ],
  [
    /(?:show\s+(?:me\s+)?(?:the\s+)?)?(?:population\s+(?:of\s+)?)?(.+?)(?:'s)?\s+(?:states?|provinces?)\s+(?:population\s+)?(?:and|with|&)\s+(?:(?:state\s+)?capitals?|(?:capital\s+)?cities)(?:\s+by\s+population)?/i,
    (m) => _choroplethAndCapitals(m[1].trim()),
  ],
  // "Show me [country] states by population with capitals"
  [
    /(?:show\s+(?:me\s+)?(?:the\s+)?)?(.+?)(?:'s)?\s+(?:states?|provinces?)\s+by\s+population\s+(?:and|with|&)\s+(?:(?:state\s+)?capitals?|(?:capital\s+)?cities)/i,
    (m) => _choroplethAndCapitals(m[1].trim()),
  ],
  // "Show me the state populations, boundaries, and capitals of [country]"
  [
    /(?:show\s+(?:me\s+)?(?:the\s+)?)?(?:states?|provinces?)\s+populations?\s*,?\s*(?:boundaries?\s*,?\s*)?(?:and|with|&)\s+(?:(?:state\s+)?capitals?|(?:capital\s+)?cities)\s+(?:of|in|for)\s+(.+)/i,
    (m) => _choroplethAndCapitals(m[1].trim()),
  ],

  // ── FAOSTAT Agriculture (MUST be before division/boundary patterns) ────
  // "Show wheat production by country" / "Show rice production in Asia"
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(wheat|rice|maize|corn|cereal|grain|crop|food)\s+(?:production|output|harvest)(?:\s+(?:by\s+country|worldwide|globally))?(?:\s+(?:in|of|for|across|around)\s+(.+))?/i,
    (m) => ({
      type: 'faostat',
      indicator: m[1].trim(),
      location: m[2]?.trim() || null,
      _fastQuery: true,
    }),
  ],
  // "Which countries produce the most rice/wheat?"
  [
    /(?:which|what)\s+countries?\s+(?:produce|grow|harvest)\s+(?:the\s+)?(?:most\s+)?(wheat|rice|maize|corn|cereal|grain)/i,
    (m) => ({ type: 'faostat', indicator: m[1].trim(), _fastQuery: true }),
  ],
  // "Show fertilizer use in South America" / "Show irrigation in Africa"
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(fertilizer|fertiliser|irrigation|irrigated)\s+(?:use|usage|rates?|levels?|data)?(?:\s+(?:in|of|for|across|around)\s+(.+))?/i,
    (m) => ({
      type: 'faostat',
      indicator: m[1].trim(),
      location: m[2]?.trim() || null,
      _fastQuery: true,
    }),
  ],
  // "Show cattle/livestock/sheep/poultry by country"
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(cattle|livestock|sheep|poultry|chicken|chickens?)\s*(?:population|numbers?|density|stock|data|by\s+country)?(?:\s+(?:in|of|for|across|around)\s+(.+))?/i,
    (m) => ({
      type: 'faostat',
      indicator: m[1].trim(),
      location: m[2]?.trim() || null,
      _fastQuery: true,
    }),
  ],
  // "Show crop yield/agricultural land/arable land by country"
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(crop\s+yields?|cereal\s+yields?|arable\s+land|agricultural\s+land|farmland|cropland|ag(?:ricultural)?\s+(?:exports?|imports?|trade))\s*(?:by\s+country)?(?:\s+(?:in|of|for|across|around)\s+(.+))?/i,
    (m) => ({
      type: 'faostat',
      indicator: m[1].trim(),
      location: m[2]?.trim() || null,
      _fastQuery: true,
    }),
  ],
  // "Show agricultural data" / "FAOSTAT map" (generic)
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(?:faostat|agricultural?\s+data|agriculture\s+(?:data|map|index|statistics))(?:\s+(?:in|of|for|across|around)\s+(.+))?/i,
    (m) => ({ type: 'faostat', indicator: null, location: m[1]?.trim() || null, _fastQuery: true }),
  ],
  // "Show food production" / "global agriculture"
  [
    /(?:global|world)\s+(?:agriculture|food\s+production|crop\s+production)/i,
    () => ({ type: 'faostat', indicator: 'cereal', _fastQuery: true }),
  ],

  // ── USDA FAS (Production, Supply & Distribution) ─────────────────────
  // "Show USDA wheat production" / "Show USDA soybean exports"
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(?:usda\s+)?(?:fas\s+)?(wheat|corn|maize|rice|soybeans?|soy|cotton|coffee|cocoa|tea|barley|sorghum|oats|millet|palm\s*oil|sugar(?:\s*cane)?|peanuts?|groundnuts?|sunflower|rapeseed|canola|almonds?|apples?|soybean\s+meal)\s+(production|imports?|exports?|consumption|stocks?|yields?|area|supply|distribution)(?:\s+(?:by\s+country|worldwide|globally))?(?:\s+(?:in|of|for|across|around)\s+(.+))?/i,
    (m) => ({
      type: 'usda_fas',
      commodity: m[1].trim(),
      attribute: m[2].trim(),
      location: m[3]?.trim() || null,
      _fastQuery: true,
    }),
  ],
  // "Show US wheat exports" / "Where does the US export soybeans?"
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(?:us|u\.?s\.?|united\s+states?|american)\s+(wheat|corn|maize|rice|soybeans?|soy|cotton|coffee|cocoa|barley|sorghum)\s+exports?(?:\s+(?:by\s+country|worldwide|globally))?(?:\s+(?:in|of|for|to|across)\s+(.+))?/i,
    (m) => ({
      type: 'usda_fas',
      commodity: m[1].trim(),
      us_exports: true,
      location: m[2]?.trim() || null,
      _fastQuery: true,
    }),
  ],
  [
    /where\s+(?:does|do)\s+(?:the\s+)?(?:us|u\.?s\.?|united\s+states?|america)\s+export\s+(wheat|corn|maize|rice|soybeans?|soy|cotton|coffee|cocoa|barley|sorghum)/i,
    (m) => ({ type: 'usda_fas', commodity: m[1].trim(), us_exports: true, _fastQuery: true }),
  ],
  // "Which countries produce/import/export the most wheat/corn/soybeans?" (USDA variant)
  [
    /(?:which|what)\s+countries?\s+(?:produce|grow|import|export|consume)\s+(?:the\s+)?(?:most\s+)?(soybeans?|soy|cotton|coffee|cocoa|tea|palm\s*oil|sugar(?:\s*cane)?|peanuts?|groundnuts?|sunflower|rapeseed|canola|almonds?|barley|sorghum|oats|millet|soybean\s+meal)/i,
    (m) => ({
      type: 'usda_fas',
      commodity: m[1].trim(),
      attribute: 'production',
      _fastQuery: true,
    }),
  ],
  // "Show USDA data" / "USDA FAS data" / "USDA agricultural data" (generic)
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(?:usda|usda\s+fas)\s+(?:data|statistics|map|agricultural\s+data)(?:\s+(?:in|of|for|across|around)\s+(.+))?/i,
    (m) => ({
      type: 'usda_fas',
      commodity: null,
      attribute: null,
      location: m[1]?.trim() || null,
      _fastQuery: true,
    }),
  ],
  // "Show soybean/cotton/coffee production by country" (commodities NOT caught by faostat)
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(soybeans?|soy|cotton|coffee|cocoa|tea|palm\s*oil|sugar(?:\s*cane)?|peanuts?|groundnuts?|sunflower|rapeseed|canola|almonds?|soybean\s+meal)\s+(?:production|output|harvest|data)(?:\s+(?:by\s+country|worldwide|globally))?(?:\s+(?:in|of|for|across|around)\s+(.+))?/i,
    (m) => ({
      type: 'usda_fas',
      commodity: m[1].trim(),
      attribute: 'production',
      location: m[2]?.trim() || null,
      _fastQuery: true,
    }),
  ],

  // ── Global Food Security Index (GFSI / EIU) ────────────────────────
  // "Show food security index" / "Show global food security"
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(?:global\s+)?food\s+security(?:\s+index)?(?:\s+(?:map|data|scores?))?(?:\s+(?:in|of|for|across|around)\s+(.+))?/i,
    (m) => ({
      type: 'food_security',
      indicator: 'overall',
      location: m[1]?.trim() || null,
      _fastQuery: true,
    }),
  ],
  // "Show GFSI" / "Show GFSI scores"
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?gfsi(?:\s+(?:scores?|data|map|index))?(?:\s+(?:in|of|for|across|around)\s+(.+))?/i,
    (m) => ({
      type: 'food_security',
      indicator: 'overall',
      location: m[1]?.trim() || null,
      _fastQuery: true,
    }),
  ],
  // "Show food affordability/availability/quality/sustainability by country"
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?food\s+(affordability|availability|quality|safety|sustainability|nutrition)(?:\s+(?:scores?|index|data|map))?(?:\s+(?:by\s+country|worldwide|globally))?(?:\s+(?:in|of|for|across|around)\s+(.+))?/i,
    (m) => ({
      type: 'food_security',
      indicator: m[1].trim(),
      location: m[2]?.trim() || null,
      _fastQuery: true,
    }),
  ],
  // "Which countries have the best/worst food security?"
  [
    /(?:which|what)\s+countries?\s+(?:have|has|are)\s+(?:the\s+)?(?:best|worst|highest|lowest|most|least)\s+food\s+(?:security|affordability|availability|quality|sustainability)/i,
    () => ({ type: 'food_security', indicator: 'overall', _fastQuery: true }),
  ],
  // "Show food insecurity" / "Show hunger index"
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(?:food\s+insecurity|hunger\s+(?:index|map|data))(?:\s+(?:in|of|for|across|around)\s+(.+))?/i,
    (m) => ({
      type: 'food_security',
      indicator: 'overall',
      location: m[1]?.trim() || null,
      _fastQuery: true,
    }),
  ],

  // ── UN Treaty Database ─────────────────────────────────────────────
  // "Which countries signed the Paris Agreement?" / "Show Kyoto Protocol signatories"
  [
    /(?:which|what)\s+countries?\s+(?:signed|ratified|joined|are\s+(?:in|part(?:y|\s+of)?|members?\s+of))\s+(?:the\s+)?(paris\s+agreement|kyoto\s+protocol|geneva\s+convention|npt|unclos|rome\s+statute|icc|chemical\s+weapons|landmine|cluster\s+munitions?|arms\s+trade|bio(?:logical)?\s+weapons?|nuclear\s+(?:ban|weapons?\s+(?:ban|prohibition))|tpnw|montreal\s+protocol|biodiversity|tobacco|refugee|child\s*(?:ren'?s?)?\s+rights?|crc|cedaw|torture|ottawa)/i,
    (m) => ({ type: 'un_treaties', treaty: m[1].trim(), _fastQuery: true }),
  ],
  // "Show Paris Agreement" / "Show Paris Agreement members"
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(paris\s+agreement|kyoto\s+protocol|geneva\s+convention|npt|unclos|rome\s+statute|icc|chemical\s+weapons\s+convention|landmine\s+(?:treaty|convention|ban)|cluster\s+munitions?\s+convention|arms\s+trade\s+treaty|nuclear\s+(?:ban|(?:non-?\s*)?proliferation)|tpnw|montreal\s+protocol|biodiversity\s+convention|tobacco\s+(?:convention|control)|refugee\s+convention|(?:rights?\s+of\s+(?:the\s+)?)?child(?:ren)?\s+(?:convention|rights)|crc|cedaw|torture\s+convention|ottawa\s+treaty)\s*(?:members?|signatories|parties|participation|status|(?:participation\s+)?map)?(?:\s+(?:in|of|for|across|around)\s+(.+))?/i,
    (m) => ({
      type: 'un_treaties',
      treaty: m[1].trim(),
      location: m[2]?.trim() || null,
      _fastQuery: true,
    }),
  ],
  // "Show treaty participation" / "Show UN treaties" / "Show treaty map"
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(?:un\s+)?treat(?:y|ies)\s*(?:participation|map|data|index)?(?:\s+(?:by\s+country|worldwide|globally))?(?:\s+(?:in|of|for|across|around)\s+(.+))?/i,
    (m) => ({
      type: 'un_treaties',
      treaty: null,
      location: m[1]?.trim() || null,
      _fastQuery: true,
    }),
  ],
  // "Show NATO members" (redirect to treaty count — NATO is not in the dataset but this is a common query)
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(?:nato|un)\s+members?(?:\s+(?:map|countries|worldwide))?/i,
    () => ({ type: 'un_treaties', treaty: null, _fastQuery: true }),
  ],
  // "Show international law" / "Show international agreements"
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?international\s+(?:law|agreements?|treaties|conventions?)(?:\s+(?:map|participation|data))?(?:\s+(?:in|of|for|across|around)\s+(.+))?/i,
    (m) => ({
      type: 'un_treaties',
      treaty: null,
      location: m[1]?.trim() || null,
      _fastQuery: true,
    }),
  ],

  // ── Fragile States Index (MUST be before division/boundary patterns) ───
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(?:fragile\s+states?\s+)?(?:fragility|fragile\s+states?\s+index|fsi|state\s+fragility)\s*(?:in|of|for|across|around)\s+(.+)/i,
    (m) => ({ type: 'fragile_states', location: m[1].trim(), _fastQuery: true }),
  ],
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(?:fragile\s+states?\s+index|fsi|state\s+fragility|fragility\s+index|fragile\s+states?\s+map)(?:\s+(?:across|around)\s+the\s+world)?$/i,
    () => ({ type: 'fragile_states', _fastQuery: true }),
  ],
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(?:fragile\s+states?\s+index|fsi|state\s+fragility|fragility)\s+(?:across|around)\s+(?:the\s+)?(?:world|globe|earth)/i,
    () => ({ type: 'fragile_states', _fastQuery: true }),
  ],
  [
    /(?:which|what)\s+countries\s+(?:are|is)\s+(?:in\s+)?(?:the\s+)?['"]?(alert|warning|stable|sustainable)['"]?\s*(?:category|status|state)?/i,
    (m) => ({ type: 'fragile_states', category_filter: m[1].trim(), _fastQuery: true }),
  ],
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?['"]?(alert|warning|stable|sustainable)['"]?\s+(?:countries|states|nations)/i,
    (m) => ({ type: 'fragile_states', category_filter: m[1].trim(), _fastQuery: true }),
  ],
  [
    /(?:most\s+)?fragile\s+(?:countries|states|nations)/i,
    () => ({ type: 'fragile_states', category_filter: 'alert', _fastQuery: true }),
  ],
  [
    /fragile\s+states?\s+(?:index|data|map|report|ranking)$/i,
    () => ({ type: 'fragile_states', _fastQuery: true }),
  ],

  // ── UNESCO World Heritage Sites (MUST be before division/boundary patterns) ──
  // "Show UNESCO World Heritage Sites" / "Show heritage sites"
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(?:unesco\s+)?(?:world\s+)?heritage\s+sites?(?:\s+(?:in|of|for|across|around)\s+(.+))?/i,
    (m) => ({ type: 'heritage_sites', location: m[1]?.trim() || null, _fastQuery: true }),
  ],
  // "Show UNESCO sites in Italy"
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?unesco\s+sites?(?:\s+(?:in|of|for|across|around)\s+(.+))?/i,
    (m) => ({ type: 'heritage_sites', location: m[1]?.trim() || null, _fastQuery: true }),
  ],
  // "Show endangered heritage sites" / "Show endangered world heritage sites"
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?endangered\s+(?:unesco\s+)?(?:world\s+)?heritage\s+sites?(?:\s+(?:in|of|for|across|around)\s+(.+))?/i,
    (m) => ({
      type: 'heritage_sites',
      endangered: true,
      location: m[1]?.trim() || null,
      _fastQuery: true,
    }),
  ],
  // "Show natural/cultural/mixed heritage sites"
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(cultural|natural|mixed)\s+(?:unesco\s+)?(?:world\s+)?heritage\s+sites?(?:\s+(?:in|of|for|across|around)\s+(.+))?/i,
    (m) => ({
      type: 'heritage_sites',
      category_filter: m[1].trim(),
      location: m[2]?.trim() || null,
      _fastQuery: true,
    }),
  ],
  // "How many World Heritage Sites does India have?"
  [
    /how\s+many\s+(?:unesco\s+)?(?:world\s+)?heritage\s+sites?\s+(?:does|do|are\s+(?:in|there\s+in))\s+(.+?)(?:\s+have)?/i,
    (m) => ({ type: 'heritage_sites', location: m[1].trim(), count_query: true, _fastQuery: true }),
  ],
  // "Which country has the most heritage sites?"
  [
    /(?:which|what)\s+country\s+(?:has|have)\s+(?:the\s+)?most\s+(?:unesco\s+)?(?:world\s+)?heritage\s+sites?/i,
    () => ({ type: 'heritage_sites', _fastQuery: true }),
  ],
  // Short forms: "world heritage sites" / "UNESCO heritage"
  [
    /(?:world\s+heritage\s+sites?|unesco\s+heritage|heritage\s+sites?\s+(?:map|data|list))(?:\s+(?:in|of|for|across|around)\s+(.+))?$/i,
    (m) => ({ type: 'heritage_sites', location: m[1]?.trim() || null, _fastQuery: true }),
  ],

  // ── World Languages (Glottolog) — MUST be before division/boundary patterns ──
  // "Show languages of the world" / "Show world languages"
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(?:world\s+)?languages?\s+(?:of\s+the\s+world|map|data|around\s+the\s+world)/i,
    () => ({ type: 'languages', _fastQuery: true }),
  ],
  // "Show languages in Africa" / "Display indigenous languages of the Americas"
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(?:all\s+)?(?:indigenous\s+|native\s+)?languages?\s+(?:in|of|spoken\s+in|from)\s+(.+)/i,
    (m) => ({ type: 'languages', location: m[1].trim(), _fastQuery: true }),
  ],
  // CL-144: "Show Spanish speaking countries" / "Show Hindi speaking areas" /
  // "Show Korean language regions" / "Display Mandarin Chinese speakers".
  // These previously fell into the ADM1 boundary pattern ("…regions" read as
  // divisions of a context country) or missed entirely.
  [
    /^(?:show|display)\s+(?:me\s+)?(?:the\s+)?([a-z]+(?:\s+[a-z]+)?)[\s-](?:speaking|language)\s+(?:countries|nations|regions?|areas?)$/i,
    (m) => ({ type: 'languages', language_search: m[1].trim(), _fastQuery: true }),
  ],
  [
    /^(?:show|display)\s+(?:me\s+)?(?:the\s+)?([a-z]+(?:\s+[a-z]+)?)\s+speakers$/i,
    (m) => ({ type: 'languages', language_search: m[1].trim(), _fastQuery: true }),
  ],
  // "What languages are spoken in Nigeria?"
  [
    /(?:what|which)\s+languages?\s+(?:are\s+)?(?:spoken|used|found)\s+(?:in|of|across)\s+(.+)/i,
    (m) => ({ type: 'languages', location: m[1].trim(), _fastQuery: true }),
  ],
  // "Show endangered languages" / "Show extinct languages"
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(?:all\s+)?(endangered|extinct|vulnerable|dying|threatened)\s+languages?(?:\s+(?:in|of|from)\s+(.+))?/i,
    (m) => ({
      type: 'languages',
      endangered: true,
      location: m[2]?.trim() || null,
      _fastQuery: true,
    }),
  ],
  // "Show Indo-European languages" / "Show Austronesian languages"
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(?:all\s+)?(indo[- ]european|sino[- ]tibetan|niger[- ]congo|austronesian|afro[- ]asiatic|dravidian|turkic|uralic|tai[- ]kadai|austroasiatic|austro[- ]asiatic|japonic|koreanic|mongolic|quechuan|bantu|celtic|romance|germanic|slavic|semitic|polynesian|papuan|aboriginal)\s+languages?(?:\s+(?:in|of|from)\s+(.+))?/i,
    (m) => ({
      type: 'languages',
      family_filter: m[1].trim(),
      location: m[2]?.trim() || null,
      _fastQuery: true,
    }),
  ],
  // "How many languages does Papua New Guinea have?" / "How many languages in Africa?"
  [
    /how\s+many\s+languages?\s+(?:does|do|are\s+(?:in|there\s+in|spoken\s+in))\s+(.+?)(?:\s+have)?/i,
    (m) => ({ type: 'languages', location: m[1].trim(), count_query: true, _fastQuery: true }),
  ],
  // "Show the spread of Arabic" / "Show where Swahili is spoken"
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(?:spread|distribution|extent|range)\s+of\s+(.+)/i,
    (m) => ({ type: 'languages', language_search: m[1].trim(), _fastQuery: true }),
  ],
  // "Show where [language] is spoken"
  [
    /(?:show|display)\s+(?:me\s+)?where\s+(.+?)\s+is\s+spoken/i,
    (m) => ({ type: 'languages', language_search: m[1].trim(), _fastQuery: true }),
  ],
  // Short forms: "world languages" / "language map" / "language distribution
  // map" / "linguistic diversity" (CL-144: + distribution, trailing globally)
  [
    /(?:world\s+languages?|language\s+(?:map|data|diversity|distribution)|linguistic\s+diversity|glottolog)(?:\s+(?:map|globally|worldwide))?(?:\s+(?:in|of|for)\s+(.+))?$/i,
    (m) => ({ type: 'languages', location: m[1]?.trim() || null, _fastQuery: true }),
  ],

  // ── World Religions & Ethnicity (CIA Factbook) — MUST be before division/boundary patterns ──
  // "Show religions of the world" / "Show world religions"
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(?:world\s+)?religions?\s+(?:of\s+the\s+world|map|across\s+the\s+world)/i,
    () => ({ type: 'religions', _fastQuery: true }),
  ],
  // "Show religions in Africa" / "Show dominant religion in Southeast Asia"
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(?:dominant\s+)?religions?\s+(?:in|of|across)\s+(.+)/i,
    (m) => ({ type: 'religions', location: m[1].trim(), _fastQuery: true }),
  ],
  // "What's the dominant religion in Southeast Asia?"
  [
    /(?:what(?:'s|\s+is))\s+(?:the\s+)?(?:dominant|main|major|primary)\s+religion\s+(?:in|of)\s+(.+)/i,
    (m) => ({ type: 'religions', location: m[1].trim(), _fastQuery: true }),
  ],
  // "Show Muslim countries" / "Show Christian countries" / "Show Buddhist countries"
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(?:all\s+)?(christian|muslim|islamic|hindu|buddhist|jewish|secular|unaffiliated)\s+(?:countries|nations|states)(?:\s+(?:in|of)\s+(.+))?/i,
    (m) => ({
      type: 'religions',
      religion_filter: m[1].trim(),
      location: m[2]?.trim() || null,
      _fastQuery: true,
    }),
  ],
  // "Show religious diversity index" / "Show ethnic diversity"
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(religious|ethnic)\s+diversity(?:\s+index)?(?:\s+(?:in|of|across|for)\s+(.+))?/i,
    (m) => ({
      type: 'religions',
      diversity_mode: m[1].trim(),
      location: m[2]?.trim() || null,
      _fastQuery: true,
    }),
  ],
  // "Show where Christianity is dominant" / "Show the spread of Islam"
  [
    /(?:show|display)\s+(?:me\s+)?(?:where\s+)?(christianity|islam|hinduism|buddhism|judaism)\s+(?:is\s+)?(?:dominant|practiced|spread)/i,
    (m) => ({ type: 'religions', religion_filter: m[1].trim(), _fastQuery: true }),
  ],
  // Short forms: "world religions" / "religion map"
  [
    /(?:world\s+religions?|religion\s+(?:map|data|distribution)|religious\s+(?:map|composition|distribution))(?:\s+(?:in|of|for)\s+(.+))?$/i,
    (m) => ({ type: 'religions', location: m[1]?.trim() || null, _fastQuery: true }),
  ],

  // ── Denomination queries ──
  // "Show Catholic countries" / "Show Sunni vs Shia" / "Show Protestant Christianity"
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(catholic|protestant|orthodox|evangelical|sunni|shia|shiite|theravada|mahayana|vajrayana|sikh|jain|bahai|baha'i|shinto|tao(?:ist|ism)?|confucian(?:ism)?|zoroastrian(?:ism)?)\s+(?:countries|nations|population|map|distribution|percentage|presence)(?:\s+(?:in|of|across)\s+(.+))?/i,
    (m) => ({
      type: 'religions',
      denomination: m[1].trim().toLowerCase(),
      location: m[2]?.trim() || null,
      _fastQuery: true,
    }),
  ],
  // "Show Catholic vs Protestant" / "Compare Catholic and Protestant"
  [
    /(?:show|compare|display)\s+(?:me\s+)?(catholic)\s+(?:vs?\.?|versus|and|compared?\s+to)\s+(protestant|orthodox)(?:\s+(?:in|of|across)\s+(.+))?/i,
    (m) => ({
      type: 'religions',
      denomination: m[1].trim().toLowerCase(),
      location: m[3]?.trim() || null,
      _fastQuery: true,
    }),
  ],
  // "Show Sunni vs Shia" / "Compare Sunni and Shia"
  [
    /(?:show|compare|display)\s+(?:me\s+)?(sunni)\s+(?:vs?\.?|versus|and|compared?\s+to)\s+(shia|shiite)(?:\s+(?:in|of|across)\s+(.+))?/i,
    (m) => ({
      type: 'religions',
      denomination: m[1].trim().toLowerCase(),
      location: m[3]?.trim() || null,
      _fastQuery: true,
    }),
  ],
  // "Where are Catholics?" / "Where is Sunni Islam dominant?"
  [
    /(?:where\s+(?:are|is|do))\s+(?:the\s+)?(catholic|protestant|orthodox|sunni|shia|sikh|jain|bahai|shinto)s?\s+(?:dominant|majority|concentrated|found|live)/i,
    (m) => ({ type: 'religions', denomination: m[1].trim().toLowerCase(), _fastQuery: true }),
  ],

  // ── State religion queries ──
  // "Show state religions" / "Show countries with state religions"
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(?:countries?\s+with\s+)?(?:state|official|established)\s+religions?(?:\s+(?:in|of|across)\s+(.+))?/i,
    (m) => ({
      type: 'religions',
      state_religion_mode: true,
      location: m[1]?.trim() || null,
      _fastQuery: true,
    }),
  ],
  // "Show secular states" / "Show secular countries"
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?secular\s+(?:states?|countries|nations)(?:\s+(?:in|of|across)\s+(.+))?/i,
    (m) => ({
      type: 'religions',
      state_religion_mode: true,
      state_rel_filter: 'Secular',
      location: m[1]?.trim() || null,
      _fastQuery: true,
    }),
  ],
  // "Show Islamic states" / "Show countries where Islam is the state religion"
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(?:islamic|muslim)\s+(?:states?|theocrac(?:y|ies))(?:\s+(?:in|of|across)\s+(.+))?/i,
    (m) => ({
      type: 'religions',
      state_religion_mode: true,
      state_rel_filter: 'Islam',
      location: m[1]?.trim() || null,
      _fastQuery: true,
    }),
  ],
  // "Which countries have a state religion?"
  [
    /(?:which|what)\s+countries\s+(?:have|recognize)\s+(?:a\s+)?(?:state|official|established)\s+religion/i,
    () => ({ type: 'religions', state_religion_mode: true, _fastQuery: true }),
  ],
  // "Show government-religion relationship" / "Show religion and government"
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(?:government|state)[\s-]+religion\s+(?:relationship|type|classification)/i,
    () => ({ type: 'religions', state_religion_mode: true, _fastQuery: true }),
  ],

  // ── Religion population queries ──
  // "Show Muslim population by country" / "Show Christian population"
  // CL-144: "…population(s) by region/state" asks for an admin-level
  // breakdown (division choropleth) — defer to the AI, don't render the
  // country-level religions dataset.
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(christian|muslim|islamic|hindu|buddhist|sikh|jain)\s+populations?(?:\s+(?:by\s+country|worldwide|globally|numbers?))?(?:\s+(?:in|of|across)\s+(.+))?/i,
    (m) => {
      if (/\bby\s+(?:region|state|province|district|county|division)s?\b/i.test(m.input))
        return null
      const popMap = {
        christian: 'christianity',
        muslim: 'islam',
        islamic: 'islam',
        hindu: 'hinduism',
        buddhist: 'buddhism',
        sikh: 'sikhism',
        jain: 'jainism',
      }
      return {
        type: 'religions',
        population_mode: popMap[m[1].toLowerCase()] || m[1].toLowerCase(),
        location: m[2]?.trim() || null,
        _fastQuery: true,
      }
    },
  ],
  // "How many Muslims are there?" / "How many Christians in Africa?"
  [
    /(?:how\s+many)\s+(christians?|muslims?|hindus?|buddhists?|sikhs?|jains?)\s+(?:are\s+there|live|exist)(?:\s+(?:in|across)\s+(.+))?/i,
    (m) => {
      const popMap = {
        christian: 'christianity',
        christians: 'christianity',
        muslim: 'islam',
        muslims: 'islam',
        hindu: 'hinduism',
        hindus: 'hinduism',
        buddhist: 'buddhism',
        buddhists: 'buddhism',
        sikh: 'sikhism',
        sikhs: 'sikhism',
        jain: 'jainism',
        jains: 'jainism',
      }
      return {
        type: 'religions',
        population_mode: popMap[m[1].toLowerCase()] || m[1].toLowerCase(),
        location: m[2]?.trim() || null,
        _fastQuery: true,
      }
    },
  ],

  // ── Projection / trend queries ──
  // "How has Christianity spread over time?" / "Show Christianity trends"
  [
    /(?:how\s+(?:has|will)|show)\s+(?:me\s+)?(christianity|islam)\s+(?:spread|changed?|grown?|trend|project(?:ion|ed)?|future|2050|grow(?:th|n)?|declin(?:e|ed|ing))(?:\s+(?:over\s+time|by\s+2050|in\s+the\s+future))?/i,
    (m) => ({ type: 'religions', projection_mode: m[1].toLowerCase(), _fastQuery: true }),
  ],
  // "Show religious composition trends" / "Show religion projections 2050"
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(?:religious?\s+)?(?:composition\s+)?(?:trends?|projections?|forecast|future)(?:\s+(?:by\s+)?2050)?/i,
    () => ({ type: 'religions', projection_mode: 'christianity', _fastQuery: true }),
  ],

  // ── Joshua Project — Ethnic & People Groups ─────────────────
  // "Show people groups of the world" / "Show world people groups"
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(?:world\s+)?(?:people\s+groups?|ethnic\s+groups?|ethno(?:linguistic)?\s+groups?)\s+(?:of\s+the\s+world|map|data|around\s+the\s+world)/i,
    () => ({ type: 'joshua_project', _fastQuery: true }),
  ],
  // Reversed word order: "groups of people on earth" / "different groups of people" / "tribes of the world"
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(?:all\s+)?(?:different\s+)?(?:groups?\s+of\s+people|tribes?|peoples?)(?:\s+(?:on|of|around|across)\s+(?:the\s+)?(?:earth|world|globe|planet))/i,
    () => ({ type: 'joshua_project', _fastQuery: true }),
  ],
  // "Show the different peoples" / "Show all the different people groups" / "Show different ethnic groups"
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(?:all\s+)?(?:the\s+)?different\s+(?:people\s+groups?|ethnic\s+groups?|peoples?|tribes?|groups?\s+of\s+people)/i,
    () => ({ type: 'joshua_project', _fastQuery: true }),
  ],
  // "Show people groups in India" / "Show ethnic groups in Southeast Asia"
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(?:all\s+)?(?:people\s+groups?|ethnic\s+groups?|ethno(?:linguistic)?\s+groups?)\s+(?:in|of|from|across)\s+(.+)/i,
    (m) => ({ type: 'joshua_project', location: m[1].trim(), _fastQuery: true }),
  ],
  // Reversed word order with location: "groups of people in Africa" / "tribes in South America"
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(?:all\s+)?(?:different\s+)?(?:groups?\s+of\s+people|tribes?)\s+(?:in|of|from|across)\s+(.+)/i,
    (m) => ({ type: 'joshua_project', location: m[1].trim(), _fastQuery: true }),
  ],
  // "How many people groups are in India?" / "How many ethnic groups does Nigeria have?"
  [
    /how\s+many\s+(?:people|ethnic|ethno(?:linguistic)?)\s+groups?\s+(?:are\s+(?:in|there\s+in)|does|do)\s+(.+?)(?:\s+have)?/i,
    (m) => ({ type: 'joshua_project', location: m[1].trim(), count_query: true, _fastQuery: true }),
  ],
  // "Show unreached people groups" / "Show unreached peoples in India"
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(?:all\s+)?unreached\s+(?:people\s+groups?|peoples?)(?:\s+(?:in|of|from|across)\s+(.+))?/i,
    (m) => ({
      type: 'joshua_project',
      unreached: true,
      location: m[1]?.trim() || null,
      _fastQuery: true,
    }),
  ],
  // "Show indigenous peoples" / "Show indigenous people groups in South America"
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(?:all\s+)?indigenous\s+(?:people\s+groups?|peoples?)(?:\s+(?:in|of|from|across)\s+(.+))?/i,
    (m) => ({
      type: 'joshua_project',
      indigenous: true,
      location: m[1]?.trim() || null,
      _fastQuery: true,
    }),
  ],
  // "Where do the Tuareg live?" / "Where are the Hmong?" / "Find the Maasai"
  // CL-144: anchored to the start of the utterance and restricted to
  // proper-noun-looking names (every word capitalized, ≤4 words). Before this
  // guard the pattern stole "Where are the heritage sites in Europe",
  // "Where are temperate climates", "Find the center of all US capital
  // cities", and even "Can you help me find Bigfoot" (unanchored "find").
  [
    /^(?:where\s+(?:do|does|are)\s+(?:the\s+)?|find\s+(?:the\s+)?)(.+?)(?:\s+(?:live|located|found))?\s*\??$/i,
    (m) => {
      // Only match if it looks like a people group name (capitalized, no common POI words)
      const name = m[1].trim()
      const lower = name.toLowerCase()
      if (/\b(?:hospital|school|airport|museum|restaurant|hotel|church|mosque|park)\b/.test(lower))
        return null
      // "Where do animals live" is a Grade 1 biome/animals query, not a
      // people-group search — let it fall through to the animals fast-query.
      if (/\b(?:animals?|wildlife|creatures?)\b/.test(lower)) return null
      // "Find the center of X" is a geo_center / centrography question.
      if (/\bcent(?:er|re)s?\s+of\b|\bmiddle\s+of\b/.test(lower)) return null
      // Spatial-analysis queries ("Where are the hot spots of X?") must reach
      // the Worker tools, not the people-groups search. Keep in sync with the
      // SPATIAL_ANALYSIS_GUARD in concept-registry.js.
      if (
        /\b(?:hot|cold)\s*spots?\b|\bcluster\w*|\bautocorrelat\w*|\bgetis\b|\bdeviational\b|\bdirectional\s+trend\b|\bnearest\s+neighbou?r\b|\bmost\s+central\b/.test(
          lower
        )
      )
        return null
      if (name.length < 3 || name.length > 40) return null
      // Proper-noun shape: every word starts with an uppercase letter
      // ("Tuareg", "Mauna Loa people"). Generic lowercase phrases ("temperate
      // climates", "major waterfalls in the world") defer to later patterns,
      // the concept registry, or the AI.
      const words = name.split(/\s+/)
      if (words.length > 4) return null
      if (!words.every((w) => /^[A-ZÀ-Þ]/.test(w))) return null
      return { type: 'joshua_project', people_search: name, _fastQuery: true }
    },
  ],
  // "Show people groups that speak Arabic" / "Show Arabic-speaking people groups"
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(?:people\s+groups?|ethnic\s+groups?)\s+(?:that\s+)?speak(?:ing)?\s+(.+)/i,
    (m) => ({ type: 'joshua_project', language_filter: m[1].trim(), _fastQuery: true }),
  ],
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(.+?)[\s-]speaking\s+(?:people\s+groups?|ethnic\s+groups?|peoples?)(?:\s+(?:in|of|from)\s+(.+))?/i,
    (m) => ({
      type: 'joshua_project',
      language_filter: m[1].trim(),
      location: m[2]?.trim() || null,
      _fastQuery: true,
    }),
  ],
  // "Show people groups in the 10/40 window"
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(?:people\s+groups?|ethnic\s+groups?|peoples?)\s+(?:in|within)\s+(?:the\s+)?10[\s/]?40\s+window/i,
    () => ({ type: 'joshua_project', location: '10/40 window', _fastQuery: true }),
  ],
  // Short forms: "people groups" / "Joshua Project" / "ethnic groups map"
  [
    /(?:people\s+groups?\s+(?:map|data|list)|joshua\s+project|ethnic\s+groups?\s+(?:map|data|list))(?:\s+(?:in|of|for)\s+(.+))?$/i,
    (m) => ({ type: 'joshua_project', location: m[1]?.trim() || null, _fastQuery: true }),
  ],
  // Standalone: "people groups" / "ethnic groups" / "people groups on earth"
  [
    /^(?:show|display)\s+(?:me\s+)?(?:the\s+)?(?:all\s+)?(?:people\s+groups?|ethnic\s+groups?|peoples?\s+of\s+the\s+world)$/i,
    () => ({ type: 'joshua_project', _fastQuery: true }),
  ],

  // ── WHO Global Health Observatory ───────────────────────────
  // "Show malaria cases by country" / "Show malaria in Africa"
  [
    /(?:show|display|map)\s+(?:me\s+)?(?:the\s+)?(?:global\s+)?(?:malaria)\s+(?:cases?|incidence|rates?|by\s+country)?(?:\s+(?:in|of|across|for)\s+(.+))?/i,
    (m) => ({
      type: 'who_health',
      indicator: 'malaria',
      location: m[1]?.trim() || null,
      _fastQuery: true,
    }),
  ],
  // "Show HIV prevalence" / "Show HIV in Africa"
  [
    /(?:show|display|map)\s+(?:me\s+)?(?:the\s+)?(?:global\s+)?(?:hiv|aids)\s+(?:prevalence|rates?|cases?)?(?:\s+(?:in|of|across|for)\s+(.+))?/i,
    (m) => ({
      type: 'who_health',
      indicator: 'hiv',
      location: m[1]?.trim() || null,
      _fastQuery: true,
    }),
  ],
  // "Show tuberculosis rates" / "Show TB in Asia"
  [
    /(?:show|display|map)\s+(?:me\s+)?(?:the\s+)?(?:global\s+)?(?:tuberculosis|tb)\s+(?:incidence|rates?|cases?)?(?:\s+(?:in|of|across|for)\s+(.+))?/i,
    (m) => ({
      type: 'who_health',
      indicator: 'tuberculosis',
      location: m[1]?.trim() || null,
      _fastQuery: true,
    }),
  ],
  // "Show vaccination rates" / "Show DTP3 coverage" / "Show measles vaccination in Africa"
  [
    /(?:show|display|map)\s+(?:me\s+)?(?:the\s+)?(?:global\s+)?(?:vaccination|vaccine|immunization|dtp3?|measles(?:\s+vaccine)?)\s+(?:rates?|coverage)?(?:\s+(?:in|of|across|for)\s+(.+))?/i,
    (m) => {
      const txt = m[0].toLowerCase()
      const indicator = txt.includes('measles') ? 'measles' : 'vaccination'
      return { type: 'who_health', indicator, location: m[1]?.trim() || null, _fastQuery: true }
    },
  ],
  // "Show maternal mortality" / "Show child mortality in Africa"
  [
    /(?:show|display|map)\s+(?:me\s+)?(?:the\s+)?(?:global\s+)?(?:maternal|child|under-?5|infant|ncd)\s+mortality(?:\s+(?:rates?|ratio))?(?:\s+(?:in|of|across|for)\s+(.+))?/i,
    (m) => {
      const txt = m[0].toLowerCase()
      let indicator = 'under5_mortality'
      if (txt.includes('maternal')) indicator = 'maternal mortality'
      else if (txt.includes('ncd')) indicator = 'ncd mortality'
      return { type: 'who_health', indicator, location: m[1]?.trim() || null, _fastQuery: true }
    },
  ],
  // "Show life expectancy" / "Show life expectancy in Africa"
  [
    /(?:show|display|map)\s+(?:me\s+)?(?:the\s+)?(?:global\s+)?life\s+expectancy(?:\s+(?:in|of|across|for)\s+(.+))?/i,
    (m) => ({
      type: 'who_health',
      indicator: 'life expectancy',
      location: m[1]?.trim() || null,
      _fastQuery: true,
    }),
  ],
  // "Which countries have the fewest/most doctors?" / "Show physicians per capita"
  [
    /(?:show|display|map|which\s+countries?\s+(?:have|has)\s+(?:the\s+)?(?:fewest|most|least))\s+(?:doctors?|physicians?|nurses?|health\s+workers?)(?:\s+(?:in|of|across|for)\s+(.+))?/i,
    (m) => {
      const txt = m[0].toLowerCase()
      const indicator = txt.includes('nurse') ? 'nurses' : 'doctors'
      return { type: 'who_health', indicator, location: m[1]?.trim() || null, _fastQuery: true }
    },
  ],
  // "Show obesity rates" / "Show alcohol consumption" / "Show smoking rates"
  [
    /(?:show|display|map)\s+(?:me\s+)?(?:the\s+)?(?:global\s+)?(?:obesity|alcohol\s+consumption|tobacco|smoking)\s+(?:rates?|prevalence|per\s+capita)?(?:\s+(?:in|of|across|for)\s+(.+))?/i,
    (m) => {
      const txt = m[0].toLowerCase()
      let indicator = 'obesity'
      if (txt.includes('alcohol')) indicator = 'alcohol'
      else if (txt.includes('tobacco') || txt.includes('smoking')) indicator = 'smoking'
      return { type: 'who_health', indicator, location: m[1]?.trim() || null, _fastQuery: true }
    },
  ],
  // "Show clean water access" / "Show sanitation" / "Show safe water"
  [
    /(?:show|display|map)\s+(?:me\s+)?(?:the\s+)?(?:global\s+)?(?:clean\s+water|safe\s+water|drinking\s+water|water\s+access|sanitation|wash)\s+(?:access|rates?|coverage)?(?:\s+(?:in|of|across|for)\s+(.+))?/i,
    (m) => {
      const txt = m[0].toLowerCase()
      const indicator = txt.includes('sanitation') ? 'sanitation' : 'clean water'
      return { type: 'who_health', indicator, location: m[1]?.trim() || null, _fastQuery: true }
    },
  ],
  // "Show health spending per capita" / "Show healthcare spending"
  [
    /(?:show|display|map)\s+(?:me\s+)?(?:the\s+)?(?:global\s+)?(?:health(?:care)?\s+(?:spending|expenditure))\s*(?:per\s+capita)?(?:\s+(?:in|of|across|for)\s+(.+))?/i,
    (m) => ({
      type: 'who_health',
      indicator: 'health spending',
      location: m[1]?.trim() || null,
      _fastQuery: true,
    }),
  ],
  // "Show child stunting" / "Show malnutrition" / "Show child wasting"
  [
    /(?:show|display|map)\s+(?:me\s+)?(?:the\s+)?(?:global\s+)?(?:child\s+)?(?:stunting|wasting|malnutrition|nutrition)(?:\s+(?:rates?|prevalence))?(?:\s+(?:in|of|across|for)\s+(.+))?/i,
    (m) => {
      const txt = m[0].toLowerCase()
      const indicator = txt.includes('wasting') ? 'wasting' : 'stunting'
      return { type: 'who_health', indicator, location: m[1]?.trim() || null, _fastQuery: true }
    },
  ],
  // Generic WHO health: "Show WHO health data" / "global health indicators"
  [
    /(?:who\s+health|global\s+health\s+(?:data|indicators?|statistics?))/i,
    () => ({ type: 'who_health', indicator: 'life expectancy', _fastQuery: true }),
  ],

  // ── FAO Aquastat — Water Resources ──────────────────────────
  // "Show water stress levels" / "Show water stress in Africa"
  [
    /(?:show|display|map)\s+(?:me\s+)?(?:the\s+)?(?:global\s+)?water\s+(?:stress|scarcity|crisis)(?:\s+(?:levels?|rates?|index))?(?:\s+(?:in|of|across|for)\s+(.+))?/i,
    (m) => ({
      type: 'aquastat',
      indicator: 'water stress',
      location: m[1]?.trim() || null,
      _fastQuery: true,
    }),
  ],
  // "Show water use by country" / "Show water withdrawal"
  [
    /(?:show|display|map)\s+(?:me\s+)?(?:the\s+)?(?:global\s+)?water\s+(?:use|usage|withdrawal|consumption)(?:\s+(?:by\s+country))?(?:\s+(?:in|of|across|for)\s+(.+))?/i,
    (m) => ({
      type: 'aquastat',
      indicator: 'water withdrawal',
      location: m[1]?.trim() || null,
      _fastQuery: true,
    }),
  ],
  // "Which countries use the most irrigation?" / "Show irrigated land"
  [
    /(?:show|display|map|which\s+countries?\s+(?:use|have)\s+(?:the\s+)?(?:most|least))\s+(?:irrigation|irrigated\s+land)(?:\s+(?:in|of|across|for)\s+(.+))?/i,
    (m) => ({
      type: 'aquastat',
      indicator: 'irrigation',
      location: m[1]?.trim() || null,
      _fastQuery: true,
    }),
  ],
  // "Show freshwater availability" / "Show renewable water resources"
  [
    /(?:show|display|map)\s+(?:me\s+)?(?:the\s+)?(?:global\s+)?(?:freshwater|renewable\s+water|water\s+resources?)(?:\s+(?:availability|per\s+capita))?(?:\s+(?:in|of|across|for)\s+(.+))?/i,
    (m) => ({
      type: 'aquastat',
      indicator: 'freshwater per capita',
      location: m[1]?.trim() || null,
      _fastQuery: true,
    }),
  ],
  // "Show agricultural water use" / "Show industrial water use" / "Show domestic water use"
  [
    /(?:show|display|map)\s+(?:me\s+)?(?:the\s+)?(?:global\s+)?(?:agricultural|farm|industrial|domestic|household)\s+water\s+(?:use|usage|withdrawal|consumption)(?:\s+(?:in|of|across|for)\s+(.+))?/i,
    (m) => {
      const txt = m[0].toLowerCase()
      let indicator = 'agricultural water'
      if (txt.includes('industrial')) indicator = 'industrial water'
      else if (txt.includes('domestic') || txt.includes('household')) indicator = 'domestic water'
      return { type: 'aquastat', indicator, location: m[1]?.trim() || null, _fastQuery: true }
    },
  ],
  // "Compare freshwater availability: Middle East" / "Show water data for Asia"
  [
    /(?:compare|show)\s+(?:freshwater|water)\s+(?:availability|resources?|data)(?:\s*:?\s*(?:in|of|across|for)\s+(.+))?/i,
    (m) => ({
      type: 'aquastat',
      indicator: 'freshwater per capita',
      location: m[1]?.trim() || null,
      _fastQuery: true,
    }),
  ],
  // Short forms: "water resources" / "aquastat" / "water data"
  [
    /(?:aquastat|water\s+resources?\s+(?:data|map|by\s+country))(?:\s+(?:in|of|for)\s+(.+))?$/i,
    (m) => ({
      type: 'aquastat',
      indicator: 'water stress',
      location: m[1]?.trim() || null,
      _fastQuery: true,
    }),
  ],

  // ── WTO Trade Statistics ──────────────────────────────────
  // "Show exports by country" / "Show global exports" / "Show merchandise exports"
  [
    /(?:show|display|map)\s+(?:me\s+)?(?:the\s+)?(?:global\s+)?(?:merchandise\s+)?(?:exports?|export\s+(?:volume|data|map))(?:\s+(?:by\s+country))?(?:\s+(?:in|of|across|for)\s+(.+))?/i,
    (m) => ({
      type: 'wto_trade',
      indicator: 'exports',
      location: m[1]?.trim() || null,
      _fastQuery: true,
    }),
  ],
  // "Show imports by country" / "Show global imports"
  [
    /(?:show|display|map)\s+(?:me\s+)?(?:the\s+)?(?:global\s+)?(?:merchandise\s+)?(?:imports?|import\s+(?:volume|data|map))(?:\s+(?:by\s+country))?(?:\s+(?:in|of|across|for)\s+(.+))?/i,
    (m) => ({
      type: 'wto_trade',
      indicator: 'imports',
      location: m[1]?.trim() || null,
      _fastQuery: true,
    }),
  ],
  // "Show trade balance" / "Show trade deficit" / "Show trade surplus"
  [
    /(?:show|display|map)\s+(?:me\s+)?(?:the\s+)?(?:global\s+)?trade\s+(?:balance|deficit|surplus)(?:\s+(?:in|of|across|for)\s+(.+))?/i,
    (m) => ({
      type: 'wto_trade',
      indicator: 'trade balance',
      location: m[1]?.trim() || null,
      _fastQuery: true,
    }),
  ],
  // "Show trade openness" / "Show trade as percent of GDP"
  [
    /(?:show|display|map)\s+(?:me\s+)?(?:the\s+)?(?:global\s+)?trade\s+(?:openness|as\s+(?:percent|%)\s+of\s+gdp|to\s+gdp)(?:\s+(?:in|of|across|for)\s+(.+))?/i,
    (m) => ({
      type: 'wto_trade',
      indicator: 'trade openness',
      location: m[1]?.trim() || null,
      _fastQuery: true,
    }),
  ],
  // "Show trade flows from China" / "Show trade with Japan"
  [
    /(?:show|display|map)\s+(?:me\s+)?(?:the\s+)?trade\s+(?:flows?|partners?|with|from|data)(?:\s+(?:from|with|of|for)\s+(.+))?/i,
    (m) => ({
      type: 'wto_trade',
      indicator: 'exports',
      partner: m[1]?.trim() || null,
      location: m[1]?.trim() || null,
      _fastQuery: true,
    }),
  ],
  // "What does Germany export?" / "What does China trade?"
  [
    /what\s+does?\s+(.+?)\s+(?:export|import|trade)/i,
    (m) => ({
      type: 'wto_trade',
      indicator: 'exports',
      partner: m[1]?.trim(),
      location: m[1]?.trim(),
      _fastQuery: true,
    }),
  ],
  // "Show high-tech exports" / "Show technology exports"
  [
    /(?:show|display|map)\s+(?:me\s+)?(?:the\s+)?(?:global\s+)?(?:high[\s-]?tech(?:nology)?|tech)\s+exports?(?:\s+(?:in|of|across|for)\s+(.+))?/i,
    (m) => ({
      type: 'wto_trade',
      indicator: 'high tech',
      location: m[1]?.trim() || null,
      _fastQuery: true,
    }),
  ],
  // "Show service exports" / "Show services trade"
  [
    /(?:show|display|map)\s+(?:me\s+)?(?:the\s+)?(?:global\s+)?(?:service|services)\s+(?:exports?|trade)(?:\s+(?:in|of|across|for)\s+(.+))?/i,
    (m) => ({
      type: 'wto_trade',
      indicator: 'service exports',
      location: m[1]?.trim() || null,
      _fastQuery: true,
    }),
  ],
  // "Show current account balance" / "Show current account"
  [
    /(?:show|display|map)\s+(?:me\s+)?(?:the\s+)?(?:global\s+)?current\s+account(?:\s+balance)?(?:\s+(?:in|of|across|for)\s+(.+))?/i,
    (m) => ({
      type: 'wto_trade',
      indicator: 'current account',
      location: m[1]?.trim() || null,
      _fastQuery: true,
    }),
  ],
  // "Compare exports: Japan vs South Korea" — comparison pattern
  [
    /compare\s+(?:exports?|imports?|trade)\s*(?::|\s+)\s*(.+?)\s+(?:vs?\.?|versus|and)\s+(.+)/i,
    (m) => ({
      type: 'wto_trade',
      indicator: 'exports',
      location: null,
      compare: [m[1].trim(), m[2].trim()],
      _fastQuery: true,
    }),
  ],
  // Short forms: "wto trade" / "trade statistics" / "trade data"
  [
    /(?:wto\s+trade|trade\s+(?:statistics|data|map))(?:\s+(?:in|of|for)\s+(.+))?$/i,
    (m) => ({
      type: 'wto_trade',
      indicator: 'exports',
      location: m[1]?.trim() || null,
      _fastQuery: true,
    }),
  ],

  // ── Natural Earth Physical Features ─────────────────────────
  // "Show mountain ranges" / "Show mountains in Asia"
  [
    /(?:show|display|where\s+are)\s+(?:me\s+)?(?:the\s+)?(?:world'?s?\s+)?(?:major\s+)?(?:mountain\s+ranges?|mountains?)(?:\s+(?:in|of|across|for)\s+(.+))?/i,
    (m) => ({
      type: 'physical_feature',
      feature: 'mountains',
      location: m[1]?.trim() || null,
      _fastQuery: true,
    }),
  ],
  // "Show deserts" / "Where are the world's deserts?" / "Show the Sahara Desert"
  [
    // CL-144: "desert climates/zones" is a Köppen query — fall through.
    /(?:show|display|where\s+are)\s+(?:me\s+)?(?:the\s+)?(?:world'?s?\s+)?(?:major\s+)?(?:deserts?)\b(?!\s+(?:climates?|zones?))(?:\s+(?:in|of|across|for)\s+(.+))?/i,
    (m) => ({
      type: 'physical_feature',
      feature: 'deserts',
      location: m[1]?.trim() || null,
      _fastQuery: true,
    }),
  ],
  // Specific desert: "Show the Sahara" / "Show the Gobi Desert"
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(sahara|gobi|kalahari|mojave|sonoran|atacama|arabian|thar|namib|karakum|simpson|gibson)(?:\s+desert)?/i,
    (m) => ({
      type: 'physical_feature',
      feature: 'deserts',
      name: m[1].trim(),
      location: m[1].trim(),
      _fastQuery: true,
    }),
  ],
  // "Show glaciers" / "Where are glaciers?" / "Show ice sheets"
  [
    /(?:show|display|where\s+are)\s+(?:me\s+)?(?:the\s+)?(?:world'?s?\s+)?(?:major\s+)?(?:glaciers?|ice\s+sheets?|ice\s+caps?|glaciated\s+areas?)(?:\s+(?:in|of|across|for)\s+(.+))?/i,
    (m) => ({
      type: 'physical_feature',
      feature: 'glaciated',
      location: m[1]?.trim() || null,
      _fastQuery: true,
    }),
  ],
  // "Show coral reefs" / "Where are coral reefs?" / "Show the Great Barrier Reef"
  [
    /(?:show|display|where\s+are)\s+(?:me\s+)?(?:the\s+)?(?:world'?s?\s+)?(?:major\s+)?(?:coral\s+)?reefs?(?:\s+(?:in|of|across|for)\s+(.+))?/i,
    (m) => ({
      type: 'physical_feature',
      feature: 'reefs',
      location: m[1]?.trim() || null,
      _fastQuery: true,
    }),
  ],
  // "Show major lakes" / "Show lakes in Africa"
  [
    /(?:show|display|where\s+are)\s+(?:me\s+)?(?:the\s+)?(?:world'?s?\s+)?(?:major\s+|great\s+)?lakes?(?:\s+(?:in|of|across|for)\s+(.+))?/i,
    (m) => ({
      type: 'physical_feature',
      feature: 'lakes',
      location: m[1]?.trim() || null,
      _fastQuery: true,
    }),
  ],
  // "Show the oceans" / "Show me the five oceans" / "Where are the oceans?"
  [
    /(?:show|display|where\s+are)\s+(?:me\s+)?(?:the\s+)?(?:world'?s?\s+)?(?:five\s+)?oceans?(?:\s+(?:in|of|across|for)\s+(.+))?$/i,
    (m) => ({
      type: 'physical_feature',
      feature: 'oceans',
      location: m[1]?.trim() || null,
      _fastQuery: true,
    }),
  ],
  // Specific ocean: "Show the Pacific Ocean" / "Show me the Atlantic Ocean".
  // "ocean" is required so "show Indian states" doesn't match the Indian Ocean.
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(pacific|atlantic|indian|arctic|southern)\s+ocean\b/i,
    (m) => ({
      type: 'physical_feature',
      feature: 'oceans',
      name: `${m[1].trim()} Ocean`,
      location: m[1].trim(),
      _fastQuery: true,
    }),
  ],
  // "Show the seas" / "Where are the seas?" (all named seas).
  [
    /(?:show|display|where\s+are)\s+(?:me\s+)?(?:the\s+)?(?:world'?s?\s+)?seas(?:\s+(?:in|of|across|for)\s+(.+))?$/i,
    (m) => ({
      type: 'physical_feature',
      feature: 'seas',
      location: m[1]?.trim() || null,
      _fastQuery: true,
    }),
  ],
  // Specific sea: "Show the Mediterranean Sea" / "Show the Caribbean Sea".
  // Requires the word "sea" (with a space before it) so "Chelsea" etc. can't
  // match. The captured name is filtered against the layer's `name` field.
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?([a-z][a-z\s]{2,28}?)\s+sea\b/i,
    (m) => ({
      type: 'physical_feature',
      feature: 'seas',
      name: `${m[1].trim()} Sea`,
      location: m[1].trim(),
      _fastQuery: true,
    }),
  ],
  // "Show the gulfs" (all named gulfs).
  [
    /(?:show|display|where\s+are)\s+(?:me\s+)?(?:the\s+)?(?:world'?s?\s+)?gulfs(?:\s+(?:in|of|across|for)\s+(.+))?$/i,
    (m) => ({
      type: 'physical_feature',
      feature: 'gulfs',
      location: m[1]?.trim() || null,
      _fastQuery: true,
    }),
  ],
  // "Show the Gulf of Mexico" / "Gulf of Guinea" (Gulf-of-X form — try before X-Gulf).
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?gulf\s+of\s+([a-z][a-z\s]{2,28}?)\b/i,
    (m) => ({
      type: 'physical_feature',
      feature: 'gulfs',
      name: `Gulf of ${m[1].trim()}`,
      location: m[1].trim(),
      _fastQuery: true,
    }),
  ],
  // "Show the Persian Gulf" (X-Gulf form).
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?([a-z][a-z\s]{2,20}?)\s+gulf\b/i,
    (m) => ({
      type: 'physical_feature',
      feature: 'gulfs',
      name: `${m[1].trim()} Gulf`,
      location: m[1].trim(),
      _fastQuery: true,
    }),
  ],
  // "Show the bays" (all named bays).
  [
    /(?:show|display|where\s+are)\s+(?:me\s+)?(?:the\s+)?(?:world'?s?\s+)?bays(?:\s+(?:in|of|across|for)\s+(.+))?$/i,
    (m) => ({
      type: 'physical_feature',
      feature: 'bays',
      location: m[1]?.trim() || null,
      _fastQuery: true,
    }),
  ],
  // "Show the Bay of Bengal" / "Bay of Biscay" (Bay-of-X form — try before X-Bay).
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?bay\s+of\s+([a-z][a-z\s]{2,28}?)\b/i,
    (m) => ({
      type: 'physical_feature',
      feature: 'bays',
      name: `Bay of ${m[1].trim()}`,
      location: m[1].trim(),
      _fastQuery: true,
    }),
  ],
  // "Show the Hudson Bay" / "Baffin Bay" (X-Bay form).
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?([a-z][a-z\s]{2,20}?)\s+bay\b/i,
    (m) => ({
      type: 'physical_feature',
      feature: 'bays',
      name: `${m[1].trim()} Bay`,
      location: m[1].trim(),
      _fastQuery: true,
    }),
  ],
  // ── Geographic reference lines (equator, tropics, polar circles, date line) ──
  // "Show the equator and the tropics" — a compound request naming two line
  // families. Must precede the plain-equator pattern below, which would
  // otherwise match first and silently drop the tropics half of the question.
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(?:equator\s+and\s+(?:the\s+)?tropics?|tropics?\s+and\s+(?:the\s+)?equator)\b/i,
    () => ({
      type: 'physical_feature',
      feature: 'geographic_lines',
      names: ['Equator', 'Tropic'],
      _fastQuery: true,
    }),
  ],
  // "Show the equator"
  [
    /(?:show|display|where\s+is)\s+(?:me\s+)?(?:the\s+)?equator\b/i,
    () => ({
      type: 'physical_feature',
      feature: 'geographic_lines',
      name: 'Equator',
      _fastQuery: true,
    }),
  ],
  // "Show the Tropic of Cancer" / "Tropic of Capricorn".
  // The trailing word is matched loosely and then normalised, so a mistyped or
  // mis-transcribed "capicorn" / "capricon" / "canser" still lands on the right
  // line. Previously anything but the exact spelling fell through to the LLM,
  // which mis-routed "tropic of capicorn" to the timezones layer.
  [
    /(?:show|display|where\s+is)\s+(?:me\s+)?(?:the\s+)?tropics?\s+of\s+([a-z]+)\b/i,
    (m) => {
      const w = (m[1] || '').toLowerCase()
      const name = w.startsWith('can')
        ? 'Tropic of Cancer'
        : w.startsWith('cap')
          ? 'Tropic of Capricorn'
          : 'Tropic'
      return { type: 'physical_feature', feature: 'geographic_lines', name, _fastQuery: true }
    },
  ],
  // "Show the tropics" (both tropic lines — name filter "Tropic" matches both)
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?tropics\b/i,
    () => ({
      type: 'physical_feature',
      feature: 'geographic_lines',
      name: 'Tropic',
      _fastQuery: true,
    }),
  ],
  // "Show the Arctic Circle" / "Antarctic Circle"
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(arctic|antarctic)\s+circle\b/i,
    (m) => ({
      type: 'physical_feature',
      feature: 'geographic_lines',
      name: `${m[1][0].toUpperCase()}${m[1].slice(1).toLowerCase()} Circle`,
      _fastQuery: true,
    }),
  ],
  // "Show the polar circles" / "Show the circles" (both — name filter "Circle")
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(?:polar\s+)?circles\b/i,
    () => ({
      type: 'physical_feature',
      feature: 'geographic_lines',
      name: 'Circle',
      _fastQuery: true,
    }),
  ],
  // "Show the date line" / "the International Date Line"
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(?:international\s+)?date\s+line\b/i,
    () => ({
      type: 'physical_feature',
      feature: 'geographic_lines',
      name: 'Date Line',
      _fastQuery: true,
    }),
  ],
  // "Show the reference lines" / "latitude lines" / "the lines on the globe" (all)
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(?:reference|latitude|geographic|globe|grid)\s+lines\b/i,
    () => ({ type: 'physical_feature', feature: 'geographic_lines', _fastQuery: true }),
  ],
  // ── Biomes (RESOLVE Ecoregions 2017) ──
  // "Show the biomes" (all 14 + ice).
  [
    /(?:show|display|where\s+are)\s+(?:me\s+)?(?:the\s+)?biomes\b/i,
    () => ({ type: 'physical_feature', feature: 'biomes', _fastQuery: true }),
  ],
  // Friendly habitat names → filter to the matching biome(s) by name substring.
  [
    // CL-144: "rainforest/tundra/savanna + regions|zones|climates" (and
    // "rainforests located") are Köppen climate-zone queries — the lookaheads
    // let them fall through to the climate_zones patterns / concept registry.
    /(?:show|display|where\s+are)\s+(?:me\s+)?(?:the\s+)?rainforests?\b(?!\s+(?:regions?|zones?|climates?|located))/i,
    () => ({
      type: 'physical_feature',
      feature: 'biomes',
      name: 'Moist Broadleaf',
      _fastQuery: true,
    }),
  ],
  [
    /(?:show|display|where\s+is)\s+(?:me\s+)?(?:the\s+)?tundra\b(?!\s+(?:regions?|zones?|climates?))/i,
    () => ({ type: 'physical_feature', feature: 'biomes', name: 'Tundra', _fastQuery: true }),
  ],
  [
    /(?:show|display|where\s+are)\s+(?:me\s+)?(?:the\s+)?(?:savannas?|grasslands?)\b(?!\s+(?:regions?|zones?|climates?))/i,
    () => ({ type: 'physical_feature', feature: 'biomes', name: 'Grasslands', _fastQuery: true }),
  ],
  [
    /(?:show|display|where\s+are)\s+(?:me\s+)?(?:the\s+)?(?:taiga|boreal(?:\s+forests?)?)\b/i,
    () => ({ type: 'physical_feature', feature: 'biomes', name: 'Taiga', _fastQuery: true }),
  ],
  [
    /(?:show|display|where\s+are)\s+(?:me\s+)?(?:the\s+)?mangroves?\b/i,
    () => ({ type: 'physical_feature', feature: 'biomes', name: 'Mangroves', _fastQuery: true }),
  ],
  // ── Climate zones (latitude bands: polar / temperate / tropical) ──
  // "Show the temperature zones" / "hot and cold belts/zones".
  // CL-144: "climate zones" now routes to the Köppen climate_zones dataset
  // (see the Köppen patterns below + golden pc-401); only the explicitly
  // latitude-band phrasings stay on the physical_feature layer.
  [
    /(?:show|display|where\s+are)\s+(?:me\s+)?(?:the\s+)?(?:temperature|(?:hot\s+and\s+cold))\s+(?:zones?|belts?)\b/i,
    () => ({ type: 'physical_feature', feature: 'climate_zones', _fastQuery: true }),
  ],
  // "Show the tropical zone"
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?tropical\s+zone\b/i,
    () => ({
      type: 'physical_feature',
      feature: 'climate_zones',
      name: 'Tropical',
      _fastQuery: true,
    }),
  ],
  // "Show the polar zones" / "the cold zones" (both polar caps — name filter "Polar")
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(?:polar|cold)\s+zones?\b/i,
    () => ({ type: 'physical_feature', feature: 'climate_zones', name: 'Polar', _fastQuery: true }),
  ],
  // "Show the mild zones" (temperate latitude band — name filter "Temperate").
  // CL-144: "temperate zones" itself now routes to the Köppen dataset
  // (golden pc-405); only the kid-phrasing "mild zones" stays here.
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?mild\s+zones?\b/i,
    () => ({
      type: 'physical_feature',
      feature: 'climate_zones',
      name: 'Temperate',
      _fastQuery: true,
    }),
  ],
  // ── Landforms (Natural Earth physical regions: plains/plateaus/etc) ──
  // "Show the landforms" (all types, colored by landform_type).
  [
    /(?:show|display|where\s+are)\s+(?:me\s+)?(?:the\s+)?landforms?\b/i,
    () => ({ type: 'physical_feature', feature: 'landforms', _fastQuery: true }),
  ],
  // Specific landform types → filter by landform_type.
  [
    /(?:show|display|where\s+are)\s+(?:me\s+)?(?:the\s+)?(?:world'?s\s+)?plains\b/i,
    () => ({
      type: 'physical_feature',
      feature: 'landforms',
      landform_type: 'Plain',
      _fastQuery: true,
    }),
  ],
  [
    /(?:show|display|where\s+are)\s+(?:me\s+)?(?:the\s+)?(?:world'?s\s+)?plateaus?\b/i,
    () => ({
      type: 'physical_feature',
      feature: 'landforms',
      landform_type: 'Plateau',
      _fastQuery: true,
    }),
  ],
  [
    /(?:show|display|where\s+are)\s+(?:me\s+)?(?:the\s+)?peninsulas?\b/i,
    () => ({
      type: 'physical_feature',
      feature: 'landforms',
      landform_type: 'Peninsula',
      _fastQuery: true,
    }),
  ],
  [
    /(?:show|display|where\s+are)\s+(?:me\s+)?(?:the\s+)?(?:world'?s\s+)?islands\b/i,
    () => ({
      type: 'physical_feature',
      feature: 'landforms',
      landform_type: 'Island',
      _fastQuery: true,
    }),
  ],
  [
    /(?:show|display|where\s+are)\s+(?:me\s+)?(?:the\s+)?valleys?\b/i,
    () => ({
      type: 'physical_feature',
      feature: 'landforms',
      landform_type: 'Valley',
      _fastQuery: true,
    }),
  ],
  [
    /(?:show|display|where\s+are)\s+(?:me\s+)?(?:the\s+)?basins?\b/i,
    () => ({
      type: 'physical_feature',
      feature: 'landforms',
      landform_type: 'Basin',
      _fastQuery: true,
    }),
  ],
  // ── Capital cities ──
  // "Show the capitals" / "Show the capital cities" (all national capitals).
  [
    /(?:show|display|where\s+are)\s+(?:me\s+)?(?:the\s+)?(?:world'?s\s+)?capitals?(?:\s+cities)?\b/i,
    () => ({ type: 'physical_feature', feature: 'capitals', _fastQuery: true }),
  ],
  // ── Major cities (sized by population) ──
  // "Show the big cities" / "biggest cities" / "largest cities" / "major cities".
  [
    /(?:show|display|where\s+are)\s+(?:me\s+)?(?:the\s+)?(?:big|biggest|largest|major|world'?s\s+(?:biggest|largest))\s+cities\b/i,
    () => ({ type: 'physical_feature', feature: 'major_cities', _fastQuery: true }),
  ],
  // ── Animals & their homes (emoji markers tied to biomes) ──
  // "Show the animals" / "Where do animals live" / "Show animal homes".
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(?:animals|animal\s+homes|wildlife)\b/i,
    () => ({ type: 'physical_feature', feature: 'animals', _fastQuery: true }),
  ],
  [
    /where\s+do\s+(?:the\s+)?animals\s+live\b/i,
    () => ({ type: 'physical_feature', feature: 'animals', _fastQuery: true }),
  ],
  // ── Country neighbors / borders (adjacency) ──
  // Highlights the bordering countries via the country_neighbors table.
  // "Show the neighbors of France" / "Show France's neighbors"
  [
    /(?:show|display|highlight)\s+(?:me\s+)?(?:the\s+)?neighbou?rs?\s+of\s+(.+)/i,
    (m) => ({ type: 'neighbors', country: m[1].trim().replace(/[?.!]+$/, ''), _fastQuery: true }),
  ],
  [
    /(?:show|display|highlight)\s+(?:me\s+)?(.+?)['’]?s\s+neighbou?rs?\b/i,
    (m) => ({ type: 'neighbors', country: m[1].trim().replace(/['’]$/, ''), _fastQuery: true }),
  ],
  // Specific mountain ranges: "Show the Himalayas" / "Show the Andes" / "Show the Alps"
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(himalayas?|andes|alps|rockies|rocky\s+mountains?|appalachian|urals?|atlas|pyrenees|caucasus|karakoram|sierra\s+nevada)/i,
    (m) => ({
      type: 'physical_feature',
      feature: 'mountains',
      name: m[1].trim(),
      location: m[1].trim(),
      _fastQuery: true,
    }),
  ],
  // Generic: "Show physical features" / "Show landforms"
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(?:physical\s+features?|landforms?)(?:\s+(?:in|of|for)\s+(.+))?$/i,
    (m) => ({
      type: 'physical_feature',
      feature: 'mountains',
      location: m[1]?.trim() || null,
      _fastQuery: true,
    }),
  ],

  // ── ACLED conflict events (B6) ────────────────────────────
  // "Show protests/battles/riots/violence in <location>" — event_type first so
  // it isn't swallowed by the generic conflicts pattern below.
  [
    /(?:show|display|where\s+are)\s+(?:me\s+)?(?:the\s+)?(?:recent\s+)?(protests?|battles?|riots?|airstrikes?|bombings?|massacres?|violence\s+against\s+civilians)(?:\s+(?:in|of|across|near|around)\s+(.+))?/i,
    (m) => ({
      type: 'acled',
      event_type: m[1].trim(),
      location: m[2]?.trim() || null,
      _fastQuery: true,
    }),
  ],
  // "Show conflicts" / "Show recent conflicts" / "Show unrest in the Sahel"
  [
    /(?:show|display|where\s+are)\s+(?:me\s+)?(?:the\s+)?(?:recent\s+)?(?:armed\s+)?(?:conflicts?|conflict\s+events?|unrest|fighting|war)(?:\s+(?:in|of|across|near|around)\s+(.+))?/i,
    (m) => ({ type: 'acled', location: m[1]?.trim() || null, _fastQuery: true }),
  ],

  // ── Volcanoes (Smithsonian GVP) ───────────────────────────
  // "Show volcanoes" / "Show volcanoes in Japan"
  [
    /(?:show|display|where\s+are)\s+(?:me\s+)?(?:the\s+)?(?:world'?s?\s+)?(?:all\s+)?volcanoes?(?:\s+(?:in|of|across|near|around)\s+(.+))?/i,
    (m) => ({ type: 'volcanoes', location: m[1]?.trim() || null, _fastQuery: true }),
  ],
  // "Show active volcanoes" / "Show active volcanoes in Indonesia"
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(?:active|historically?\s+active)\s+volcanoes?(?:\s+(?:in|of|across|near)\s+(.+))?/i,
    (m) => ({
      type: 'volcanoes',
      active_only: true,
      location: m[1]?.trim() || null,
      _fastQuery: true,
    }),
  ],
  // "Show volcanoes in the Ring of Fire"
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(?:volcanoes?\s+(?:in|along|on)\s+(?:the\s+)?)?ring\s+of\s+fire/i,
    () => ({ type: 'volcanoes', location: 'Ring of Fire', _fastQuery: true }),
  ],
  // "Show stratovolcanoes" / "Show shield volcanoes" / "Show calderas"
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(?:all\s+)?(stratovolcanoes?|shield\s+volcanoes?|calderas?|cinder\s+cones?|submarine\s+volcanoes?|lava\s+domes?|fissure\s+volcanoes?)(?:\s+(?:in|of|across|near)\s+(.+))?/i,
    (m) => ({
      type: 'volcanoes',
      volcano_type: m[1].trim(),
      location: m[2]?.trim() || null,
      _fastQuery: true,
    }),
  ],
  // "Which volcanoes erupted in the last 10 years?" / "Recent volcanic eruptions"
  [
    /(?:which|what)\s+volcanoes?\s+(?:have\s+)?erupted\s+(?:in\s+)?(?:the\s+)?(?:last|past)\s+(\d+)\s+years?/i,
    (m) => ({ type: 'volcanoes', recent_years: parseInt(m[1]), _fastQuery: true }),
  ],
  [
    /(?:recent|latest)\s+(?:volcanic\s+)?eruptions?(?:\s+(?:in|of|across|near)\s+(.+))?/i,
    (m) => ({
      type: 'volcanoes',
      recent_years: 25,
      location: m[1]?.trim() || null,
      _fastQuery: true,
    }),
  ],
  // "Show Mount Vesuvius" / "Show Krakatoa" / "Show Mount Fuji"
  [
    /(?:show|display|where\s+is)\s+(?:me\s+)?(?:the\s+)?(?:mount\s+|mt\.?\s+)?(vesuvius|krakatoa|pinatubo|fuji|etna|st\.?\s*helens?|tambora|kilauea|mauna\s+loa|stromboli|popocatepetl|cotopaxi|eyjafjallaj[oö]kull|rainier|kilimanjaro|erebus)(?:\s+volcano)?/i,
    (m) => ({ type: 'volcanoes', name: m[1].trim(), _fastQuery: true }),
  ],
  // "Show volcanic hazard zones" / "eruption history"
  [/(?:volcanic\s+hazard|eruption\s+histor)/i, () => ({ type: 'volcanoes', _fastQuery: true })],

  // ── NOAA Climate Data (Ocean Currents) ──────────────
  // "Show ocean currents" / "Show ocean currents in the Atlantic"
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(?:major\s+)?(?:ocean|sea)\s+currents?(?:\s+(?:in|of|across)\s+(?:the\s+)?(.+))?/i,
    (m) => ({
      type: 'noaa_climate',
      sub_command: 'currents',
      location: m[1]?.trim() || null,
      _fastQuery: true,
    }),
  ],
  // "Show warm ocean currents" / "Show cold currents"
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(warm|cold)\s+(?:ocean\s+)?currents?(?:\s+(?:in|of|across)\s+(?:the\s+)?(.+))?/i,
    (m) => ({
      type: 'noaa_climate',
      sub_command: 'currents',
      current_type: m[1].toLowerCase(),
      location: m[2]?.trim() || null,
      _fastQuery: true,
    }),
  ],
  // "Where are the warmest oceans?" / "Where are the coldest oceans?"
  [
    /where\s+(?:are|is)\s+(?:the\s+)?(warmest|coldest|hottest|freezing)\s+(?:ocean|sea|water)/i,
    (m) => ({
      type: 'noaa_climate',
      sub_command: /cold|freez/.test(m[1]) ? 'coldest' : 'warmest',
      _fastQuery: true,
    }),
  ],
  // "Show El Niño" / "Show El Nino pattern" / "Show La Niña"
  [
    /(?:show|display|explain)\s+(?:me\s+)?(?:the\s+)?(?:el\s*ni[ñn]o|la\s*ni[ñn]a|enso)(?:\s+pattern)?/i,
    () => ({ type: 'noaa_climate', sub_command: 'el_nino', _fastQuery: true }),
  ],
  // "Show Gulf Stream" / "Show Kuroshio" / "Show the Brazil Current"
  // Two groups: unambiguous current names (m[1]) match with "current" optional;
  // names that collide with countries / states / regions (m[2] — brazil,
  // california, alaska, canary, labrador, somali, humboldt, benguela, agulhas)
  // REQUIRE the word "current", so "Show me Brazil" falls through to country
  // resolution instead of rendering the Brazil Current. (BUG-061)
  [
    /(?:show|display|where\s+is)\s+(?:me\s+)?(?:the\s+)?(?:(gulf\s+stream|kuroshio|oyashio|antarctic\s+circumpolar|north\s+atlantic\s+drift|east\s+australian)(?:\s+current)?|(brazil|california|canary|labrador|somali|alaska|humboldt|benguela|agulhas)\s+current)/i,
    (m) => ({
      type: 'noaa_climate',
      sub_command: 'currents',
      location: (m[1] || m[2]).trim(),
      _fastQuery: true,
    }),
  ],
  // "Show thermohaline circulation" / "Show ocean circulation"
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(?:thermohaline|global\s+ocean|deep\s+ocean)\s+circulation/i,
    () => ({ type: 'noaa_climate', sub_command: 'currents', _fastQuery: true }),
  ],

  // ── Terrain analysis (GeoLibre raster: elevation / slope / aspect / hillshade on a DEM) ──
  // "Show me the slope of the Alps" / "map the aspect of Nepal" /
  // "compute hillshade of the Andes" / "elevation of Switzerland" /
  // "show me the DEM of South America" / "slope of Colorado". Routes to the
  // terrain_analysis op. Elevation renders the raw DEM as a hypsometric map;
  // slope/aspect/hillshade are computed from it.
  // NOTE: 'relief' alone is intentionally NOT a trigger — it is overloaded
  // ("shaded relief" → hillshade, handled below; "3D relief" → the terrain
  // CAMERA op). "shaded relief" is matched explicitly.
  [
    /^(?:show|display|map|compute|calculate|render)\s+(?:me\s+)?(?:the\s+)?(elevation|dem|digital\s+elevation(?:\s+model)?|topography|slope|steepness|slope\s+angle|aspect|hillshade|shaded\s+relief)\s+(?:of|for|across|in|over|around)\s+(.+?)\s*$/i,
    (m) => _terrainAnalysisQuery(m[1], m[2]),
  ],
  [
    /^(elevation|dem|digital\s+elevation(?:\s+model)?|topography|slope|steepness|aspect|hillshade|shaded\s+relief)\s+(?:of|for|across|in|over)\s+(.+?)\s*$/i,
    (m) => _terrainAnalysisQuery(m[1], m[2]),
  ],

  // ── Choropleth by admin term ───────────────────────────────
  // CL-144: "Population of US states" / "Show density of Japanese
  // prefectures" / "Population share of Brazilian states" — metric first,
  // then a (possibly demonym) country + admin term. Previously these leaked
  // into the ADM1 pattern with a garbage country scope.
  [
    /^(?:show\s+(?:me\s+)?(?:the\s+)?)?(population(?:\s+share)?|density)\s+of\s+(?:the\s+)?(.+?)\s+(states?|provinces?|regions?|counties?|districts?|prefectures?|departments?|oblasts?|governorates?|municipalities?|cantons?|emirates?)$/i,
    (m) =>
      _choroplethByTerm(m[3].trim(), m[2].trim(), /density/i.test(m[1]) ? 'density' : 'population'),
  ],
  // "Show population of Nigeria by state"
  [
    /^(?:show\s+(?:me\s+)?(?:the\s+)?)?(population|density)\s+of\s+(?:the\s+)?(.+?)\s+by\s+(state|province|region|county|district|prefecture)s?$/i,
    (m) => _choroplethByTerm(`${m[3].trim()}s`, m[2].trim(), m[1].toLowerCase()),
  ],
  // "Show me the counties of [country] by population" / "districts of India by density"
  [
    /(?:show\s+(?:me\s+)?(?:the\s+)?)?(counties?|districts?|boroughs?|villages?|communes?|sub-?districts?|wards?|municipalities?|parishes?|cantons?|states?|provinces?|regions?|departments?|prefectures?|oblasts?|governorates?|l[aä]nders?|emirate?s?|kreise?|blocks?|tehsils?|taluks?|cells?|arrondissements?|townships?|local\s+government\s+areas?|lgas?)\s+(?:of|in|for)\s+(.+?)\s+by\s+(population|density|area)/i,
    (m) => _choroplethByTerm(m[1].trim(), m[2].trim(), m[3].trim()),
  ],
  // "Show me [country]'s counties by population"
  [
    /(?:show\s+(?:me\s+)?(?:the\s+)?)?(.+?)(?:'s)\s+(counties?|districts?|boroughs?|villages?|communes?|sub-?districts?|wards?|municipalities?|parishes?|cantons?|states?|provinces?|regions?|departments?|prefectures?|oblasts?|governorates?|l[aä]nders?|emirate?s?|kreise?|blocks?|tehsils?|taluks?|cells?|arrondissements?|townships?|local\s+government\s+areas?|lgas?)\s+by\s+(population|density|area)/i,
    (m) => _choroplethByTerm(m[2].trim(), m[1].trim(), m[3].trim()),
  ],
  // "Show me the population of [country]'s counties" / "population of the districts of India"
  [
    /(?:show\s+(?:me\s+)?(?:the\s+)?)?(?:population|density)\s+(?:of\s+)?(?:the\s+)?(counties?|districts?|boroughs?|villages?|communes?|sub-?districts?|wards?|municipalities?|parishes?|cantons?|states?|provinces?|regions?|departments?|prefectures?|oblasts?|governorates?|l[aä]nders?|emirate?s?|kreise?|blocks?|tehsils?|taluks?|cells?|arrondissements?|townships?|local\s+government\s+areas?|lgas?)\s+(?:of|in|for)\s+(.+)/i,
    (m) => _choroplethByTerm(m[1].trim(), m[2].trim(), 'population'),
  ],

  // ── Boundary + capitals ────────────────────────────────────
  [
    /(?:show\s+(?:me\s+)?(?:the\s+)?)?(?:states?|provinces?|regions?|divisions?)\s+(?:and|with|&)\s+(?:(?:state\s+)?capitals?|(?:capital\s+)?cities)\s+(?:of|in|for)\s+(.+)/i,
    (m) => _boundaryAndCapitals(m[1].trim()),
  ],
  [
    /(?:show\s+(?:me\s+)?(?:the\s+)?)?(.+?)(?:'s)?\s+(?:states?|provinces?)\s+(?:and|with|&)\s+(?:(?:state\s+)?capitals?|(?:capital\s+)?cities)/i,
    (m) => _boundaryAndCapitals(m[1].trim()),
  ],
  // "Show me the states of [country] and their capitals"
  [
    /(?:show\s+(?:me\s+)?(?:the\s+)?)?(?:states?|provinces?|regions?)\s+(?:of|in|for)\s+(?:the\s+)?(.+?)\s+and\s+(?:their\s+)?(?:(?:state\s+)?capitals?|(?:capital\s+)?cities)/i,
    (m) => _boundaryAndCapitals(m[1].trim()),
  ],

  // ── Boundary only ──────────────────────────────────────────
  // Deep admin terms: districts, counties, villages, communes, sub-districts, blocks, etc.
  [
    /(?:show\s+(?:me\s+)?(?:the\s+)?)?(?:villages?|communes?|sub-?districts?|tehsils?|taluks?|blocks?|wards?|cells?|arrondissements?|townships?|municipalities?|local\s+government\s+areas?|lgas?|cantons?|parishes?)\s+(?:of|in|for)\s+(.+)/i,
    (m) =>
      _boundaryDeep(
        m[0].match(
          /(?:villages?|communes?|sub-?districts?|tehsils?|taluks?|blocks?|wards?|cells?|arrondissements?|townships?|municipalities?|local\s+government\s+areas?|lgas?|cantons?|parishes?)/i
        )[0],
        m[1].trim()
      ),
  ],
  [
    /(?:show\s+(?:me\s+)?(?:the\s+)?)?(.+?)(?:'s)?\s+(?:villages?|communes?|sub-?districts?|tehsils?|taluks?|blocks?|wards?|cells?|arrondissements?|townships?|municipalities?|local\s+government\s+areas?|lgas?|cantons?|parishes?)/i,
    (m) =>
      _boundaryDeep(
        m[0].match(
          /(?:villages?|communes?|sub-?districts?|tehsils?|taluks?|blocks?|wards?|cells?|arrondissements?|townships?|municipalities?|local\s+government\s+areas?|lgas?|cantons?|parishes?)/i
        )[0],
        m[1].trim()
      ),
  ],
  // Districts and counties (ADM2 typically)
  [
    /(?:show\s+(?:me\s+)?(?:the\s+)?)?(?:districts?|counties?|boroughs?|kreise?)\s+(?:of|in|for)\s+(.+)/i,
    (m) => _boundaryDeep(m[0].match(/(?:districts?|counties?|boroughs?|kreise?)/i)[0], m[1].trim()),
  ],
  [
    /(?:show\s+(?:me\s+)?(?:the\s+)?)?(.+?)(?:'s)?\s+(?:districts?|counties?|boroughs?|kreise?)/i,
    (m) => _boundaryDeep(m[0].match(/(?:districts?|counties?|boroughs?|kreise?)/i)[0], m[1].trim()),
  ],
  // States/provinces (ADM1) — includes common local terms (NOT counties — handled above as ADM2)
  [
    /(?:show\s+(?:me\s+)?(?:the\s+)?)?(states?|provinces?|regions?|departments?|wilayas?|prefectures?|oblasts?|governorates?|l[aä]nders?|bundesl[aä]nders?|emirate?s?|voivodeships?|fylkes?)\s+(?:of|in|for)\s+(.+)/i,
    (m) => _boundaryDeep(m[1].trim(), m[2].trim()),
  ],
  [
    /(?:show\s+(?:me\s+)?(?:the\s+)?)?(.+?)(?:'s)?\s+(states?|provinces?|regions?|departments?|wilayas?|prefectures?|oblasts?|governorates?|l[aä]nders?|bundesl[aä]nders?|emirate?s?|voivodeships?|fylkes?)/i,
    (m) => {
      const country = m[1].trim()
      const lower = country.toLowerCase()
      // "United States" ends in "States" — don't misread the trailing ADM1
      // keyword and treat "...the United" as a country name. (The legitimate
      // "states of the United States" form is handled by the pattern above.)
      if (/\bunited$/.test(lower)) return null
      // Spatial-analysis phrasings ("Which city is most central in X?",
      // "directional trend of X cities") must reach the Worker tools, not the
      // ADM1 boundary search. Keep in sync with SPATIAL_ANALYSIS_GUARD.
      if (
        /\bmost\s+central\b|\bmedian\s+cent|\bmean\s+cent|\bweighted\s+cent|\bdirectional\s+trend\b|\bspread\s+out\b|\bcluster\w*|\b(?:hot|cold)\s*spots?\b|\bnearest\s+neighbou?r\b/.test(
          lower
        )
      )
        return null
      return _boundaryDeep(m[2].trim(), country)
    },
  ],
  // Native-term fallback: any single Unicode noun ending in "of/in/for [country]".
  // Validated against synonym_dictionary.json — unrecognized nouns return null
  // and the chain falls through to the LLM. Catches Umudugudu, Daïra, Kanton,
  // Bakhsh, Kabupaten, etc. without a hardcoded vocabulary list.
  [
    /^(?:show\s+(?:me\s+)?(?:the\s+)?)?([\p{L}'-]+(?:\s+[\p{L}'-]+){0,2})\s+(?:of|in|for)\s+(?:the\s+)?(.+?)\s*\??$/iu,
    (m) => {
      const term = m[1].trim()
      const country = m[2].trim()
      const iso3 = _resolveCountry(country)
      if (!iso3 || !_isKnownAdminTerm(term, iso3)) return null
      return _boundaryDeep(term, country)
    },
  ],
  // Drill-down and drill-up commands
  [
    /(?:go|drill|zoom)\s+deeper/i,
    () => ({ type: 'division_action', action: 'drill_down', _fastQuery: true }),
  ],
  [
    /(?:show\s+(?:me\s+)?)?(?:the\s+)?(?:most\s+detailed|finest\s+detail|lowest\s+level|deepest\s+level)\s+(?:of|in|for)\s+(.+)/i,
    (m) => _boundaryMaxDepth(m[1].trim()),
  ],
  [
    /(?:go|drill|zoom)\s+(?:back\s+)?up/i,
    () => ({ type: 'division_action', action: 'drill_up', _fastQuery: true }),
  ],

  // ── Capitals only ──────────────────────────────────────────
  [
    /(?:show\s+(?:me\s+)?(?:the\s+)?)?(?:(?:state\s+)?capitals?|(?:capital\s+)?cities)\s+(?:of|in|for)\s+(.+)/i,
    (m) => _capitalsOnly(m[1].trim()),
  ],

  // "Show me the states and capitals" (no country — assume USA)
  [
    /(?:show\s+(?:me\s+)?(?:the\s+)?)?states?\s+(?:and|with|&)\s+(?:(?:state\s+)?capitals?)/i,
    () => _boundaryAndCapitals('United States'),
  ],

  // ── World political map ──────────────────────────────────
  // "Show the countries of the world" / "show all countries" / "world political
  // map" — a bare political map of every country (no data metric). Routes to the
  // boundaries op with the world aggregate ('WLD'), which fills each country by
  // Natural Earth MAPCOLOR7 so no two neighbours share a colour. Placed before
  // the country-choropleth patterns; each only matches when NO metric ("by
  // population", etc.) is present, so metric requests still fall through. (BUG-060)
  [
    /^(?:show\s+(?:me\s+)?)?(?:all\s+|every\s+|the\s+)?countr(?:y|ies)(?:\s+(?:of|in|on|around|across)\s+(?:the\s+)?(?:world|globe|earth))?\s*$/i,
    () => _showWorldQuery(),
  ],
  [
    /^(?:show\s+(?:me\s+)?)?(?:a\s+|the\s+)?(?:world\s+)?political\s+map(?:\s+of\s+(?:the\s+)?(?:world|globe|earth))?\s*$/i,
    () => _showWorldQuery(),
  ],
  [
    /^(?:colou?r)\s+(?:in\s+)?(?:the\s+)?(?:countr(?:y|ies)|world|globe|map)(?:\s+(?:of|in)\s+(?:the\s+)?(?:world|globe|earth))?\s*$/i,
    () => _showWorldQuery(),
  ],
  // CL-261: "Show me the countries of Africa" / "African countries" / "the
  // nations in South America" — the same political map cut to one continent
  // or named region. Anchored, so "countries of Africa by population" still
  // falls through to the choropleth patterns; an unknown region returns null
  // and falls through too.
  [
    /^(?:show\s+(?:me\s+)?)?(?:(?:all|every|the)\s+){0,2}(?:countr(?:y|ies)|nations?)\s+(?:of|in|on|across)\s+(?:the\s+)?([a-z][a-z .'-]*?)\s*$/i,
    (m) => _showRegionQuery(m[1]),
  ],
  [
    /^(?:show\s+(?:me\s+)?)?(?:(?:all|the)\s+){0,2}([a-z][a-z .'-]*?)\s+(?:countr(?:y|ies)|nations)\s*$/i,
    (m) => _showRegionQuery(m[1]),
  ],

  // ── Country choropleth queries ───────────────────────────
  // "Show me the population of each/every/all country on earth" / "population of each country"
  [
    /(?:show\s+(?:me\s+)?(?:the\s+)?)?(population|gdp|income(?:\s+group)?|density|area)\s+(?:of\s+)?(?:each|every|all)\s+(?:country|countries|nation)(?:\s+(?:on|in|around|across)\s+(?:the\s+)?(?:earth|world|globe))?/i,
    (m) => _choroplethQuery(null, m[1].trim()),
  ],
  // "Show population by country" / "Show GDP of African countries" / "Color countries by income"
  // "Show me the population by country in Africa"
  [
    /(?:show\s+(?:me\s+)?(?:the\s+)?)?(population|gdp|income(?:\s+group)?|density|area)\s+by\s+country(?:\s+(?:in|of|for)\s+(.+))?$/i,
    (m) => _choroplethQuery(m[2]?.trim() || null, m[1].trim()),
  ],
  // "Show me the countries of Africa by population"
  [
    /(?:show\s+(?:me\s+)?(?:the\s+)?)?countries?\s+(?:of|in)\s+(.+?)\s+by\s+(population|gdp|income(?:\s+group)?|density|area)/i,
    (m) => _choroplethQuery(m[1].trim(), m[2].trim()),
  ],
  [
    /^(?:show\s+(?:me\s+)?(?:the\s+)?)?(?:countries?\s+(?:of\s+the\s+)?(?:world\s+)?)?(?:by|colored?\s+by)\s+(population|gdp|income(?:\s+group)?|density|area|pop_est|gdp_md|income_grp|pop_density|area_km2)$/i,
    (m) => _choroplethQuery(null, m[1].trim()),
  ],
  [
    /(?:show\s+(?:me\s+)?(?:the\s+)?)?(population|gdp|income(?:\s+group)?|density|area)\s+(?:(?:by|of(?:\s+(?:all|the))?)\s+)?countries?(?:\s+(?:in|of)\s+(.+))?$/i,
    (m) => _choroplethQuery(m[2]?.trim() || null, m[1].trim()),
  ],
  [
    /(?:show\s+(?:me\s+)?(?:the\s+)?)?(population|gdp|income(?:\s+group)?|density|area)\s+(?:of|for|in)\s+(.+?)\s+(?:countries?|nations?)$/i,
    (m) => _choroplethQuery(m[2].trim(), m[1].trim()),
  ],
  [
    /(?:color|colour)\s+(?:the\s+)?(?:countries?|world|globe|map)\s+by\s+(population|gdp|income(?:\s+group)?|density|area)/i,
    (m) => _choroplethQuery(null, m[1].trim()),
  ],
  // CL-144: "Color Indian states by population" — a (demonym) country + admin
  // term is an admin choropleth, not a country choropleth.
  [
    /(?:color|colour)\s+(?:the\s+)?(.+?)\s+(states?|provinces?|regions?|counties?|districts?|prefectures?)\s+by\s+(population|gdp|income(?:\s+group)?|density|area|hdi)$/i,
    (m) =>
      _choroplethByTerm(m[2].trim(), m[1].trim(), /density/i.test(m[3]) ? 'density' : 'population'),
  ],
  // "Color the Middle East by GDP" / "Color Africa by income"
  [
    /(?:color|colour)\s+(?:the\s+)?(.+?)\s+by\s+(population|gdp|income(?:\s+group)?|density|area|hdi)/i,
    (m) => {
      // CL-144: "<X> states/regions by <metric>" is an admin request — if the
      // scope above didn't strictly resolve (e.g. "European regions"), defer
      // to the AI rather than rendering a country choropleth.
      if (/(?:states?|provinces?|regions?|counties?|districts?|prefectures?)$/i.test(m[1].trim()))
        return null
      return _choroplethQuery(m[1].trim(), m[2].trim())
    },
  ],
  // "Show [continent] countries by population"
  [
    /(?:show\s+(?:me\s+)?(?:the\s+)?)?(.+?)\s+countries?\s+by\s+(population|gdp|income(?:\s+group)?|density|area|hdi)/i,
    (m) => _choroplethQuery(m[1].trim(), m[2].trim()),
  ],

  // ── Terrain controls ─────────────────────────────────────
  [
    /(?:show|enable|turn\s+on|activate)\s+(?:3d\s+)?terrain/i,
    () => ({ type: 'terrain', action: 'on', _fastQuery: true }),
  ],
  [
    /(?:hide|disable|turn\s+off|remove|flatten)\s+(?:3d\s+)?terrain/i,
    () => ({ type: 'terrain', action: 'off', _fastQuery: true }),
  ],
  [
    /(?:increase|more|raise)\s+(?:terrain\s+)?exaggeration/i,
    () => ({ type: 'terrain', action: 'on', exaggeration: 3, _fastQuery: true }),
  ],
  [
    /(?:decrease|less|lower|reduce)\s+(?:terrain\s+)?exaggeration/i,
    () => ({ type: 'terrain', action: 'on', exaggeration: 1, _fastQuery: true }),
  ],
  [
    /(?:show|enable)\s+(?:3d\s+)?elevation/i,
    () => ({ type: 'terrain', action: 'on', _fastQuery: true }),
  ],
  [
    /flat(?:ten)?\s+(?:the\s+)?(?:terrain|map|globe|earth)/i,
    () => ({ type: 'terrain', action: 'off', _fastQuery: true }),
  ],

  // ── Overture Maps layer toggles ────────────────────────────
  [
    /(?:show|display|enable)\s+(?:me\s+)?(?:the\s+)?(?:overture\s+)?(?:3d\s+)?buildings?\s+(?:in|of|for|around)\s+(.+)/i,
    (m) => ({
      type: 'overture_layer',
      theme: 'buildings',
      action: 'show',
      target: { location: m[1].trim() },
      _fastQuery: true,
    }),
  ],
  [
    /(?:show|display|enable)\s+(?:me\s+)?(?:the\s+)?(?:overture\s+)?(?:3d\s+)?buildings?$/i,
    () => ({ type: 'overture_layer', theme: 'buildings', action: 'show', _fastQuery: true }),
  ],

  [
    /(?:show|display|enable)\s+(?:me\s+)?(?:the\s+)?(?:overture\s+)?land\s*use\s+(?:in|of|for)\s+(.+)/i,
    (m) => ({
      type: 'overture_layer',
      theme: 'land_use',
      action: 'show',
      target: { location: m[1].trim() },
      _fastQuery: true,
    }),
  ],
  [
    /(?:show|display|enable)\s+(?:me\s+)?(?:the\s+)?(?:overture\s+)?(?:land\s*use|zoning)$/i,
    () => ({ type: 'overture_layer', theme: 'land_use', action: 'show', _fastQuery: true }),
  ],

  [
    /(?:show|display|enable)\s+(?:me\s+)?(?:the\s+)?(?:overture\s+)?(?:water|rivers?\s+and\s+lakes?|bodies\s+of\s+water)\s+(?:in|of|for)\s+(.+)/i,
    (m) => ({
      type: 'overture_layer',
      theme: 'water',
      action: 'show',
      target: { location: m[1].trim() },
      _fastQuery: true,
    }),
  ],
  [
    /(?:show|display|enable)\s+(?:me\s+)?(?:the\s+)?(?:overture\s+)?(?:water|rivers?\s+and\s+lakes?|bodies\s+of\s+water)$/i,
    () => ({ type: 'overture_layer', theme: 'water', action: 'show', _fastQuery: true }),
  ],

  [
    /(?:show|display|enable)\s+(?:me\s+)?(?:the\s+)?(?:overture\s+)?(?:roads?|highways?|transportation|road\s+network|railways?)\s+(?:in|of|for)\s+(.+)/i,
    (m) => ({
      type: 'overture_layer',
      theme: 'transportation',
      action: 'show',
      target: { location: m[1].trim() },
      _fastQuery: true,
    }),
  ],
  [
    /(?:show|display|enable)\s+(?:me\s+)?(?:the\s+)?(?:overture\s+)?(?:roads?|highways?|transportation|road\s+network|railways?)$/i,
    () => ({ type: 'overture_layer', theme: 'transportation', action: 'show', _fastQuery: true }),
  ],

  [
    /(?:show|display|enable)\s+(?:me\s+)?(?:the\s+)?(?:overture\s+)?land\s+(?:in|of|for)\s+(.+)/i,
    (m) => ({
      type: 'overture_layer',
      theme: 'land',
      action: 'show',
      target: { location: m[1].trim() },
      _fastQuery: true,
    }),
  ],
  [
    /(?:show|display|enable)\s+(?:me\s+)?(?:the\s+)?(?:overture\s+)?(?:land|coastlines?|islands?)$/i,
    () => ({ type: 'overture_layer', theme: 'land', action: 'show', _fastQuery: true }),
  ],

  [
    /(?:show|display|enable)\s+(?:me\s+)?(?:the\s+)?(?:all\s+)?(?:overture\s+)?(?:places|points?\s+of\s+interest|pois?)\s+(?:in|of|for)\s+(.+)/i,
    (m) => ({
      type: 'overture_layer',
      theme: 'places',
      action: 'show',
      target: { location: m[1].trim() },
      _fastQuery: true,
    }),
  ],
  [
    /(?:show|display|enable)\s+(?:me\s+)?(?:the\s+)?(?:all\s+)?(?:overture\s+)?(?:all\s+places|all\s+points?\s+of\s+interest)$/i,
    () => ({ type: 'overture_layer', theme: 'places', action: 'show', _fastQuery: true }),
  ],

  [
    /(?:hide|remove|disable|turn\s+off)\s+(?:the\s+)?(?:overture\s+)?(?:3d\s+)?buildings?/i,
    () => ({ type: 'overture_layer', theme: 'buildings', action: 'hide', _fastQuery: true }),
  ],
  [
    /(?:hide|remove|disable|turn\s+off)\s+(?:the\s+)?(?:overture\s+)?land\s*use/i,
    () => ({ type: 'overture_layer', theme: 'land_use', action: 'hide', _fastQuery: true }),
  ],
  [
    /(?:hide|remove|disable|turn\s+off)\s+(?:the\s+)?(?:overture\s+)?(?:water|rivers?)/i,
    () => ({ type: 'overture_layer', theme: 'water', action: 'hide', _fastQuery: true }),
  ],
  [
    /(?:hide|remove|disable|turn\s+off)\s+(?:the\s+)?(?:overture\s+)?(?:roads?|transportation)/i,
    () => ({ type: 'overture_layer', theme: 'transportation', action: 'hide', _fastQuery: true }),
  ],
  [
    /(?:hide|remove|disable|turn\s+off)\s+(?:the\s+)?(?:overture\s+)?land$/i,
    () => ({ type: 'overture_layer', theme: 'land', action: 'hide', _fastQuery: true }),
  ],
  [
    /(?:hide|remove|disable|turn\s+off)\s+(?:the\s+)?(?:overture\s+)?(?:places|pois?)/i,
    () => ({ type: 'overture_layer', theme: 'places', action: 'hide', _fastQuery: true }),
  ],

  // ── Curated water bodies (oceans / intermittent / salt flats) ──
  // Distinct from overture_layer water — these are named GeoJSON layers
  // sourced from Esri WorldWaterBodies and uploaded to R2.
  [
    /^(?:show|display|highlight)\s+(?:me\s+)?(?:the\s+)?oceans?(?:\s+and\s+seas?)?$/i,
    () => ({ type: 'water_layer', sublayer: 'oceans', action: 'show', _fastQuery: true }),
  ],
  [
    /^(?:show|display|highlight)\s+(?:me\s+)?(?:the\s+)?seas?$/i,
    () => ({ type: 'water_layer', sublayer: 'oceans', action: 'show', _fastQuery: true }),
  ],
  [
    /^(?:show|highlight)\s+(?:me\s+)?(?:the\s+)?(pacific|atlantic|indian|arctic|southern|antarctic)\s+ocean$/i,
    (m) => ({
      type: 'water_layer',
      sublayer: 'oceans',
      action: 'show',
      name: `${m[1].charAt(0).toUpperCase()}${m[1].slice(1).toLowerCase()} Ocean`,
      _fastQuery: true,
    }),
  ],
  [
    /^(?:show|display)\s+(?:me\s+)?(?:the\s+)?(?:inland\s+)?intermittent\s+(?:water|lakes?)(?:\s+bodies)?$/i,
    () => ({ type: 'water_layer', sublayer: 'intermittent', action: 'show', _fastQuery: true }),
  ],
  [
    /^(?:show|display)\s+(?:me\s+)?(?:the\s+)?seasonal\s+(?:water|lakes?|wetlands?)$/i,
    () => ({ type: 'water_layer', sublayer: 'intermittent', action: 'show', _fastQuery: true }),
  ],
  [
    /^(?:show|display)\s+(?:me\s+)?(?:the\s+)?(?:dry\s+)?salt\s+flats?$/i,
    () => ({ type: 'water_layer', sublayer: 'salt_flats', action: 'show', _fastQuery: true }),
  ],
  [
    /^(?:show|display)\s+(?:me\s+)?(?:the\s+)?(?:salt\s+pans?|playas?)$/i,
    () => ({ type: 'water_layer', sublayer: 'salt_flats', action: 'show', _fastQuery: true }),
  ],
  [
    /^(?:hide|remove|turn\s+off)\s+(?:the\s+)?(oceans?|seas?|intermittent\s+(?:water|lakes?)|salt\s+flats?|salt\s+pans?|playas?)$/i,
    (m) => {
      const t = m[1].toLowerCase()
      const sub = /salt|playa|pan/.test(t)
        ? 'salt_flats'
        : /intermittent/.test(t)
          ? 'intermittent'
          : 'oceans'
      return { type: 'water_layer', sublayer: sub, action: 'hide', _fastQuery: true }
    },
  ],

  // ── Lakes & Rivers (PMTiles vector tiles) ─────────────────
  [
    /^(?:show|display|highlight)\s+(?:me\s+)?(?:the\s+)?(?:all\s+)?(?:lakes?(?:\s+and\s+rivers?)?|rivers?(?:\s+and\s+lakes?)?|inland\s+(?:perennial\s+)?water(?:\s+bodies)?)$/i,
    () => ({ type: 'water_layer', sublayer: 'lakes_rivers', action: 'show', _fastQuery: true }),
  ],
  [
    /^(?:show|highlight|find)\s+(?:me\s+)?(?:the\s+)?lake\s+(.+)$/i,
    (m) => {
      const titled = m[1]
        .trim()
        .split(/\s+/)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(' ')
      return {
        type: 'water_layer',
        sublayer: 'lakes_rivers',
        action: 'show',
        name: `Lake ${titled}`,
        _fastQuery: true,
      }
    },
  ],
  [
    /^(?:hide|remove|turn\s+off)\s+(?:the\s+)?(?:lakes?(?:\s+and\s+rivers?)?|rivers?(?:\s+and\s+lakes?)?|inland\s+(?:perennial\s+)?water)$/i,
    () => ({ type: 'water_layer', sublayer: 'lakes_rivers', action: 'hide', _fastQuery: true }),
  ],

  // ── Köppen Climate Zones ───────────────────────────────────
  // CL-144: also accepts "…regions", trailing "worldwide"/"on the map", and
  // the rainforest/savanna vernacular (normalized to the tropical class) —
  // these were previously stolen by biome/ADM1-boundary patterns.
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(?:k[öo]ppen\s+)?climate\s+(?:zones?|classification|types?|map)\s+(?:in|of|for|around)\s+(.+)/i,
    (m) => ({ type: 'climate_zones', location: m[1].trim(), _fastQuery: true }),
  ],
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(?:k[öo]ppen\s+)?climate\s+(?:zones?|classification|types?|map)(?:\s+(?:worldwide|globally|around\s+the\s+world|of\s+the\s+world|on\s+the\s+map))?$/i,
    () => ({ type: 'climate_zones', _fastQuery: true }),
  ],
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(tropical|arid|desert|temperate|mediterranean|oceanic|continental|polar|tundra|subarctic|subtropical|steppe|rainforest|savanna)\s+climate\s+(?:zones?|regions?)\b(?:\s+on\s+the\s+map)?\s*(?:(?:in|of|for|around)\s+(.+))?$/i,
    (m) => ({
      type: 'climate_zones',
      climate_class: _koppenClass(m[1]),
      location: m[2]?.trim() || null,
      _fastQuery: true,
    }),
  ],
  // Bare "<class> zones/regions" (no "climate") — 'polar' is deliberately
  // absent: "polar zones/regions" reads as the latitude bands / polar caps,
  // handled by the physical_feature patterns above.
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(tropical|arid|desert|temperate|mediterranean|oceanic|continental|tundra|subarctic|subtropical|steppe|rainforest|savanna)\s+(?:zones?|regions?)\b(?:\s+on\s+the\s+map)?\s*(?:(?:in|of|for|around)\s+(.+))?$/i,
    (m) => ({
      type: 'climate_zones',
      climate_class: _koppenClass(m[1]),
      location: m[2]?.trim() || null,
      _fastQuery: true,
    }),
  ],
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(tropical|arid|desert|temperate|mediterranean|oceanic|continental|polar|tundra|subarctic|subtropical|steppe|rainforest|savanna)\s+climates?$/i,
    (m) => ({ type: 'climate_zones', climate_class: _koppenClass(m[1]), _fastQuery: true }),
  ],
  [
    /(?:what|which)\s+(?:climate|k[öo]ppen)\s+(?:type|zone|classification)\s+(?:is|does|are)\s+(.+?)(?:\s+have|\s+in)?$/i,
    (m) => ({ type: 'climate_zones', location: m[1].trim(), _fastQuery: true }),
  ],
  [
    /k[öo]ppen\s+(?:map|classification|zones?)$/i,
    () => ({ type: 'climate_zones', _fastQuery: true }),
  ],

  // ── World Time Zones ───────────────────────────────────────
  // "what time is it in X when it's [noon|N o'clock|N am|N pm] in Y"
  [
    /(?:what(?:'s|\s+is)?\s+)?(?:the\s+)?time\s+(?:is\s+it\s+)?in\s+(.+?)\s+when\s+(?:it'?s\s+)?(noon|midnight|\d{1,2}(?::\d{2})?\s*(?:am|pm)?|\d{1,2}\s*o'?clock)\s+in\s+(.+?)\??$/i,
    (m) => {
      const targetCity = m[1].trim()
      const timeStr = m[2].trim().toLowerCase()
      const sourceCity = m[3].trim()
      // Normalise time string to a 0-23 source_hour
      let sourceHour = 12
      if (timeStr === 'noon') sourceHour = 12
      else if (timeStr === 'midnight') sourceHour = 0
      else {
        const match = timeStr.match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)?/)
        if (match) {
          let h = parseInt(match[1], 10)
          const ampm = match[3]
          if (ampm === 'pm' && h < 12) h += 12
          if (ampm === 'am' && h === 12) h = 0
          sourceHour = h
        }
      }
      return {
        type: 'timezones',
        compare_cities: [sourceCity, targetCity],
        source_hour: sourceHour,
        _fastQuery: true,
      }
    },
  ],
  // "what time zone is X (in)?" / "what's the time zone of X"
  [
    /(?:what(?:'s|\s+is)?|which)\s+(?:the\s+)?(?:time\s*zone|timezone|tz|utc\s+offset)\s+(?:is|does|of|for)\s+(.+?)(?:\s+in)?\??$/i,
    (m) => ({ type: 'timezones', place: m[1].trim(), _fastQuery: true }),
  ],
  // "what is the time zone in X" / "time zone of X"
  [
    /(?:what(?:'s|\s+is)?\s+)?(?:the\s+)?(?:time\s*zone|timezone)\s+(?:in|of|for)\s+(.+?)\??$/i,
    (m) => ({ type: 'timezones', place: m[1].trim(), _fastQuery: true }),
  ],
  // "show UTC-5" / "show GMT+9" / "show timezone +5:30"
  [
    /(?:show|display|highlight)\s+(?:me\s+)?(?:the\s+)?(?:time\s*zones?\s+(?:at\s+)?)?(?:utc|gmt)\s*([+-]?\d{1,2}(?:[.:]\d{1,2})?)/i,
    (m) => {
      const raw = m[1]
      let offset
      if (raw.includes(':')) {
        const [h, mm] = raw.split(':')
        offset = parseInt(h, 10) + (parseInt(mm, 10) / 60) * (h.startsWith('-') ? -1 : 1)
      } else {
        offset = parseFloat(raw)
      }
      return { type: 'timezones', offset, _fastQuery: true }
    },
  ],
  // "show DST zones" / "show daylight saving time"
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(?:dst|daylight\s+saving(?:\s+time)?)\s*(?:zones?|map)?$/i,
    () => ({ type: 'timezones', dst: true, _fastQuery: true }),
  ],
  // "show standard time" — explicit non-DST
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?standard\s+time(?:\s+zones?)?$/i,
    () => ({ type: 'timezones', dst: false, _fastQuery: true }),
  ],
  // "show time zones" / "show world clock" / "show world time" / "show timezones"
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(?:world\s+)?(?:time\s*zones?|timezones?|world\s+clock|world\s+time)(?:\s+map)?$/i,
    () => ({ type: 'timezones', _fastQuery: true }),
  ],
  // "show time zones in/of/for X"
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(?:time\s*zones?|timezones?)\s+(?:in|of|for|across|around)\s+(.+?)\??$/i,
    (m) => ({ type: 'timezones', place: m[1].trim(), _fastQuery: true }),
  ],

  // ── Freedom House Index ────────────────────────────────────
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(?:freedom\s+house\s+)?(?:political\s+)?(?:freedom|liberty|liberties)\s+(?:index|rating|ratings|score|scores|map|status)?\s*(?:in|of|for|across|around)\s+(.+)/i,
    (m) => ({ type: 'freedom_house', location: m[1].trim(), _fastQuery: true }),
  ],
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(?:freedom\s+house|political\s+freedom|freedom\s+index|civil\s+libert(?:y|ies)|freedom\s+rating|freedom\s+score|freedom\s+map)(?:\s+(?:across|around)\s+the\s+world)?$/i,
    () => ({ type: 'freedom_house', _fastQuery: true }),
  ],
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(?:freedom\s+house\s+)?(?:political\s+freedom|freedom)\s+(?:across|around)\s+(?:the\s+)?(?:world|globe|earth)/i,
    () => ({ type: 'freedom_house', _fastQuery: true }),
  ],
  [
    /(?:which|what)\s+countries\s+(?:are|is)\s+(?:rated\s+)?['"]?(free|not\s+free|partly\s+free|unfree|authoritarian)['"]?/i,
    (m) => ({ type: 'freedom_house', status_filter: m[1].trim(), _fastQuery: true }),
  ],
  [
    /(?:show|display)\s+(?:me\s+)?(?:the\s+)?(?:countries\s+(?:that\s+are|rated)\s+)?['"]?(free|not\s+free|partly\s+free)['"]?\s+countries/i,
    (m) => ({ type: 'freedom_house', status_filter: m[1].trim(), _fastQuery: true }),
  ],
  [
    /freedom\s+house\s+(?:index|data|map|rating|ratings|report)$/i,
    () => ({ type: 'freedom_house', _fastQuery: true }),
  ],

  // ── Choropleth style switching ──────────────────────────────
  // CL-144: anchored to anaphoric objects (it / this / the map…) or explicit
  // style nouns. The old "show.*3d" / "show.*hex" wildcards stole "Show 3D
  // Mount Everest" (terrain) and "Show hexagon density of POIs in Europe"
  // (h3_density).
  [
    /\b(?:make\s+(?:it|this|that|them|the\s+(?:map|data|choropleth))\s+(?:3d|three.?d|3-d)|extrude(?:\s+it)?|show\s+(?:it|this|that|the\s+(?:map|data|choropleth))\s+(?:in|as)\s+(?:3d|three.?d|3-d)|(?:as\s+)?3d\s+(?:bars?|columns?)|3d\s+choropleth|show\s+(?:the\s+)?(?:data\s+)?(?:as\s+)?bars?\b)\b/i,
    () => ({ type: 'choropleth_style', style: 'extruded', _fastQuery: true }),
  ],
  [
    /\b(?:show.*(?:bubble|bubbles|proportional|symbol)|bubble.*map|proportional.*symbol)\b/i,
    () => ({ type: 'choropleth_style', style: 'symbols', _fastQuery: true }),
  ],
  [
    /\b(?:make\s+(?:it|this|that|the\s+(?:map|data|choropleth))\s+hex\w*|switch\s+to\s+hex\w*|show\s+(?:it|this|that|the\s+(?:map|data|choropleth))\s+as\s+hex\w*|hex(?:agon)?\s+(?:map|view|style|mode)|hexbin(?:\s+(?:map|view|style|mode))?|as\s+hexagons?$)\b/i,
    () => ({ type: 'choropleth_style', style: 'hexbin', _fastQuery: true }),
  ],
  [
    /\b(?:show.*flat|flat.*view|normal.*view|standard.*view|reset.*view|2d.*view)\b/i,
    () => ({ type: 'choropleth_style', style: 'flat', _fastQuery: true }),
  ],

  // ── Choropleth scale mode switching ──────────────────────────
  [
    /\b(?:global\s+scale|absolute\s+scale|world\s+scale|compare\s+globally)\b/i,
    () => ({ type: 'choropleth_scale', mode: 'absolute', _fastQuery: true }),
  ],
  [
    /\b(?:local\s+scale|relative\s+scale|compare\s+within|within\s+(?:this\s+)?(?:level|country|region))\b/i,
    () => ({ type: 'choropleth_scale', mode: 'relative', _fastQuery: true }),
  ],

  // ── Routing queries (directions, isochrones, matrix) ─────────
  // Negative signals — fall through to Haiku for complex/ambiguous queries
  // These are checked first; if matched, routing patterns are skipped.

  // Group 1: Point-to-point directions
  [
    /how (?:far|long|many km|many miles)(?: is it| to get)? from (.+?) to (.+)/i,
    (m) =>
      _routingNegativeCheck(m.input) ? null : _directionsQuery(m[1].trim(), m[2].trim(), m.input),
  ],
  [
    /(?:route|directions|navigate|get me) from (.+?) to (.+)/i,
    (m) =>
      _routingNegativeCheck(m.input) ? null : _directionsQuery(m[1].trim(), m[2].trim(), m.input),
  ],
  [
    /(?:show|draw|map)(?: me)?(?: the)? (?:route|road|path|way) (?:from|between) (.+?) (?:to|and) (.+)/i,
    (m) =>
      _routingNegativeCheck(m.input) ? null : _directionsQuery(m[1].trim(), m[2].trim(), m.input),
  ],
  [
    /(?:distance|travel time|driving time) (?:from|between) (.+?) (?:to|and) (.+)/i,
    (m) =>
      _routingNegativeCheck(m.input) ? null : _directionsQuery(m[1].trim(), m[2].trim(), m.input),
  ],
  [
    /how (?:do I|do you|would I) (?:get|travel|drive|walk) from (.+?) to (.+)/i,
    (m) =>
      _routingNegativeCheck(m.input) ? null : _directionsQuery(m[1].trim(), m[2].trim(), m.input),
  ],
  [
    /(?:cycling|hiking|walking|wheelchair) (?:route|directions?) from (.+?) to (.+)/i,
    (m) =>
      _routingNegativeCheck(m.input) ? null : _directionsQuery(m[1].trim(), m[2].trim(), m.input),
  ],

  // Group 3: Multi-ring isochrones (before Group 2 — more specific)
  [
    /((?:\d+,?\s*)+)(?:and\s)?(\d+)\s*(minute|hour|min|hr)s?\s+(?:rings?|zones?|bands?|contours?)\s+(?:from|around)\s+(.+)/i,
    (m) => (_routingNegativeCheck(m.input) ? null : _multiRingIsochroneQuery(m)),
  ],
  [
    /every (\d+) (minute|hour|min|hr)s? up to (\d+) (minute|hour|min|hr)s? (?:from|around) (.+)/i,
    (m) => (_routingNegativeCheck(m.input) ? null : _everyNIsochroneQuery(m)),
  ],

  // Group 2: Single isochrones
  [
    /(?:show|draw|map) (?:everywhere|how far|areas?) (?:reachable|accessible|you can reach|I can reach) (?:in|within) (\d+) (minute|hour|min|hr)s? (?:of|from) (.+)/i,
    (m) =>
      _routingNegativeCheck(m.input) ? null : _isochroneQuery(m[1], m[2], m[3].trim(), m.input),
  ],
  [
    /(\d+)[- ](?:minute|hour|min|hr) (?:drive|walk|cycle|isochrone) (?:from|around|of) (.+)/i,
    (m) =>
      _routingNegativeCheck(m.input)
        ? null
        : _isochroneQuery(
            m[1],
            m.input.match(/minute|hour|min|hr/i)?.[0] || 'minute',
            m[2].trim(),
            m.input
          ),
  ],
  [
    /(?:isochrone|catchment area|service area|reachability) (?:for|around|of|from) (.+) (?:in|within) (\d+) (minute|hour|min|hr)s?/i,
    (m) =>
      _routingNegativeCheck(m.input) ? null : _isochroneQuery(m[2], m[3], m[1].trim(), m.input),
  ],
  [
    /what (?:places|areas|regions) can (?:I|you|we|someone) reach (?:from|within) (.+) in (\d+) (minute|hour|min|hr)s?/i,
    (m) =>
      _routingNegativeCheck(m.input) ? null : _isochroneQuery(m[2], m[3], m[1].trim(), m.input),
  ],
  [
    /how (?:far|much area) can (?:you|I|someone) (?:travel|cover|reach) (?:from|in) (.+) in (\d+) (minute|hour|min|hr)s?/i,
    (m) =>
      _routingNegativeCheck(m.input) ? null : _isochroneQuery(m[2], m[3], m[1].trim(), m.input),
  ],
  [
    /(\d+)[- ](?:minute|hour|min|hr) (?:drive|walk|cycle) (?:zone|area|radius) (?:around|from|of) (.+)/i,
    (m) =>
      _routingNegativeCheck(m.input)
        ? null
        : _isochroneQuery(
            m[1],
            m.input.match(/minute|hour|min|hr/i)?.[0] || 'minute',
            m[2].trim(),
            m.input
          ),
  ],

  // Group 4: Matrix queries
  [
    /(?:travel time|driving time|distance) (?:matrix|table|grid) (?:between|from) (.+?) (?:and|to) (.+)/i,
    (m) =>
      _routingNegativeCheck(m.input) ? null : _matrixQuery(m[1].trim(), m[2].trim(), m.input),
  ],
  [
    /(?:compare|show) (?:travel times?|distances?) (?:from|between) (.+?) (?:to|and) (.+)/i,
    (m) =>
      _routingNegativeCheck(m.input) ? null : _matrixQuery(m[1].trim(), m[2].trim(), m.input),
  ],

  // ── Known bare division names (last resort, BUG-016) ──────────
  // "Show me Punjab" / "Punjab" — no state/province suffix needed
  // Returns null for unknown names so the AI fallback handles it
  [/^(?:show\s+(?:me\s+)?(?:the\s+)?)?(.+)$/i, (m) => _singleDivisionQueryInfer(m[1].trim())],
]

// ── Moon fast-query shortcuts ──────────────────────────────────────
const MOON_FAST_QUERIES = [
  // "Show me all landing sites" / "Show landing sites"
  [
    /(?:show\s+(?:me\s+)?(?:all\s+)?(?:the\s+)?)?(?:lunar\s+)?landing\s+sites?$/i,
    () => ({
      type: 'lunar_landing',
      target: { type: 'landing_site' },
      operation: { action: 'showAll' },
      voiceResponse: {
        shouldSpeak: true,
        template:
          'Here are all {count} lunar landing sites from 6 countries spanning 1959 to 2024.',
      },
      _fastQuery: true,
    }),
  ],

  // "Show me the Apollo landing sites" / "Where did Apollo land?"
  [
    /(?:show\s+(?:me\s+)?(?:all\s+)?(?:the\s+)?)?apollo\s+(?:landing\s+)?sites?/i,
    () => ({
      type: 'lunar_filter',
      target: { type: 'landing_site' },
      operation: {
        action: 'filter',
        filter: { attribute: 'mission', operator: 'contains', value: 'Apollo' },
      },
      voiceResponse: {
        shouldSpeak: true,
        template:
          'Here are all 6 Apollo landing sites, from Apollo 11 in 1969 to Apollo 17 in 1972.',
      },
      _fastQuery: true,
    }),
  ],

  // "Where did [country] land in [year]?" — MUST be before country-only pattern
  [
    /(?:where\s+did\s+(?:the\s+)?|show\s+(?:me\s+)?(?:the\s+)?)(americans?|united\s+states|usa?|soviets?|soviet\s+union|russia|china|chinese|india|indian|japan|japanese)\s+land(?:ing\s+sites?)?\s+(?:on\s+the\s+moon\s+)?in\s+(\d{4})/i,
    (m) => _moonCountryYearFilter(m[1], m[2]),
  ],

  // "Where did [country] land?" / "Show me [country] landing sites"
  [
    /(?:where\s+did\s+(?:the\s+)?|show\s+(?:me\s+)?(?:the\s+)?)(americans?|united\s+states|usa?|soviets?|soviet\s+union|russia|china|chinese|india|indian|japan|japanese)\s+(?:land(?:ing\s+sites?)?|missions?)\s*\??$/i,
    (m) => _moonCountryFilter(m[1]),
  ],

  // "Show me the craters" / "Show me the maria" / "Show me the mountains"
  [
    /(?:show\s+(?:me\s+)?(?:all\s+)?(?:the\s+)?)?(?:lunar\s+)?(?:major\s+)?(craters?|maria|seas?|mountains?|mountain\s+ranges?)$/i,
    (m) => _moonFeatureTypeFilter(m[1]),
  ],

  // "Show me rover missions" / "Show me sample return missions"
  [
    /(?:show\s+(?:me\s+)?(?:all\s+)?(?:the\s+)?)?(?:lunar\s+)?(rover|sample\s+return|crewed|robotic|impact)\s+missions?/i,
    (m) => _moonCategoryFilter(m[1]),
  ],

  // "Go to [feature]" / "Fly to [feature]" / "Show me [feature]"
  [/(?:go|fly|navigate)\s+to\s+(?:the\s+)?(.+)/i, (m) => _moonNavigation(m[1].trim())],
  [/(?:show\s+me|take\s+me\s+to)\s+(?:the\s+)?(.+)/i, (m) => _moonNavigation(m[1].trim())],

  // "Which countries have landed on the Moon?"
  [
    /which\s+countries?\s+(?:have\s+)?land(?:ed)?\s+on\s+(?:the\s+)?moon/i,
    () => ({
      type: 'lunar_landing',
      target: { type: 'landing_site' },
      operation: { action: 'showAll' },
      voiceResponse: {
        shouldSpeak: true,
        template:
          'Six countries have successfully landed on the Moon: the United States, Soviet Union, China, India, Japan, and the first commercial lander by Intuitive Machines.',
      },
      _fastQuery: true,
    }),
  ],
]

function _moonResolveCountry(text) {
  const lower = text.toLowerCase().trim()
  const map = {
    american: 'United States',
    americans: 'United States',
    'united states': 'United States',
    usa: 'United States',
    us: 'United States',
    soviet: 'Soviet Union',
    soviets: 'Soviet Union',
    'soviet union': 'Soviet Union',
    russia: 'Soviet Union',
    china: 'China',
    chinese: 'China',
    india: 'India',
    indian: 'India',
    japan: 'Japan',
    japanese: 'Japan',
  }
  return map[lower] || text
}

function _moonCountryFilter(countryText) {
  const country = _moonResolveCountry(countryText)
  return {
    type: 'lunar_filter',
    target: { type: 'landing_site' },
    operation: {
      action: 'filter',
      filter: { attribute: 'country', operator: '==', value: country },
    },
    voiceResponse: { shouldSpeak: true, template: `Here are the ${country} lunar landing sites.` },
    _fastQuery: true,
  }
}

function _moonCountryYearFilter(countryText, year) {
  const country = _moonResolveCountry(countryText)
  return {
    type: 'lunar_filter',
    target: { type: 'landing_site' },
    operation: {
      action: 'filter',
      filter: { attribute: 'country_year', operator: '==', value: `${country}_${year}` },
    },
    voiceResponse: {
      shouldSpeak: true,
      template: `Here are the ${country} lunar missions from ${year}.`,
    },
    _fastQuery: true,
  }
}

function _moonFeatureTypeFilter(typeText) {
  const lower = typeText.toLowerCase()
  let type, label
  if (lower.includes('crater')) {
    type = 'crater'
    label = 'craters'
  } else if (lower.includes('maria') || lower.includes('sea')) {
    type = 'mare'
    label = 'maria (lunar seas)'
  } else if (lower.includes('mountain')) {
    type = 'mountain'
    label = 'mountains'
  } else {
    type = lower
    label = lower
  }

  return {
    type: 'lunar_filter',
    target: { type },
    operation: { action: 'showAll' },
    voiceResponse: { shouldSpeak: true, template: `Here are the major ${label} of the Moon.` },
    _fastQuery: true,
  }
}

function _moonCategoryFilter(categoryText) {
  const category = categoryText.toLowerCase().replace(/\s+/g, '_').trim()
  return {
    type: 'lunar_filter',
    target: { type: 'landing_site' },
    operation: {
      action: 'filter',
      filter: { attribute: 'category', operator: '==', value: category },
    },
    voiceResponse: {
      shouldSpeak: true,
      template: `Here are the ${categoryText} missions on the Moon.`,
    },
    _fastQuery: true,
  }
}

function _moonNavigation(name) {
  const loc = findLunarLocation(name)
  if (loc) {
    return {
      type: 'lunar_navigation',
      target: {
        name: loc.feature?.properties?.name || name,
        coordinates: loc.center,
        type: loc.feature?.properties?.type || loc.feature?.properties?.category || 'region',
      },
      operation: { action: 'flyTo' },
      voiceResponse: {
        shouldSpeak: true,
        template: loc.feature?.properties?.description
          ? `Here is ${loc.feature.properties.name}. ${loc.feature.properties.description}`
          : `Flying to ${name}.`,
      },
      _fastQuery: true,
    }
  }
  // Couldn't match — let AI handle it
  return null
}

// Street-type keywords and abbreviations used to recognize street addresses
// for the HERE geocoding fast path (CL-046).
const _ADDRESS_HINT_RE =
  /\b(?:street|st\.?|avenue|ave\.?|road|rd\.?|boulevard|blvd\.?|drive|dr\.?|lane|ln\.?|highway|hwy\.?|route|rte\.?|way|place|plaza|square|sq\.?|court|ct\.?|parkway|pkwy\.?|terrace|ter\.?|circle|cir\.?|alley|broadway)\b/i

function _looksLikeAddress(str) {
  if (!str) return false
  const s = str.trim()
  // Leading house number + word token ("221B Baker Street" too)
  if (/^\d+[A-Za-z]?\s+\S/.test(s)) return true
  // Contains a street-type keyword
  if (_ADDRESS_HINT_RE.test(s)) return true
  return false
}

// HERE Routing transportMode values supported by /api/route (CL-054)
const ROUTE_TRANSPORT_MODES = new Set(['car', 'truck', 'pedestrian', 'bicycle', 'publicTransport'])

function _routeQuery(origin, destination, transportMode) {
  if (!origin || !destination) return null
  const mode = transportMode && ROUTE_TRANSPORT_MODES.has(transportMode) ? transportMode : 'car'
  return {
    type: 'route',
    origin,
    destination,
    transportMode: mode,
    _fastQuery: true,
  }
}

function _geocodeQuery(query) {
  if (!query) return null
  return { type: 'geocode', query: query.trim(), _fastQuery: true }
}

function _geocodeQueryIfAddress(query) {
  if (!_looksLikeAddress(query)) return null
  return _geocodeQuery(query)
}

function _navigationQuery(locationText) {
  const iso3 = _resolveCountry(locationText)
  return {
    type: 'navigation',
    target: { dataset: 'countries', location: locationText, country: iso3 || undefined },
    operation: { action: 'flyTo' },
    visualization: { type: 'outline' },
    voiceResponse: { shouldSpeak: false },
    _fastQuery: true,
  }
}

function _showCountryQuery(locationText) {
  const iso3 = _resolveCountry(locationText)
  return {
    type: 'division_action',
    action: 'show_country',
    country: iso3,
    speech: `Here is ${locationText}.`,
    _fastQuery: true,
  }
}

// "Show the slope/aspect/hillshade of <place>" → terrain_analysis op, which
// computes the metric from an ingested DEM COG for that location.
//
// Sub-national hints (CL-227): the location is additionally parsed for
//   - a trailing ", <parent>" ("Orange County, CA" / "São Paulo, Brazil") →
//     parent_name, so the op can resolve the single division inside it;
//   - a trailing ADM1 term ("São Paulo State", "Sichuan Province") → the term
//     is stripped and admin_level:"ADM1" is set;
//   - a trailing ADM2 term ("Orange County") → the term STAYS in the name
//     (geoBoundaries stores "Orange County") and admin_level:"ADM2" is set.
// Bare division names with no hints still resolve via the op's context-country
// fallback or the AI path (which emits country/admin_level).
function _terrainAnalysisQuery(rawMetric, rawLocation) {
  const m = (rawMetric || '').toLowerCase()
  let metric = 'slope'
  // Order matters: check "shaded relief" (hillshade) before the bare "relief"
  // and "topography" synonyms that mean the elevation map.
  if (/aspect/.test(m)) metric = 'aspect'
  else if (/hillshade|shaded\s+relief/.test(m)) metric = 'hillshade'
  else if (/elevation|dem|digital\s+elevation|height|altitude|topography|relief/.test(m))
    metric = 'elevation'
  // steepness / slope / slope angle → slope (the default)
  // Strip a leading article so "the Alps" matches the "Alps …" DEM layer name.
  let location = rawLocation ? rawLocation.trim().replace(/^the\s+/i, '') : null
  let parentName = null
  let adminLevel = null
  let country = null
  if (location) {
    const commaM = location.match(/^(.+?),\s*(.+)$/)
    if (commaM) {
      location = commaM[1].trim()
      parentName = commaM[2].trim().replace(/^the\s+/i, '')
    }
    const adm1M = location.match(
      /^(.+?)\s+(?:state|province|prefecture|oblast|canton|governorate|department)$/i
    )
    if (adm1M) {
      location = adm1M[1].trim()
      adminLevel = 'ADM1'
    } else if (/\b(?:county|parish|borough|municipality)$/i.test(location)) {
      adminLevel = 'ADM2'
    }

    // Resolve the COUNTRY the division belongs to, synchronously:
    //  - the location itself is a US state ("Washington State", "California")
    //  - the parent is a US state name/postal code ("Orange County, CA")
    //  - the parent is a country name ("São Paulo, Brazil")
    const isUsState = (s) => {
      const k = String(s || '')
        .toLowerCase()
        .replace(/^the\s+/, '')
      return !!(US_STATES[k] || Object.values(US_STATES).some((n) => n.toLowerCase() === k))
    }
    if (isUsState(location)) country = 'USA'
    else if (parentName && isUsState(parentName)) country = 'USA'
    else if (parentName) country = _resolveCountry(parentName, { strict: true }) || null

    // A sub-national hint we CANNOT anchor to a country locally ("São Paulo
    // State", "Sichuan Province") must go to the AI path — the system prompt
    // instructs Haiku to emit {location, admin_level, country}, which the op
    // needs to fetch the division's boundary. Returning null lets tryFastQuery
    // fall through to the API instead of degrading to a geocoded rectangle.
    if ((adminLevel || parentName) && !country) return null
  }
  const verb = metric === 'elevation' ? 'Mapping' : 'Computing'
  return {
    type: 'terrain_analysis',
    metric,
    location,
    ...(country ? { country } : {}),
    ...(parentName ? { parent_name: parentName } : {}),
    ...(adminLevel ? { admin_level: adminLevel } : {}),
    speech: `${verb} ${metric}${location ? ` for ${location}` : ''}.`,
    _fastQuery: true,
  }
}

// "Show the countries of the world" and friends → world political map. Emits the
// world aggregate ('WLD'); the boundaries op renders every country filled by
// MAPCOLOR7 so neighbours differ. (BUG-060)
function _showWorldQuery() {
  return {
    type: 'division_action',
    action: 'show_country',
    country: 'WLD',
    speech:
      'Here are all the countries of the world. Neighbouring countries use different colours so they are easy to tell apart.',
    _fastQuery: true,
  }
}

// CL-261: continent / region political map. Returns null for anything that is
// not a known region so the pattern falls through.
function _showRegionQuery(text) {
  const region = resolveRegionToken(text)
  if (!region) return null
  return {
    type: 'division_action',
    action: 'show_country',
    country: region,
    speech: `Here are the countries of ${region}.`,
    _fastQuery: true,
  }
}

function _singleDivisionQuery(divisionName, countryName) {
  const iso3 = _resolveCountry(countryName)
  return {
    type: 'division_action',
    action: 'show_single_division',
    name: divisionName,
    country: iso3,
    speech: `Here is ${divisionName} in ${countryName}.`,
    _fastQuery: true,
  }
}

function _singleDivisionQueryInfer(divisionName) {
  // Try to infer country from well-known states/provinces
  const knownDivisions = {
    'são paulo': 'BRA',
    'sao paulo': 'BRA',
    'rio de janeiro': 'BRA',
    'minas gerais': 'BRA',
    california: 'USA',
    texas: 'USA',
    florida: 'USA',
    'new york': 'USA',
    punjab: 'IND',
    rajasthan: 'IND',
    maharashtra: 'IND',
    'tamil nadu': 'IND',
    kerala: 'IND',
    karnataka: 'IND',
    gujarat: 'IND',
    'uttar pradesh': 'IND',
    bayern: 'DEU',
    bavaria: 'DEU',
    sachsen: 'DEU',
    hessen: 'DEU',
    queensland: 'AUS',
    victoria: 'AUS',
    tasmania: 'AUS',
    ontario: 'CAN',
    quebec: 'CAN',
    'british columbia': 'CAN',
    lagos: 'NGA',
    kano: 'NGA',
    oyo: 'NGA',
  }
  const lower = divisionName.toLowerCase()
  const iso3 = knownDivisions[lower] || null
  if (!iso3) return null // Let AI handle it
  return {
    type: 'division_action',
    action: 'show_single_division',
    name: divisionName,
    country: iso3,
    speech: `Here is ${divisionName}.`,
    _fastQuery: true,
  }
}

// ── Routing query helpers ──────────────────────────────────────────

// Negative signals — queries that mention these should fall through to Haiku
const ROUTING_NEGATIVE_SIGNALS = [
  /\b(why|because|explain|historically|used to|in the past)\b/i,
  /\b(best|safest|fastest|shortest)\b(?! \d)/i,
  /\b(compare|versus|vs\.?|alternative)\b/i,
  /\b(trade route|silk road|ancient|medieval|historical)\b/i,
]

function _routingNegativeCheck(query) {
  return ROUTING_NEGATIVE_SIGNALS.some((p) => p.test(query))
}

// Profile detection — scans for transport mode keywords
const ROUTING_PROFILE_MAP = [
  { pattern: /\b(on foot|walking|walk|pedestrian)\b/i, profile: 'foot-walking' },
  { pattern: /\b(cycling|by bike|biking|bicycle|cycle)\b/i, profile: 'cycling-regular' },
  { pattern: /\b(hiking|trek|trekking|hike|trail)\b/i, profile: 'foot-hiking' },
  { pattern: /\b(truck|freight|heavy vehicle|HGV|lorry)\b/i, profile: 'driving-hgv' },
  { pattern: /\b(wheelchair|accessible|disability)\b/i, profile: 'wheelchair' },
  { pattern: /\b(by car|driving|drive|by road|road)\b/i, profile: 'driving-car' },
]

function _detectRoutingProfile(query) {
  for (const { pattern, profile } of ROUTING_PROFILE_MAP) {
    if (pattern.test(query)) return profile
  }
  return 'driving-car'
}

function _parseRangeToSeconds(value, unit) {
  const n = parseInt(value, 10)
  return /hour|hr/i.test(unit) ? n * 3600 : n * 60
}

function _directionsQuery(origin, dest, fullQuery) {
  // Strip trailing transport modifiers from destination
  dest = dest
    .replace(
      /\s+(?:by\s+(?:car|road|foot|bike|truck)|walking|cycling|driving|on\s+foot)\s*\??$/i,
      ''
    )
    .trim()
  return {
    type: 'routing',
    routing_type: 'directions',
    routing_profile: _detectRoutingProfile(fullQuery),
    routing_locations: [origin, dest],
    routing_coordinates: null,
    routing_range_seconds: null,
    voiceResponse: { shouldSpeak: false },
    _fastQuery: true,
  }
}

function _isochroneQuery(timeVal, timeUnit, location, fullQuery) {
  const seconds = _parseRangeToSeconds(timeVal, timeUnit)
  return {
    type: 'routing',
    routing_type: 'isochrone',
    routing_profile: _detectRoutingProfile(fullQuery),
    routing_locations: [location],
    routing_coordinates: null,
    routing_range_seconds: [seconds],
    voiceResponse: { shouldSpeak: false },
    _fastQuery: true,
  }
}

function _multiRingIsochroneQuery(m) {
  // Extract all numbers except the last group (which is location)
  const rawNums = m[0].match(/\d+/g)
  const unit = m[3]
  const location = m[4].trim()
  // All numbers except those that might be in the location string
  const ranges = rawNums
    .map((n) => _parseRangeToSeconds(n, unit))
    .filter((s) => s > 0 && s <= 14400) // cap at 4 hours
    .sort((a, b) => a - b)
  return {
    type: 'routing',
    routing_type: 'isochrone',
    routing_profile: _detectRoutingProfile(m.input),
    routing_locations: [location],
    routing_coordinates: null,
    routing_range_seconds: ranges,
    voiceResponse: { shouldSpeak: false },
    _fastQuery: true,
  }
}

function _everyNIsochroneQuery(m) {
  const step = _parseRangeToSeconds(m[1], m[2])
  const max = _parseRangeToSeconds(m[3], m[4])
  const location = m[5].trim()
  const ranges = []
  for (let s = step; s <= max; s += step) {
    ranges.push(s)
  }
  return {
    type: 'routing',
    routing_type: 'isochrone',
    routing_profile: _detectRoutingProfile(m.input),
    routing_locations: [location],
    routing_coordinates: null,
    routing_range_seconds: ranges,
    voiceResponse: { shouldSpeak: false },
    _fastQuery: true,
  }
}

function _matrixQuery(origins, destinations, fullQuery) {
  // Split comma/and-separated location lists
  const parseLocList = (s) =>
    s
      .split(/,\s*|\s+and\s+/i)
      .map((l) => l.trim())
      .filter(Boolean)
  const allLocs = [...parseLocList(origins), ...parseLocList(destinations)]
  return {
    type: 'routing',
    routing_type: 'matrix',
    routing_profile: _detectRoutingProfile(fullQuery),
    routing_locations: allLocs,
    routing_coordinates: null,
    routing_range_seconds: null,
    voiceResponse: { shouldSpeak: false },
    _fastQuery: true,
  }
}

// ISO-3 → human-readable country name (reverse of _resolveCountry aliases)
const ISO3_NAMES = {
  USA: 'the United States',
  GBR: 'the United Kingdom',
  IND: 'India',
  CHN: 'China',
  JPN: 'Japan',
  BRA: 'Brazil',
  FRA: 'France',
  DEU: 'Germany',
  MEX: 'Mexico',
  CAN: 'Canada',
  AUS: 'Australia',
  ITA: 'Italy',
  ESP: 'Spain',
  RUS: 'Russia',
  KOR: 'South Korea',
  IDN: 'Indonesia',
  NGA: 'Nigeria',
  ZAF: 'South Africa',
  TUR: 'Turkey',
  EGY: 'Egypt',
  PAK: 'Pakistan',
  ARG: 'Argentina',
  COL: 'Colombia',
  DZA: 'Algeria',
  KEN: 'Kenya',
  ETH: 'Ethiopia',
  SAU: 'Saudi Arabia',
  THA: 'Thailand',
  VNM: 'Vietnam',
  MYS: 'Malaysia',
  PHL: 'Philippines',
  PER: 'Peru',
  CHL: 'Chile',
  NLD: 'the Netherlands',
  PRT: 'Portugal',
  GRC: 'Greece',
  SWE: 'Sweden',
  NOR: 'Norway',
  POL: 'Poland',
  CHE: 'Switzerland',
  AUT: 'Austria',
  IRL: 'Ireland',
  UKR: 'Ukraine',
  IRN: 'Iran',
  IRQ: 'Iraq',
  NZL: 'New Zealand',
}

/**
 * Get a clean display name for a country.
 * Strips articles, falls back to ISO3 name or context.
 */
function _displayName(rawCountry, iso3) {
  // Strip common articles/junk that regex might capture
  const cleaned = rawCountry
    ?.trim()
    ?.replace(/^(?:the|a|an|all|show|me|my|our)\s*/gi, '')
    ?.trim()
  if (cleaned) return cleaned
  // Raw was empty or just an article — use ISO3 name or context
  if (iso3 && ISO3_NAMES[iso3]) return ISO3_NAMES[iso3]
  if (iso3) return iso3
  const ctx = globeContext.getContext()
  const ctxCountry = ctx.geographic_focus?.country
  if (ctxCountry && ISO3_NAMES[ctxCountry]) return ISO3_NAMES[ctxCountry]
  if (ctxCountry) return ctxCountry
  return 'the selected country'
}

function _resolveCountryFromContext() {
  // Fall back to globe context's current country when no country is specified
  const ctx = globeContext.getContext()
  return ctx.geographic_focus?.country || null
}

function _resolveCountry(name, { strict = false } = {}) {
  const aliases = {
    us: 'USA',
    usa: 'USA',
    'united states': 'USA',
    america: 'USA',
    'the us': 'USA',
    'the usa': 'USA',
    'united states of america': 'USA',
    uk: 'GBR',
    'united kingdom': 'GBR',
    britain: 'GBR',
    'great britain': 'GBR',
    england: 'GBR',
    india: 'IND',
    china: 'CHN',
    japan: 'JPN',
    brazil: 'BRA',
    france: 'FRA',
    germany: 'DEU',
    mexico: 'MEX',
    canada: 'CAN',
    australia: 'AUS',
    italy: 'ITA',
    spain: 'ESP',
    russia: 'RUS',
    'south korea': 'KOR',
    korea: 'KOR',
    indonesia: 'IDN',
    nigeria: 'NGA',
    'south africa': 'ZAF',
    turkey: 'TUR',
    egypt: 'EGY',
    pakistan: 'PAK',
    argentina: 'ARG',
    colombia: 'COL',
    algeria: 'DZA',
    kenya: 'KEN',
    ethiopia: 'ETH',
    'saudi arabia': 'SAU',
    thailand: 'THA',
    vietnam: 'VNM',
    malaysia: 'MYS',
    philippines: 'PHL',
    peru: 'PER',
    chile: 'CHL',
    venezuela: 'VEN',
    iran: 'IRN',
    // Central America & Caribbean
    'costa rica': 'CRI',
    panama: 'PAN',
    guatemala: 'GTM',
    honduras: 'HND',
    'el salvador': 'SLV',
    nicaragua: 'NIC',
    belize: 'BLZ',
    cuba: 'CUB',
    jamaica: 'JAM',
    haiti: 'HTI',
    'dominican republic': 'DOM',
    'trinidad and tobago': 'TTO',
    'puerto rico': 'PRI',
    bahamas: 'BHS',
    barbados: 'BRB',
    // Europe
    portugal: 'PRT',
    greece: 'GRC',
    sweden: 'SWE',
    norway: 'NOR',
    denmark: 'DNK',
    finland: 'FIN',
    poland: 'POL',
    netherlands: 'NLD',
    holland: 'NLD',
    belgium: 'BEL',
    switzerland: 'CHE',
    austria: 'AUT',
    ireland: 'IRL',
    'czech republic': 'CZE',
    czechia: 'CZE',
    romania: 'ROU',
    hungary: 'HUN',
    ukraine: 'UKR',
    croatia: 'HRV',
    serbia: 'SRB',
    bulgaria: 'BGR',
    slovakia: 'SVK',
    slovenia: 'SVN',
    lithuania: 'LTU',
    latvia: 'LVA',
    estonia: 'EST',
    iceland: 'ISL',
    luxembourg: 'LUX',
    malta: 'MLT',
    albania: 'ALB',
    'north macedonia': 'MKD',
    bosnia: 'BIH',
    'bosnia and herzegovina': 'BIH',
    montenegro: 'MNE',
    moldova: 'MDA',
    belarus: 'BLR',
    cyprus: 'CYP',
    // Africa
    morocco: 'MAR',
    tunisia: 'TUN',
    libya: 'LBY',
    sudan: 'SDN',
    'south sudan': 'SSD',
    ghana: 'GHA',
    cameroon: 'CMR',
    'ivory coast': 'CIV',
    "cote d'ivoire": 'CIV',
    senegal: 'SEN',
    mali: 'MLI',
    niger: 'NER',
    chad: 'TCD',
    somalia: 'SOM',
    tanzania: 'TZA',
    uganda: 'UGA',
    rwanda: 'RWA',
    mozambique: 'MOZ',
    madagascar: 'MDG',
    angola: 'AGO',
    zimbabwe: 'ZWE',
    zambia: 'ZMB',
    botswana: 'BWA',
    namibia: 'NAM',
    congo: 'COG',
    'democratic republic of the congo': 'COD',
    'dr congo': 'COD',
    drc: 'COD',
    // Asia & Oceania
    iraq: 'IRQ',
    syria: 'SYR',
    jordan: 'JOR',
    lebanon: 'LBN',
    israel: 'ISR',
    palestine: 'PSE',
    yemen: 'YEM',
    oman: 'OMN',
    qatar: 'QAT',
    bahrain: 'BHR',
    kuwait: 'KWT',
    'united arab emirates': 'ARE',
    uae: 'ARE',
    afghanistan: 'AFG',
    bangladesh: 'BGD',
    myanmar: 'MMR',
    burma: 'MMR',
    cambodia: 'KHM',
    laos: 'LAO',
    nepal: 'NPL',
    'sri lanka': 'LKA',
    mongolia: 'MNG',
    'north korea': 'PRK',
    taiwan: 'TWN',
    singapore: 'SGP',
    'hong kong': 'HKG',
    'new zealand': 'NZL',
    fiji: 'FJI',
    'papua new guinea': 'PNG',
    // Major cities used as admin division scopes (wards of Tokyo, districts of Mumbai, etc.)
    tokyo: 'JPN',
    osaka: 'JPN',
    kyoto: 'JPN',
    mumbai: 'IND',
    delhi: 'IND',
    chennai: 'IND',
    kolkata: 'IND',
    london: 'GBR',
    paris: 'FRA',
    berlin: 'DEU',
    // US states → USA (for "counties of Texas" type queries)
    alabama: 'USA',
    alaska: 'USA',
    arizona: 'USA',
    arkansas: 'USA',
    california: 'USA',
    colorado: 'USA',
    connecticut: 'USA',
    delaware: 'USA',
    florida: 'USA',
    georgia: 'USA',
    hawaii: 'USA',
    idaho: 'USA',
    illinois: 'USA',
    indiana: 'USA',
    iowa: 'USA',
    kansas: 'USA',
    kentucky: 'USA',
    louisiana: 'USA',
    maine: 'USA',
    maryland: 'USA',
    massachusetts: 'USA',
    michigan: 'USA',
    minnesota: 'USA',
    mississippi: 'USA',
    missouri: 'USA',
    montana: 'USA',
    nebraska: 'USA',
    nevada: 'USA',
    'new hampshire': 'USA',
    'new jersey': 'USA',
    'new mexico': 'USA',
    'new york': 'USA',
    'north carolina': 'USA',
    'north dakota': 'USA',
    ohio: 'USA',
    oklahoma: 'USA',
    oregon: 'USA',
    pennsylvania: 'USA',
    'rhode island': 'USA',
    'south carolina': 'USA',
    'south dakota': 'USA',
    tennessee: 'USA',
    texas: 'USA',
    utah: 'USA',
    vermont: 'USA',
    virginia: 'USA',
    washington: 'USA',
    'west virginia': 'USA',
    wisconsin: 'USA',
    wyoming: 'USA',
    'district of columbia': 'USA',
    dc: 'USA',
    // South America
    bolivia: 'BOL',
    ecuador: 'ECU',
    paraguay: 'PRY',
    uruguay: 'URY',
    guyana: 'GUY',
    suriname: 'SUR',
    // Pacific Islands
    'marshall islands': 'MHL',
    palau: 'PLW',
    vanuatu: 'VUT',
    'solomon islands': 'SLB',
    nauru: 'NRU',
    micronesia: 'FSM',
    samoa: 'WSM',
    // Asia (remaining)
    maldives: 'MDV',
    'timor-leste': 'TLS',
    'east timor': 'TLS',
    tajikistan: 'TJK',
    bhutan: 'BTN',
    turkmenistan: 'TKM',
    brunei: 'BRN',
    kyrgyzstan: 'KGZ',
    // Europe (remaining)
    greenland: 'GRL',
    grenada: 'GRD',
    kosovo: 'XKX',
    'san marino': 'SMR',
    monaco: 'MCO',
    'saint vincent and the grenadines': 'VCT',
    'saint vincent': 'VCT',
    dominica: 'DMA',
    'saint kitts and nevis': 'KNA',
    'saint kitts': 'KNA',
    'saint lucia': 'LCA',
    'antigua and barbuda': 'ATG',
    antigua: 'ATG',
    // Africa (remaining)
    malawi: 'MWI',
    mauritius: 'MUS',
    'cabo verde': 'CPV',
    'cape verde': 'CPV',
    'sao tome and principe': 'STP',
    'sao tome': 'STP',
    guinea: 'GIN',
    liberia: 'LBR',
    'sierra leone': 'SLE',
    seychelles: 'SYC',
    'guinea-bissau': 'GNB',
    togo: 'TGO',
    'central african republic': 'CAF',
    'burkina faso': 'BFA',
    'equatorial guinea': 'GNQ',
    burundi: 'BDI',
    mauritania: 'MRT',
    eswatini: 'SWZ',
    swaziland: 'SWZ',
    gambia: 'GMB',
    lesotho: 'LSO',
    // Missing "the" countries — fill gaps for completeness
    comoros: 'COM',
    'the comoros': 'COM',
    'federated states of micronesia': 'FSM',
    // CL-144: common demonyms/adjectives — "Show density of Japanese
    // prefectures", "Population share of Brazilian states". Previously these
    // fell through to the slice(0,3)/context fallback and rendered whatever
    // country happened to be in focus.
    american: 'USA',
    british: 'GBR',
    french: 'FRA',
    german: 'DEU',
    spanish: 'ESP',
    portuguese: 'PRT',
    italian: 'ITA',
    dutch: 'NLD',
    swiss: 'CHE',
    austrian: 'AUT',
    swedish: 'SWE',
    norwegian: 'NOR',
    danish: 'DNK',
    finnish: 'FIN',
    polish: 'POL',
    greek: 'GRC',
    russian: 'RUS',
    ukrainian: 'UKR',
    turkish: 'TUR',
    japanese: 'JPN',
    chinese: 'CHN',
    korean: 'KOR',
    indian: 'IND',
    pakistani: 'PAK',
    bangladeshi: 'BGD',
    indonesian: 'IDN',
    vietnamese: 'VNM',
    thai: 'THA',
    filipino: 'PHL',
    malaysian: 'MYS',
    iranian: 'IRN',
    iraqi: 'IRQ',
    israeli: 'ISR',
    saudi: 'SAU',
    egyptian: 'EGY',
    moroccan: 'MAR',
    algerian: 'DZA',
    nigerian: 'NGA',
    kenyan: 'KEN',
    ethiopian: 'ETH',
    ghanaian: 'GHA',
    tanzanian: 'TZA',
    ugandan: 'UGA',
    'south african': 'ZAF',
    mexican: 'MEX',
    canadian: 'CAN',
    brazilian: 'BRA',
    argentine: 'ARG',
    argentinian: 'ARG',
    chilean: 'CHL',
    peruvian: 'PER',
    colombian: 'COL',
    venezuelan: 'VEN',
    australian: 'AUS',
  }
  if (!name || !name.trim()) {
    // No country specified — use globe context's current focus
    if (strict) return null
    return _resolveCountryFromContext() || ''
  }
  // Use centralised normalizeCountryName() for "the" handling,
  // then strip remaining common prefixes (a, an, all, show, me, etc.)
  // \s+ (not \s*) — without required whitespace, the single-letter "a"
  // alternative consumes the leading "A" of country names like "Algeria",
  // "Angola", "Argentina", producing junk codes like LGE/NGO/RGE.
  const normalized = normalizeCountryName(name)
  const lower = normalized.replace(/^(?:a|an|all|show|me|my|our)\s+/gi, '').trim()
  if (!lower) {
    // After stripping, nothing left — use globe context
    if (strict) return null
    return _resolveCountryFromContext() || ''
  }
  const matched = aliases[lower]
  if (matched) return matched
  // If the result would be a garbage 3-letter code (not a known alias), try context
  const fallback = lower.toUpperCase().slice(0, 3)
  // Only use the raw 3-letter code if it looks like an actual ISO code (all in aliases values)
  const knownIso3 = new Set(Object.values(aliases))
  // CL-144 strict mode: the name is NOT a recognizable country — report that
  // instead of falling back to the globe-context focus or a sliced prefix.
  // Used by the admin boundary/choropleth builders so "Japanese language
  // regions" or "election results by state" defer to the AI rather than
  // rendering the divisions of whichever country happens to be in focus.
  // Prefix slicing is only trusted when the input IS a bare 3-letter code —
  // otherwise "TURkish speaking" would strict-resolve to Turkey.
  if (strict) return lower.length === 3 && knownIso3.has(fallback) ? fallback : null
  if (knownIso3.has(fallback)) return fallback
  // Unknown — try globe context, then fall back to raw
  return _resolveCountryFromContext() || fallback
}

// Sub-national names that resolve to a country via aliases but should scope the view
const _SUB_NATIONAL_SCOPE = new Set([
  'alabama',
  'alaska',
  'arizona',
  'arkansas',
  'california',
  'colorado',
  'connecticut',
  'delaware',
  'florida',
  'georgia',
  'hawaii',
  'idaho',
  'illinois',
  'indiana',
  'iowa',
  'kansas',
  'kentucky',
  'louisiana',
  'maine',
  'maryland',
  'massachusetts',
  'michigan',
  'minnesota',
  'mississippi',
  'missouri',
  'montana',
  'nebraska',
  'nevada',
  'new hampshire',
  'new jersey',
  'new mexico',
  'new york',
  'north carolina',
  'north dakota',
  'ohio',
  'oklahoma',
  'oregon',
  'pennsylvania',
  'rhode island',
  'south carolina',
  'south dakota',
  'tennessee',
  'texas',
  'utah',
  'vermont',
  'virginia',
  'washington',
  'west virginia',
  'wisconsin',
  'wyoming',
  'district of columbia',
  'dc',
  'tokyo',
  'osaka',
  'kyoto',
  'mumbai',
  'delhi',
  'chennai',
  'kolkata',
  'london',
  'paris',
  'berlin',
])

// CL-144: normalize vernacular climate words to the Köppen top-level classes
// the climate_zones op understands ("rainforest" → Af, "savanna" → Aw — both
// tropical A-group zones).
function _koppenClass(word) {
  const w = word.toLowerCase().trim()
  if (w === 'rainforest' || w === 'savanna') return 'tropical'
  return w
}

function _choroplethByTerm(adminTerm, country, metric) {
  const iso3 = _resolveCountry(country)
  const displayCountry = _displayName(country, iso3)
  const term = adminTerm.toLowerCase().trim()
  const metricLabel = metric === 'density' ? 'population density' : metric
  const countryLower = country
    .toLowerCase()
    .trim()
    .replace(/^(?:the|a|an)\s*/gi, '')
    .trim()
  const isSubNational = _SUB_NATIONAL_SCOPE.has(countryLower)
  // CL-144: same strict-scope guard as _boundaryDeep — see comment there.
  if (!isSubNational && !_resolveCountry(country, { strict: true })) return null
  const intent = {
    type: 'division_action',
    action: 'admin_choropleth',
    country: iso3,
    subtype: term,
    metric: metric === 'density' ? 'density' : metric === 'area' ? 'area' : 'population',
    speech: `Here are the ${term} of ${country} by ${metricLabel}.`,
    _fastQuery: true,
  }
  if (isSubNational) {
    intent.scope_division = country
  }
  return intent
}

function _choroplethAndCapitals(country) {
  const iso3 = _resolveCountry(country)
  const displayCountry = _displayName(country, iso3)
  return {
    type: 'division_action',
    action: 'admin_choropleth',
    country: iso3,
    subtype: 'states',
    metric: 'population',
    show_capitals: true,
    speech: `Here are the state populations and capitals of ${displayCountry}.`,
    _fastQuery: true,
  }
}

function _boundaryAndCapitals(country) {
  const iso3 = _resolveCountry(country)
  const displayCountry = _displayName(country, iso3)
  return {
    type: 'division_action',
    action: 'show_divisions',
    country: iso3,
    subtype: 'states',
    show_capitals: true,
    speech: `Here are the states and capitals of ${displayCountry}.`,
    _fastQuery: true,
  }
}

function _riversOnly(country) {
  if (!country) {
    return {
      type: 'rivers',
      target: { location: null },
      speech: `Here are the major rivers of the world.`,
      _fastQuery: true,
    }
  }
  const iso3 = _resolveCountry(country)
  return {
    type: 'rivers',
    target: { location: country, country: iso3 },
    speech: `Here are the major rivers of ${country}.`,
    _fastQuery: true,
  }
}

function _riversWithBoundaries(country) {
  const iso3 = _resolveCountry(country)
  return {
    type: 'rivers',
    target: { location: country, country: iso3 },
    show_boundaries: true,
    speech: `Here are the rivers and states of ${country}.`,
    _fastQuery: true,
  }
}

function _riversWithBoundariesAndCapitals(country) {
  const iso3 = _resolveCountry(country)
  return {
    type: 'rivers',
    target: { location: country, country: iso3 },
    show_boundaries: true,
    show_capitals: true,
    speech: `Here are the rivers, states, and capitals of ${country}.`,
    _fastQuery: true,
  }
}

function _boundaryOnly(country) {
  const iso3 = _resolveCountry(country)
  return {
    type: 'division_action',
    action: 'show_divisions',
    country: iso3,
    subtype: 'states',
    speech: `Here are the states of ${country}.`,
    _fastQuery: true,
  }
}

/**
 * Resolve an admin term against the synonym dictionary.
 * Country-specific perCountry entries take precedence over global ones,
 * matching the resolution rules in admin-boundaries.js.
 * Returns true if the term is recognized for the given iso3, else false.
 */
function _isKnownAdminTerm(term, iso3) {
  if (!term) return false
  const t = term.toLowerCase().trim()
  const perCountry = synonymDict?.perCountry?.[iso3]
  if (perCountry && t in perCountry) return true
  const global = synonymDict?.global
  if (global && t in global) {
    const v = global[t]
    // Special routing values like DRILL_DOWN are not admin levels
    return typeof v === 'string' && (v.startsWith('ADM') || v.includes('_OR_'))
  }
  return false
}

function _boundaryDeep(adminTerm, country) {
  const iso3 = _resolveCountry(country)
  const displayCountry = _displayName(country, iso3)
  // Pass term as-is (lowercase) — synonym dictionary has both singular and plural forms
  const term = adminTerm.toLowerCase().trim()
  const countryLower = country
    .toLowerCase()
    .trim()
    .replace(/^(?:the|a|an)\s*/gi, '')
    .trim()
  const isSubNational = _SUB_NATIONAL_SCOPE.has(countryLower)
  // CL-144: only fast-path when the captured scope is a real country (or a
  // known sub-national unit). Free text like "Japanese language regions",
  // "election results by state", or "tropical regions" previously rendered
  // divisions of the globe-context focus country — defer to the AI instead.
  if (!isSubNational && !_resolveCountry(country, { strict: true })) return null
  const intent = {
    type: 'division_action',
    action: 'show_divisions',
    country: iso3,
    subtype: term,
    speech: `Here are the ${term} of ${country}.`,
    _fastQuery: true,
  }
  if (isSubNational) {
    intent.scope_division = country
  }
  return intent
}

function _boundaryMaxDepth(country) {
  const iso3 = _resolveCountry(country)
  const displayCountry = _displayName(country, iso3)
  return {
    type: 'division_action',
    action: 'show_divisions',
    country: iso3,
    subtype: 'MAX_DEPTH',
    speech: `Showing the most detailed boundaries of ${displayCountry}.`,
    _fastQuery: true,
  }
}

function _poiQuery(poiText, country) {
  // Normalize POI text to category
  const poiLower = poiText.toLowerCase().replace(/\s+/g, ' ').trim()
  const category = POI_CATEGORY_MAP[poiLower] || poiLower.replace(/s$/, '')
  const iso3 = _resolveCountry(country)
  // If the location isn't a real country (e.g. "Maharashtra" → garbage code
  // "MAH"), this is a sub-national unit, not a country-wide POI query. Returning
  // null lets the fast-query dispatcher fall through to the LLM, which resolves
  // the unit to its country + admin level and emits a division_action
  // (territory_pois) — routed to the poi_in_admin op for an exact in-polygon
  // count. Only forward to the country-level POI path when iso3 is a real code.
  if (!iso3 || !iso3ToIso2(iso3)) return null
  const displayCountry = country?.trim() || iso3 || 'the selected area'
  return {
    type: 'filter',
    target: { dataset: 'places', location: displayCountry, country: iso3 },
    poi_category: category,
    operation: {
      action: 'filterByAttribute',
      attribute: 'category',
      filter: { operator: '==', value: category },
    },
    visualization: { type: 'poi-markers', colorScheme: 'categorical' },
    voiceResponse: {
      shouldSpeak: true,
      template: `Here are the ${poiLower} in ${displayCountry}.`,
    },
    speech: `Please show me all the ${poiLower} in the ${displayCountry}`,
    _fastQuery: true,
  }
}

function _h3DensityQuery(poiText, country, sourceText) {
  const poiLower = poiText.toLowerCase().replace(/\s+/g, ' ').trim()
  const category = POI_CATEGORY_MAP[poiLower] || poiLower.replace(/s$/, '')
  // CL-144: country === null means an explicit worldwide/global scope — do NOT
  // fall back to the globe-context focus country.
  const iso3 = country ? _resolveCountry(country) : null
  const displayCountry = country?.trim() || (country === null ? 'the world' : iso3 || 'the area')
  // Neighborhood smoothing trigger (playbook §B2): "smoothed density",
  // "hot areas", "neighborhood density"
  const smooth = /\b(?:smooth\w*|hot\s*areas?|neighbou?rhood)\b/i.test(sourceText || '')
  return {
    type: 'h3_density',
    target: { dataset: 'places', location: displayCountry, country: iso3 },
    poi_category: category,
    smooth,
    visualization: { type: 'h3-density', colorScheme: 'sequential', colorRamp: 'YlOrRd' },
    voiceResponse: {
      shouldSpeak: true,
      template: `Here is the ${poiLower} density in ${displayCountry}.`,
    },
    speech: `Show ${poiLower} density in ${displayCountry}`,
    _fastQuery: true,
  }
}

function _h3PerCapitaQuery(poiText, country) {
  const poiLower = poiText.toLowerCase().replace(/\s+/g, ' ').trim()
  const category = POI_CATEGORY_MAP[poiLower] || poiLower.replace(/s$/, '')
  const iso3 = _resolveCountry(country)
  const displayCountry = country?.trim() || iso3 || 'the area'
  return {
    type: 'h3_density',
    target: { dataset: 'places', location: displayCountry, country: iso3 },
    poi_category: category,
    h3_mode: 'per_capita',
    visualization: { type: 'h3-density', colorScheme: 'sequential', colorRamp: 'viridis' },
    voiceResponse: {
      shouldSpeak: true,
      template: `Here are the ${poiLower} per capita in ${displayCountry}.`,
    },
    speech: `Show ${poiLower} per capita in ${displayCountry}`,
    _fastQuery: true,
  }
}

function _choroplethQuery(location, metricText) {
  // Map user-facing metric names to actual Natural Earth GeoJSON property names.
  // POP_EST, GDP_MD, INCOME_GRP are real Natural Earth properties.
  // density_per_km2 and area_km2 only exist in admin boundary data (geoBoundaries);
  // for countries they'll fall through to showDataset which is acceptable.
  const metricMap = {
    population: 'POP_EST',
    pop_est: 'POP_EST',
    POP_EST: 'POP_EST',
    gdp: 'GDP_MD',
    gdp_md: 'GDP_MD',
    GDP_MD: 'GDP_MD',
    income: 'INCOME_GRP',
    'income group': 'INCOME_GRP',
    income_grp: 'INCOME_GRP',
    INCOME_GRP: 'INCOME_GRP',
    density: 'density_per_km2',
    pop_density: 'density_per_km2',
    POP_DENSITY: 'density_per_km2',
    pop_density_km2: 'density_per_km2',
    area: 'area_km2',
    area_km2: 'area_km2',
    AREA_KM2: 'area_km2',
  }
  const attribute = metricMap[metricText.toLowerCase()] || 'population'
  const label = metricText.toLowerCase().replace(/_/g, ' ')

  // Normalize adjective → continent name for CONTINENT_VIEWS lookup
  const continentAdjectives = {
    african: 'Africa',
    asian: 'Asia',
    european: 'Europe',
    'north american': 'North America',
    'south american': 'South America',
    oceanian: 'Oceania',
    'central american': 'Central America',
    caribbean: 'Caribbean',
    'middle east': 'Middle East',
    'middle eastern': 'Middle East',
    'southeast asia': 'Southeast Asia',
    'southeast asian': 'Southeast Asia',
    'western europe': 'Western Europe',
    'western european': 'Western Europe',
    'eastern europe': 'Eastern Europe',
    'eastern european': 'Eastern Europe',
    scandinavia: 'Scandinavia',
    scandinavian: 'Scandinavia',
    nordic: 'Scandinavia',
    'east asia': 'East Asia',
    'east asian': 'East Asia',
    'south asia': 'South Asia',
    'south asian': 'South Asia',
    'southern africa': 'Southern Africa',
    'southern african': 'Southern Africa',
    'west africa': 'West Africa',
    'west african': 'West Africa',
    'east africa': 'East Africa',
    'east african': 'East Africa',
    'north africa': 'North Africa',
    'north african': 'North Africa',
    'pacific islands': 'Pacific Islands',
    pacific: 'Pacific Islands',
    'central asia': 'Central Asia',
    'central asian': 'Central Asia',
    balkans: 'Balkans',
    balkan: 'Balkans',
    'the balkans': 'Balkans',
    'sub-saharan africa': 'Sub-Saharan Africa',
    'sub saharan africa': 'Sub-Saharan Africa',
    'subsaharan africa': 'Sub-Saharan Africa',
    'sub-saharan': 'Sub-Saharan Africa',
    'the world': null,
    world: null,
    all: null,
    global: null,
  }

  let resolvedLocation = location
  if (location) {
    const lower = location.toLowerCase().replace(/^the\s+/, '')
    if (Object.prototype.hasOwnProperty.call(continentAdjectives, lower)) {
      resolvedLocation = continentAdjectives[lower]
    }
  }

  const intent = {
    type: 'choropleth',
    target: { dataset: 'countries' },
    operation: { action: 'choropleth', attribute },
    visualization: { type: 'choropleth', colorScheme: 'sequential', colorRamp: 'YlOrRd' },
    voiceResponse: {
      shouldSpeak: true,
      template: resolvedLocation
        ? `Here is the ${label} of ${resolvedLocation} countries.`
        : `Here is ${label} by country.`,
    },
    _fastQuery: true,
  }

  if (resolvedLocation) {
    intent.target.location = resolvedLocation
  }

  return intent
}

function _capitalsOnly(country) {
  const iso3 = _resolveCountry(country)
  const r2Url = getR2Url()
  const table = r2Url ? `'${r2Url}/data/cities.parquet'` : `'/data/cities.parquet'`
  const iso2Map = {
    USA: 'US',
    GBR: 'GB',
    IND: 'IN',
    CHN: 'CN',
    JPN: 'JP',
    BRA: 'BR',
    FRA: 'FR',
    DEU: 'DE',
    MEX: 'MX',
    CAN: 'CA',
    AUS: 'AU',
    ITA: 'IT',
    ESP: 'ES',
    RUS: 'RU',
    KOR: 'KR',
    IDN: 'ID',
    NGA: 'NG',
    ZAF: 'ZA',
    TUR: 'TR',
    EGY: 'EG',
    PAK: 'PK',
    ARG: 'AR',
    COL: 'CO',
    DZA: 'DZ',
    KEN: 'KE',
    ETH: 'ET',
    SAU: 'SA',
    THA: 'TH',
    VNM: 'VN',
    MYS: 'MY',
    PHL: 'PH',
    PER: 'PE',
    CHL: 'CL',
    VEN: 'VE',
    IRN: 'IR',
  }
  const iso2 = iso2Map[iso3] || iso3.slice(0, 2)
  return {
    type: 'filter',
    target: { dataset: 'cities', location: country, country: iso3 },
    operation: { action: 'filterByAttribute', attribute: 'pop_max' },
    sql: `SELECT name, adm0name, adm1name, pop_max, ST_AsGeoJSON(geometry) as geojson FROM ${table} WHERE iso_a2 = '${iso2}' AND featurecla ILIKE '%capital%' ORDER BY pop_max DESC`,
    visualization: { type: 'proportional-symbol', colorScheme: 'sequential', colorRamp: 'plasma' },
    voiceResponse: { shouldSpeak: true, template: `Here are the capital cities of ${country}.` },
    _fastQuery: true,
  }
}

/**
 * Try to match a fast-query shortcut. Returns intent or null.
 * In Moon mode, tries lunar shortcuts first.
 */
/**
 * Parse a population threshold phrase ("over 100k", "more than 1 million",
 * "below 50000 people") into { attribute, operator, value }. Returns null if none.
 */
export function _parsePopThreshold(text) {
  const m =
    text.match(
      /\b(?:population|pop|residents|people|inhabitants)\b[^\d<>]*?(over|above|more\s+than|greater\s+than|at\s+least|under|below|less\s+than|fewer\s+than)?\s*([\d][\d.,]*)\s*(k|m|b|thousand|million|billion)?/i
    ) ||
    text.match(
      /\b(over|above|more\s+than|greater\s+than|at\s+least|under|below|less\s+than|fewer\s+than)\s+([\d][\d.,]*)\s*(k|m|b|thousand|million|billion)?\s*(?:residents|people|inhabitants|population)\b/i
    )
  if (!m) return null
  let n = parseFloat(String(m[2]).replace(/,/g, ''))
  if (!Number.isFinite(n)) return null
  const suf = (m[3] || '').toLowerCase()
  if (suf === 'k' || suf === 'thousand') n *= 1e3
  else if (suf === 'm' || suf === 'million') n *= 1e6
  else if (suf === 'b' || suf === 'billion') n *= 1e9
  const lt = /under|below|less|fewer/.test((m[1] || 'over').toLowerCase())
  return { attribute: 'population', operator: lt ? '<' : '>', value: Math.round(n) }
}

/**
 * Fast-path matcher for point-in-region containment queries, e.g.
 * "cities in climate zone Cfb with population over 100k". Routes deterministically
 * to the point_in_region op — the LLM tends to mis-pick a SQL/choropleth tool for
 * this phrasing. Returns a legacy intent or null. Exported for testing.
 */
export function matchPointInRegion(text) {
  const t = text.toLowerCase()
  if (!/\bclimate\s+zone|\bk[öo]ppen|\bclimate\s+class/.test(t)) return null

  let target = null
  if (/\bcit(?:y|ies)\b|\btowns?\b/.test(t)) target = 'cities'
  else if (/\bheritage\s+sites?\b/.test(t)) target = 'heritage_sites'
  else if (/\bvolcan(?:o|oes|os)\b/.test(t)) target = 'volcanoes'
  if (!target) return null

  if (!/\b(?:in|inside|within|located|that\s+(?:are|fall))\b/.test(t)) return null

  let region_class = null
  const codeM =
    text.match(
      /(?:climate\s+zone|k[öo]ppen(?:\s+climate)?|climate\s+class(?:ification)?|zones?)\s+([A-E][A-Za-z]{1,2})\b/i
    ) || text.match(/\b([A-E][fsw][abcdh])\b/)
  if (codeM) region_class = codeM[1]
  if (!region_class) {
    const classM = t.match(
      /\b(tropical|arid|desert|temperate|mediterranean|oceanic|continental|polar|tundra|subarctic)\b/
    )
    if (classM) region_class = classM[1]
  }
  if (!region_class) return null

  const pop = _parsePopThreshold(text)
  return {
    type: 'point_in_region',
    target,
    region: 'climate_zones',
    region_class,
    point_attribute: pop ? pop.attribute : null,
    point_operator: pop ? pop.operator : null,
    point_value: pop ? pop.value : null,
    _fastQuery: true,
  }
}

/**
 * Fast-path matcher for buffer-zone queries, e.g. "show a 50 km zone around
 * volcanoes in Indonesia" / "buffer cities by 20 km". Requires an explicit
 * buffer/zone keyword so it never steals proximity ("within X of Y") phrasings.
 * Returns a legacy intent or null. Exported for testing.
 */
export function matchBuffer(text) {
  const t = text.toLowerCase()
  if (!/\bbuffer\b|\bzones?\b/.test(t)) return null

  let target = null
  if (/\bvolcan(?:o|oes|os)\b/.test(t)) target = 'volcanoes'
  else if (/\bheritage\s+sites?\b/.test(t)) target = 'heritage_sites'
  else if (/\bcit(?:y|ies)\b|\btowns?\b/.test(t)) target = 'cities'
  if (!target) return null

  const rm = text.match(/(\d[\d.,]*)\s*(?:km|kilomet(?:er|re)s?)\b/i)
  if (!rm) return null
  const radiusKm = parseFloat(rm[1].replace(/,/g, ''))
  if (!Number.isFinite(radiusKm) || radiusKm <= 0) return null

  let location = null
  const lm = text.match(/\bin\s+([A-Za-z][A-Za-z .'-]{2,})\s*$/)
  if (lm) location = lm[1].trim()

  return { type: 'buffer_zone', target, radius_km: radiusKm, location, _fastQuery: true }
}

/**
 * Clip-to-region fast-path (e.g. "show rivers inside Kenya", "rivers within
 * Brazil", "clip rivers to Egypt") → deterministic route to the clip_to_region
 * op. Requires an explicit clip/containment keyword (inside | within | clip)
 * so it never steals the plain "rivers in X" flow, and rejects any km distance
 * so it never steals proximity ("rivers within 10 km of X").
 */
export function matchClipToRegion(text) {
  const t = text.toLowerCase()

  // Must reference a clippable line layer (v1: rivers).
  let target = null
  if (/\brivers?\b/.test(t)) target = 'rivers'
  if (!target) return null

  // Require an explicit clip / containment keyword (not a bare "in").
  if (!/\bclip(?:ped)?\b|\binside\b|\bwithin\b/.test(t)) return null

  // A km distance means proximity/buffer, not a region clip.
  if (/\d[\d.,]*\s*(?:km|kilomet(?:er|re)s?)\b/i.test(t)) return null

  // Capture the region after the clip keyword (inside | within | [clip…] to).
  const rm = text.match(/\b(?:inside|within|to)\s+([A-Za-z][A-Za-z .'-]{2,})\s*$/i)
  if (!rm) return null
  const region = rm[1].trim().replace(/^the\s+/i, '')
  if (!region) return null

  return { type: 'clip_to_region', target, region, admin_level: null, _fastQuery: true }
}

/**
 * Convex-hull fast-path (e.g. "draw the area covering all volcanoes in Japan",
 * "convex hull of cities in France", "extent of heritage sites in Italy") →
 * deterministic route to the convex_hull op. Requires a known point target and
 * an explicit hull/extent keyword so it never steals a plain "show volcanoes"
 * query, and rejects a km distance so it never steals a buffer.
 */
export function matchConvexHull(text) {
  const t = text.toLowerCase()

  let target = null
  if (/\bvolcan(?:o|oes|os)\b/.test(t)) target = 'volcanoes'
  else if (/\bheritage\s+sites?\b/.test(t)) target = 'heritage_sites'
  else if (/\bcit(?:y|ies)\b|\btowns?\b/.test(t)) target = 'cities'
  if (!target) return null

  // Explicit hull/extent intent keyword.
  const hullKeyword =
    /\bconvex hull\b|\b(?:area|region|zone)\s+(?:covering|containing|enclosing|encompassing|around all)\b|\bextent of\b|\bfootprint of\b|\bsmallest area\b/
  if (!hullKeyword.test(t)) return null

  // A km distance means a buffer, not a hull.
  if (/\d[\d.,]*\s*(?:km|kilomet(?:er|re)s?)\b/i.test(t)) return null

  let location = null
  const lm = text.match(/\bin\s+([A-Za-z][A-Za-z .'-]{2,})\s*$/)
  if (lm) location = lm[1].trim().replace(/^the\s+/i, '')

  return { type: 'convex_hull', target, location, _fastQuery: true }
}

/**
 * Voronoi/Thiessen fast-path. Handles two phrasings:
 *   "divide Japan by [its] nearest city"  → location=Japan, target=cities
 *   "voronoi of cities in France" / "thiessen polygons of cities in France"
 *   "nearest-city regions in Japan"
 * Requires a known point target + a voronoi/partition keyword so it never
 * steals a plain layer query.
 */
export function matchVoronoi(text) {
  const t = text.toLowerCase()

  let target = null
  if (/\bcit(?:y|ies)\b|\btowns?\b/.test(t)) target = 'cities'
  else if (/\bvolcan(?:o|oes|os)\b/.test(t)) target = 'volcanoes'
  else if (/\bheritage\s+sites?\b/.test(t)) target = 'heritage_sites'
  if (!target) return null

  const voronoiKeyword =
    /\bvoronoi\b|\bthiessen\b|\bdivide\b[\s\S]*\bby\b|\bnearest[\s-]\w+\s+regions?\b|\bcarve up\b|\bcatchment|\bclosest to each\b/
  if (!voronoiKeyword.test(t)) return null

  let location = null
  // "divide <REGION> by …" → the region is what's being partitioned.
  const dm = text.match(/\bdivide\s+([A-Za-z][A-Za-z .'-]{2,}?)\s+(?:up\s+)?by\b/i)
  if (dm) location = dm[1].trim().replace(/^the\s+/i, '')
  // Otherwise a trailing "in <LOCATION>".
  if (!location) {
    const im = text.match(/\bin\s+([A-Za-z][A-Za-z .'-]{2,})\s*$/)
    if (im) location = im[1].trim().replace(/^the\s+/i, '')
  }

  return { type: 'voronoi', target, location, _fastQuery: true }
}

/**
 * Centroid fast-path (e.g. "show the center of each African country", "centroid
 * of France", "geographic center of Japan", "centers of countries in Europe").
 * Requires either an explicit centroid keyword, OR a center/middle word together
 * with a countries mention — so it never steals a plain "center on France"
 * navigation. Location can be a continent adjective, a trailing "in <place>", or
 * a single "<center> of <place>".
 */
export function matchCentroid(text) {
  const t = text.toLowerCase()
  const centroidWord =
    /\bcentroids?\b|\bgeographic (?:cent(?:er|re)|middle)\b|\bcent(?:er|re) of mass\b/.test(t)
  // CL-261: "middle" in "Middle East(ern)" is a place name, not a centre word.
  const centerWord = /\bcent(?:er|re)s?\b|\bmiddle\b(?!\s+east)/.test(t)
  const mentionsCountries = /\bcountr(?:y|ies)\b|\bnations?\b/.test(t)
  if (!(centroidWord || (centerWord && mentionsCountries))) return null

  let location = null
  const adj = t.match(
    /\b(african|european|asian|north american|south american|oceanian|australian)\b/
  )
  if (adj) location = adj[1]
  if (!location) {
    const im = text.match(/\bin\s+([A-Za-z][A-Za-z .'-]{2,})\s*$/)
    if (im) location = im[1].trim().replace(/^the\s+/i, '')
  }
  if (!location && !mentionsCountries) {
    const om = text.match(
      /\b(?:centroids?|cent(?:er|re)|middle)\s+of\s+([A-Za-z][A-Za-z .'-]{2,})\s*$/i
    )
    if (om) location = om[1].trim().replace(/^the\s+/i, '')
  }

  return { type: 'centroid', target: 'countries', location, _fastQuery: true }
}

/**
 * Dissolve fast-path — merge country polygons, dropping internal borders.
 * "dissolve the countries into continents" → grouped by continent.
 * "merge the African countries into one" → one polygon for that continent.
 * Requires a dissolve verb AND a geographic subject (countries/continents/a
 * continent) so it doesn't steal unrelated "merge"/"combine" commands.
 */
export function matchDissolve(text) {
  const t = ` ${text.toLowerCase().trim()} `
  const verb = /\b(dissolve|merge|combine|unify|fuse)\b/.test(t)
  if (!verb) return null

  // Named continent scope (adjective or noun), reusing the continent vocabulary.
  // Multiword and adjective forms first so "north american"/"europe" win over a
  // shorter substring match.
  let location = null
  const keys = [
    'north american',
    'south american',
    'north america',
    'south america',
    'african',
    'european',
    'asian',
    'oceanian',
    'australian',
    'antarctic',
    'africa',
    'europe',
    'asia',
    'oceania',
    'antarctica',
  ]
  for (const k of keys) {
    if (new RegExp(`\\b${k.replace(/ /g, '\\s+')}\\b`).test(t)) {
      location = k
      break
    }
  }

  // Require a geographic subject: countries/nations/continents word or a
  // named continent — so a bare "merge these layers" is not stolen.
  const geoSubject = /\bcountr(?:y|ies)\b|\bnations?\b|\bcontinents?\b/.test(t) || !!location
  if (!geoSubject) return null

  // "into continents" / "by continent" → group the scope by continent.
  const groupBy = /\binto\s+(?:their\s+)?continents?\b|\bby\s+continent\b/.test(t)
    ? 'continent'
    : null

  return { type: 'dissolve', target: 'countries', location, groupBy, _fastQuery: true }
}

/**
 * Union fast-path — combine TWO named regions into one shape.
 * "combine France and Germany", "union of Africa and Europe", "France plus Spain".
 * Runs before matchDissolve so a two-operand "A and B" isn't mistaken for a
 * single-continent dissolve. Requires an explicit combine/union verb (or "plus"/
 * "+") joining two non-empty operands.
 */
export function matchUnion(text) {
  const t = text.trim()
  let m = t.match(
    /^\s*(?:combine|union of|unite|merge|join|fuse)\s+(.+?)\s+(?:and|with|plus|&|\+)\s+(.+?)\s*$/i
  )
  if (!m) m = t.match(/^\s*(.+?)\s+(?:plus|\+)\s+(.+?)\s*$/i)
  if (!m) return null

  const clean = (s) =>
    s
      .trim()
      .replace(/^the\s+/i, '')
      .replace(/\b(?:countries|nations|continents?|regions?)\b/gi, '')
      .replace(/\s+(?:into one|combined|together|as one)\s*$/i, '')
      .replace(/\s+/g, ' ')
      .trim()
  const regionA = clean(m[1])
  const regionB = clean(m[2])
  if (!regionA || !regionB) return null
  // Guard against non-geographic "A plus B" (e.g. arithmetic, long phrases):
  // place names are short and non-numeric.
  const bad = (s) => /\d/.test(s) || s.split(' ').length > 4
  if (bad(regionA) || bad(regionB)) return null

  return { type: 'union', target: 'countries', regionA, regionB, _fastQuery: true }
}

/**
 * Erase fast-path — subtract region B from region A (A − B).
 * "Africa without Egypt", "Europe minus Russia", "South America except Brazil",
 * and the reversed "erase/remove Egypt from Africa". Requires two non-empty,
 * place-like operands and rejects leading query verbs (show/list/…) so it does
 * not steal thematic filters like "countries without coastlines".
 */
export function matchErase(text) {
  const t = text.trim()
  let regionA = null
  let regionB = null

  // "erase/remove/subtract/exclude B from A" (operands reversed)
  let m = t.match(
    /^\s*(?:erase|remove|subtract|cut|exclude)\s+(.+?)\s+(?:from|out of)\s+(.+?)\s*$/i
  )
  if (m) {
    regionB = m[1]
    regionA = m[2]
  } else {
    // "A without/minus/except/excluding B"
    m = t.match(
      /^\s*(.+?)\s+(?:without|minus|except(?:\s+for)?|excluding|not including)\s+(.+?)\s*$/i
    )
    if (m) {
      regionA = m[1]
      regionB = m[2]
    }
  }
  if (!m) return null

  const clean = (s) =>
    s
      .trim()
      .replace(/^the\s+/i, '')
      .replace(/\b(?:countries|nations|continents?|regions?)\b/gi, '')
      .replace(/\s+/g, ' ')
      .trim()
  regionA = clean(regionA)
  regionB = clean(regionB)
  if (!regionA || !regionB) return null

  const bad = (s) => /\d/.test(s) || s.split(' ').length > 4
  if (bad(regionA) || bad(regionB)) return null
  // Reject leading query verbs so thematic filters aren't stolen.
  if (/^(?:show|list|find|display|give|get|map|where|which|what|all)\b/i.test(regionA)) return null

  return { type: 'erase', target: 'countries', regionA, regionB, _fastQuery: true }
}

// Thematic operand-B words that make an intersect unambiguous (latitude bands,
// biomes, Köppen climate classes). Kept in sync with B_LAYERS in ops/intersect.js.
const INTERSECT_THEMATIC =
  /\b(tropics?|tropical|temperate|polar|arctic|antarctic|deserts?|rainforests?|jungle|tundra|taiga|boreal|savann?a|grasslands?|mangroves?|biomes?|climate|k[oö]ppen|hemisphere|steppe|mediterranean|monsoon|oceanic|zones?|bands?)\b/i

/**
 * Intersect fast-path — the overlapping part of region A and operand B.
 * "the tropical part of Brazil", "Brazil in the tropics", "Australia's desert
 * biome", "where Brazil and the tropics overlap". Requires a thematic operand
 * (or an explicit overlap/intersection phrasing) so plain navigation/filters
 * like "cities in France" are never stolen.
 */
export function matchIntersect(text) {
  const t = text.trim()
  let a = null
  let b = null

  // "the <B> part of <A>"
  let m = t.match(/^\s*(?:show|draw|display|map)?\s*the\s+(.+?)\s+part\s+of\s+(.+?)\s*$/i)
  if (m) {
    b = m[1]
    a = m[2]
  }
  // "<A>'s <B>"  (e.g. "Australia's desert biome")
  if (!m) {
    m = t.match(/^\s*(?:show|draw|display|map)?\s*(.+?)'s\s+(.+?)\s*$/i)
    if (m) {
      a = m[1]
      b = m[2]
    }
  }
  // "(the part of) <A> in/within/inside the <B>"
  if (!m) {
    m = t.match(
      /^\s*(?:show|draw|display|map)?\s*(?:the\s+part\s+of\s+)?(.+?)\s+(?:in|within|inside)\s+the\s+(.+?)\s*$/i
    )
    if (m) {
      a = m[1]
      b = m[2]
    }
  }
  // "where <A> and <B> overlap/meet/intersect" · "overlap/intersection of <A> and <B>"
  if (!m) {
    m = t.match(/^\s*where\s+(.+?)\s+and\s+(.+?)\s+(?:overlap|intersect|meet)s?\s*$/i)
    if (m) {
      a = m[1]
      b = m[2]
    }
  }
  if (!m) {
    m = t.match(/^\s*(?:the\s+)?(?:overlap|intersection)\s+of\s+(.+?)\s+and\s+(.+?)\s*$/i)
    if (m) {
      a = m[1]
      b = m[2]
    }
  }
  if (!m) return null

  const clean = (s) =>
    s
      .trim()
      .replace(/^the\s+/i, '')
      .replace(/\s+/g, ' ')
      .trim()
  a = clean(a)
  b = clean(b)
  if (!a || !b) return null

  // Require a thematic operand somewhere; if A is the thematic one, swap so A is
  // the region and B the operand ("the tropical part of Brazil" already has
  // B=tropical, A=Brazil — no swap; a reversed phrasing gets corrected here).
  const aThem = INTERSECT_THEMATIC.test(a)
  const bThem = INTERSECT_THEMATIC.test(b)
  if (!aThem && !bThem) return null
  if (aThem && !bThem) {
    const tmp = a
    a = b
    b = tmp
  }
  // Guard against runaway captures.
  const bad = (s) => /\d/.test(s) || s.split(' ').length > 5
  if (bad(a) || bad(b)) return null

  return { type: 'intersect', target: 'countries', regionA: a, operandB: b, _fastQuery: true }
}

// Exported for the golden parser eval harness (scripts/parser-eval.mjs,
// tests/parser-golden.test.js — CL-142). Pure with respect to the DOM: only
// touches globeContext/globeSwitcher state, both node-safe (same import seam
// the *-fastpath.test.js files already rely on).
export function tryFastQuery(text) {
  // Normalize speech-to-text artifacts:
  const cleaned = text
    .trim()
    .replace(/county's/gi, 'counties')
    .replace(/district's/gi, 'districts')
    .replace(/province's/gi, 'provinces')
    .replace(/borough's/gi, 'boroughs')
    .replace(/village's/gi, 'villages')
    .replace(/commune's/gi, 'communes')
    .replace(/canton's/gi, 'cantons')
    .replace(/municipality's/gi, 'municipalities')
    .replace(/prefecture's/gi, 'prefectures')
    .replace(/governorate's/gi, 'governorates')
    .replace(/emirate's/gi, 'emirates')
    .replace(/oblast's/gi, 'oblasts')
    .replace(/state's/gi, 'states')
    .replace(/region's/gi, 'regions')
    .replace(/department's/gi, 'departments')

  // Point-in-region containment fast-path (e.g. "cities in climate zone Cfb with
  // population over 100k") → deterministic route to the point_in_region op.
  const _pir = matchPointInRegion(cleaned)
  if (_pir) return _pir

  // Clip-to-region fast-path (e.g. "show rivers inside Kenya"). Before buffer so
  // an "inside/within <place>" phrasing routes to the clip op.
  const _clip = matchClipToRegion(cleaned)
  if (_clip) return _clip

  // Convex-hull fast-path (e.g. "area covering all volcanoes in Japan").
  const _hull = matchConvexHull(cleaned)
  if (_hull) return _hull

  // Voronoi fast-path (e.g. "divide Japan by its nearest city").
  const _vor = matchVoronoi(cleaned)
  if (_vor) return _vor

  // Centroid fast-path (e.g. "center of each African country").
  const _cent = matchCentroid(cleaned)
  if (_cent) return _cent

  // Union fast-path (e.g. "combine France and Germany") — BEFORE dissolve so a
  // two-operand "A and B" isn't grabbed as a single-continent dissolve.
  const _uni = matchUnion(cleaned)
  if (_uni) return _uni

  // Dissolve fast-path (e.g. "merge the African countries into one").
  const _dis = matchDissolve(cleaned)
  if (_dis) return _dis

  // Erase fast-path (e.g. "Africa without Egypt", "remove Egypt from Africa").
  const _era = matchErase(cleaned)
  if (_era) return _era

  // Intersect fast-path (e.g. "the tropical part of Brazil", "Russia in the Arctic").
  const _int = matchIntersect(cleaned)
  if (_int) return _int

  // Buffer-zone fast-path (e.g. "50 km zone around volcanoes in Indonesia").
  const _buf = matchBuffer(cleaned)
  if (_buf) return _buf

  // ── Interrogative / explanatory pre-guard (CL-144) ────────────────
  // Question-shaped utterances ("Why do volcanoes erupt", "Explain ocean
  // currents", "Is income correlated with…", "Best countries for retirement")
  // must NOT be answered by a dataset/boundary regex or a keyword concept
  // match — a wrong fast-path answer is worse than deferring to the AI
  // (educational streaming / typed tools). Question shapes that the fast path
  // handles BY DESIGN stay allowed: "where is/are …" (navigation, POI),
  // "how many/much/far/long …" (analytics, per-capita, routing), "how do I
  // get / how to get" (routing), "what/which time zone|climate zone|languages
  // …" (dataset lookups), "can you show/find …" (imperative politeness).
  const _law = cleaned.toLowerCase()
  const IMPERATIVE_VERBS =
    /(?:please\s+)?(?:show|display|map|highlight|zoom|go|fly|take|navigate|find|locate|draw|plot|colou?r|give|turn|make|switch)\b/
  if (
    /^(?:why\b|explain\b|describe\b|tell\s+me\b|what\s+can\s+you\b|what\s+is\s+(?:a|an)\b|(?:is|are|was|were|does|did|will|would|should|shall|might|must)\b|do\s+(?!i\b|we\b)|where\s+(?:should|would|could)\b|best\s+(?!way\b))/.test(
      _law
    ) ||
    // "What is the …" is definitional ("What is the ring of fire") UNLESS it
    // continues into a fast-path lookup the parser answers by design
    // ("What is the time zone of Japan", "What's the dominant religion in …").
    (/^what(?:'s|\s+is)\s+the\b/.test(_law) &&
      !/^what(?:'s|\s+is)\s+the\s+(?:time|current\s+time|utc|dominant\b|main\b|major\b|primary\b|shortest\b|fastest\b|quickest\b|best\s+way\b)/.test(
        _law
      )) ||
    (/^how\b/.test(_law) &&
      !/^how\s+(?:many\b|much\b|far\b|long\b|to\s+get\b|do\s+(?:i|we)\b|can\s+(?:i|we)\b)/.test(
        _law
      )) ||
    (/^(?:can|could)\s+(?:you|we|i)\s+/.test(_law) &&
      !new RegExp(`^(?:can|could)\\s+(?:you|we|i)\\s+${IMPERATIVE_VERBS.source}`).test(_law))
  ) {
    return null
  }

  // Spatial-analysis bypass: these analytical phrasings must reach the Worker's
  // typed tools, never a dataset/POI/boundary fast-query. Mirrors the
  // SPATIAL_ANALYSIS_GUARD in concept-registry.js (kept in sync; covered by
  // tests/spatial-eval-harness.test.js). Caught here as a class so a greedy
  // POI/boundary regex can't steal "spatial outliers in life expectancy" or
  // "are the cities of Australia spread out?".
  if (
    /\bspatial\s+outliers?|\blocal\s+(?:moran|clusters?)|\blisa\b|\bgetis|\b(?:hot|cold)\s*spots?|\bautocorrelat\w*|\b(?:spatially|geographically)\s+cluster\w*|\bdeviational\s+ellipse|\bdirectional\s+trend|\bmost\s+central|\bmedian\s+cent\w*|\bweighted\s+cent\w*|\bgravity\s+model|\bspatial\s+interaction|\bgravitational\s+pull|\bpull\s+(?:between|on)\b|\bpopulation\s+(?:change|growth|decline)|\bnearest\s+neighbou?r\s+(?:index|analysis)/.test(
      _law
    ) ||
    (/\bcities\b/.test(_law) &&
      /\bspread\s+out|\bcluster\w*|\bdispers\w*|\bevenly\s+spac\w*/.test(_law))
  ) {
    return null
  }

  // In Moon mode, try lunar fast queries first
  if (globeSwitcher.getCurrentMode() === 'moon') {
    for (const [pattern, builder] of MOON_FAST_QUERIES) {
      const match = cleaned.match(pattern)
      if (match) {
        const result = builder(match)
        if (result) {
          console.log(
            `[IntentParser] Moon fast-query match: "${cleaned}" → pattern ${pattern.source.slice(0, 40)}...`
          )
          return result
        }
      }
    }
  }

  for (const [pattern, builder] of FAST_QUERIES) {
    const match = cleaned.match(pattern)
    if (match) {
      const result = builder(match)
      if (result) {
        console.log(
          `[IntentParser] Fast-query match: "${cleaned}" → pattern ${pattern.source.slice(0, 40)}...`
        )
        // Update globe context so subsequent queries know the geographic focus
        const iso3 = result.country || result.target?.country
        if (iso3) {
          const location = result.target?.location || null
          globeContext.updateFocus(iso3, location, null, null, null)
          globeContext.advanceChain()
        }
        return result
      }
      // Builder returned null — continue trying subsequent patterns
    }
  }

  // ── Raster layer patterns (Phase 5) ─────────────────────────────
  // Checked after all existing patterns so raster "show" commands
  // don't override POI/choropleth/boundary patterns.
  for (const [pattern, builder, test] of RASTER_PATTERNS) {
    const match = cleaned.match(pattern)
    if (match) {
      // If the pattern has a test function, validate the captured group
      if (test && !test(match)) continue
      const result = builder(match)
      if (result) {
        console.log(`[IntentParser] Raster pattern match: "${cleaned}" → ${result.type}`)
        return result
      }
    }
  }

  // ── Phase 2: Concept Registry fallback ──────────────────────────
  // If no regex matched, try the keyword-based concept registry.
  // This catches dataset queries that may not have a regex pattern yet,
  // and will eventually replace the dataset-specific regex blocks above.
  const conceptResult = tryConceptMatch(cleaned)
  if (conceptResult) {
    console.log(
      `[IntentParser] Concept-registry match: "${cleaned}" → ${conceptResult._conceptMatch || conceptResult.type}`
    )
    // Update globe context if applicable
    const iso3 = conceptResult.country || conceptResult.target?.country
    if (iso3) {
      const location = conceptResult.target?.location || null
      globeContext.updateFocus(iso3, location, null, null, null)
      globeContext.advanceChain()
    }
    return conceptResult
  }

  return null
}

/**
 * Parse natural language into a structured intent object.
 * Priority: fast-query shortcuts → Worker API → local regex fallback.
 * @param {string} userText - The spoken/typed text from the user
 * @returns {Promise<object>} Parsed intent object
 */
export async function parseIntent(userText) {
  // ── Conversational turn classification (multi-step + follow-ups) ──
  // See docs/CONVERSATIONAL-AND-COMPOUND-ROADMAP.md and conversation-router.js.
  const turn = classifyTurn(userText)

  // RESET — "start over", "clear the map", "new map" → full clean reset.
  // main.js dispatches type:'reset' to handleReset() (clears layers, legend,
  // boundaries, view). resetChain() here is defensive/idempotent.
  if (turn.kind === 'reset') {
    globeContext.resetChain()
    return { type: 'reset', text: userText }
  }

  // FOLLOW-UP / COMPOUND — context-dependent. These MUST skip the
  // single-utterance fast path: tryFastQuery (and the concept guard inside it)
  // assume a self-contained sentence and would mis-parse a clause like "color
  // it by income" without the active-map context. The context-aware Worker
  // already receives globe_context + history to resolve "it/those/the same".
  // (Compound execution itself lands in Phase 2; for now a compound utterance
  // routes to the Worker as one query, same as today — no regression.)
  const contextual = turn.kind === 'followup' || turn.kind === 'compound'

  // Fast-query shortcuts — instant, deterministic, no AI latency. Fresh turns only.
  if (!contextual) {
    const fast = tryFastQuery(userText)
    if (fast) return fast
  }

  // Detect educational/conversational questions → stream response. Kept for
  // ALL turns, including question-form follow-ups ("why is it red?"), which
  // should explain rather than execute a map action.
  if (isEducationalQuestion(userText)) {
    return {
      type: 'educational',
      text: userText,
      _stream: true,
    }
  }

  // Try API — cross-origin Worker (dev) or same-origin Pages Function (prod)
  try {
    const result = await parseViaWorker(userText)
    if (result) return result
  } catch (err) {
    console.warn('[IntentParser] API failed, using local fallback:', err.message)
  }

  // Fallback: local regex matching
  return createFallbackIntent(userText)
}

/**
 * Detect educational/conversational questions that should get a streaming
 * explanation rather than a structured map action.
 *
 * Excludes analytical queries that look like questions but should hit DuckDB:
 * - "How many schools are in Germany?" → DuckDB COUNT
 * - "Which Indian state has the most hospitals?" → DuckDB analytics
 * - "Why do people live near rivers?" → educational (not river overlay)
 */
function isEducationalQuestion(text) {
  const lower = text.toLowerCase().trim()

  // First check if it matches question form at all
  if (
    !/^(?:tell\s+me\s+about|what\s+is|what\s+are|who\s+(?:are|is|was|were)|why\s+(?:is|are|do|does)|how\s+(?:is|are|do|does|many|much)|explain|describe|when\s+(?:was|did|is)|where\s+(?:is|are|did))\b/.test(
      lower
    )
  ) {
    return false
  }

  // Exclude spatial-analysis queries that should hit the Worker tools
  // (geo_center / geo_proximity — see docs/SPATIAL-ANALYSIS-PLAYBOOK.md):
  // "What is the center of Africa?" / "Where is the middle of Asia?"
  if (/\b(?:cent(?:er|re)|middle|midpoint)\s+of\b/.test(lower)) return false
  // Centrography (geo_centrography): "Which city is most central in Europe?"
  // / "How spread out are Australia's cities?"
  if (/\bmost\s+central\b|\bmedian\s+cent|\bmean\s+cent|\bspread\s+out\b/.test(lower)) return false
  // Spatial autocorrelation (geo_moran): "How clustered is wealth geographically?"
  // — but "what does clustered mean"-style concept questions stay educational.
  if (
    /\b(?:spatially|geographically)\s+cluster\w*|\bautocorrelat\w*/.test(lower) &&
    !/\bwhat\s+does\b|\bexplain\b|\bmean\b\s*\?*$/.test(lower)
  )
    return false
  // Point pattern (geo_point_pattern): "Are Egypt's cities clustered/spread out?"
  if (/\bcities\b/.test(lower) && /\bcluster\w*|\bdispers\w*|\bevenly\s+spac\w*/.test(lower))
    return false
  // Hot spot analysis (geo_hotspots): "Where are the hot spots of infant mortality?"
  if (/\b(?:hot|cold)\s*spots?\b|\bgetis\b/.test(lower)) return false
  // LISA local clusters & spatial outliers (geo_lisa): "Where are the spatial
  // outliers in life expectancy?" / "local clusters of GDP"
  if (
    /\blisa\b|\blocal\s+(?:moran|clusters?)|\bspatial\s+outliers?|\bsurrounded\s+by\b/.test(lower)
  )
    return false
  // Gravity / spatial interaction (geo_gravity): "What's the pull between
  // Tokyo and Osaka?" / "Which city interacts most with Cairo?"
  if (
    /\bgravity\s+model|\bspatial\s+interaction|\b(?:gravitational\s+)?pull\s+(?:between|on)\b|\binteracts?\s+(?:most|with)\b/.test(
      lower
    )
  )
    return false
  // Typology (geo_typology) and GWR (geo_gwr): "What development categories do
  // countries fall into?" / "How does the relationship between GDP and life
  // expectancy vary across the world?"
  if (
    /\btypolog\w*|\bdevelopment\s+typ\w*|\bgeographically\s+weighted|\bgwr\b|\brelationship\b[^.?]*\b(?:vary|varies|change[s]?)\s+(?:across|by|from)/.test(
      lower
    )
  )
    return false
  // Deviational ellipse (geo_deviational_ellipse): "Which way do US cities trend?"
  if (/\bdeviational\s+ellipse|\bdirectional\s+trend/.test(lower)) return false
  // Neighbors / adjacency (geo_neighbors): "Who are Germany's neighbors?" /
  // "Which countries border France?" — the "who are"/"which" forms otherwise
  // look like educational questions.
  if (/\bneighbou?rs?\b|\bborders?\b|\bbordering\b/.test(lower)) return false
  // "What cities are within 100 km of Cairo?" / "How many cities are near Tokyo?"
  if (/\bwithin\s+\d+(?:\.\d+)?\s*(?:km|kilometers?|kilometres?|miles?|mi)\b/.test(lower))
    return false
  if (/\b(?:closest|nearest)\b/.test(lower)) return false
  // "What country is most similar to Japan?" → geo_similarity
  if (/\b(?:similar|most like|alike|resembl|twin)\b/.test(lower)) return false
  // "What is the best place/country for solar farms?" → geo_suitability
  if (/\b(?:best|most suitable|ideal|good)\s+(?:place|countr\w*|location|spot|region)/.test(lower))
    return false

  // Exclude analytical POI/data queries that should go to DuckDB via the Worker API:
  // "How many [POI] in/are in [place]?" / "Which [place] has the most [POI]?"
  const analyticalPOIPattern = new RegExp(
    `(?:how\\s+many|which|what)\\s+.*\\b(${POI_WORDS})\\b`,
    'i'
  )
  if (analyticalPOIPattern.test(lower)) return false

  // Exclude analytical country data queries:
  // "Which European countries have the highest GDP?" / "Which country has the largest area?"
  if (
    /\b(?:which|what)\s+.*\b(?:countr|state|province|nation).*\b(?:gdp|population|area|density|income|largest|smallest|highest|lowest|most|fewest|biggest|richest|poorest)\b/i.test(
      lower
    )
  ) {
    return false
  }
  if (
    /\b(?:highest|lowest|largest|smallest|most|fewest|biggest|richest|poorest)\s+.*\b(?:gdp|population|area|density|income)\b/i.test(
      lower
    )
  ) {
    return false
  }

  // Exclude data-attribute queries where the metric appears before the place/entity:
  // "What is the population density of European countries?"
  // "What is the GDP per capita of African nations?"
  // "What is the life expectancy in Asia?"
  // "What are the literacy rates across Africa?"
  // "What is the Gini index of South American countries?"
  // "How much CO2 does China emit?" / "How much water does India use?"
  const dataAttributePattern =
    /\b(?:population|gdp|gni|gini|density|life\s+expectancy|literacy|infant\s+mortality|co2|carbon|hdi|human\s+development|urban|internet|income|trade|export|import|fertility|unemployment|poverty|debt|inflation|emission|water|food\s+security|fragile|freedom|heritage|refugee|migra)/i
  if (dataAttributePattern.test(lower)) {
    // If the query mentions both a data attribute AND a geographic scope, it's analytical
    if (
      /\b(?:of|in|across|for|among|do|does)\s+(?:the\s+)?(?:\w+\s+)?(?:countr|nation|state|province|region|continent|world|africa|europe|asia|america|oceania|middle\s+east|latin|caribbean|european|african|asian|south\s+american|pacific)/i.test(
        lower
      )
    ) {
      return false
    }
  }

  return true
}

/**
 * Stream an educational response from the Worker API.
 * Returns an async generator yielding text chunks.
 */
export async function* streamEducationalResponse(text) {
  const mode = globeSwitcher.getCurrentMode()
  const response = await fetch(`${API_URL}/api/stream`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...licenseHeaders(), ...classroomHeaders() },
    body: JSON.stringify({ text, history: sessionHistory, mode }),
  })

  if (!response.ok) {
    throw new Error(`Stream API error: ${response.status}`)
  }

  addToHistory('user', text)

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let fullText = ''

  while (true) {
    const { done, value } = await reader.read()
    if (done) break

    buffer += decoder.decode(value, { stream: true })
    const lines = buffer.split('\n')
    buffer = lines.pop() || ''

    for (const line of lines) {
      if (!line.startsWith('data: ')) continue
      const data = line.slice(6)
      if (data === '[DONE]') {
        addToHistory('assistant', fullText)
        return
      }
      try {
        const event = JSON.parse(data)
        if (event.text) {
          fullText += event.text
          yield event.text
        }
        if (event.error) throw new Error(event.error)
      } catch (e) {
        if (e.message !== 'Unexpected end of JSON input') throw e
      }
    }
  }

  if (fullText) addToHistory('assistant', fullText)
}

/**
 * Parse via the Cloudflare Worker API proxy
 */
async function parseViaWorker(userText) {
  const r2Url = getR2Url()
  const mode = globeSwitcher.getCurrentMode()

  // Client-side heuristic reset: if extracted country differs from focus, reset chain
  const extractedCountry = extractCountryFromText(userText)
  const currentFocus = globeContext.getContext().geographic_focus.country
  if (extractedCountry && currentFocus && extractedCountry !== currentFocus) {
    globeContext.resetChain()
  }

  // Check for explicit reset commands
  const lowerText = userText.toLowerCase().trim()
  if (/^(clear|reset|start\s+over)$/i.test(lowerText)) {
    globeContext.resetChain()
  }

  const response = await fetch(`${API_URL}/api/intent`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...licenseHeaders(), ...classroomHeaders() },
    body: JSON.stringify({
      text: userText,
      r2Url,
      history: sessionHistory,
      mode,
      globe_context: globeContext.serialize(),
    }),
  })

  // CL-059: capture per-request correlation ID for bug-report correlation
  const requestId = response.headers.get('X-Request-ID')
  const refTag = requestId ? ` (ref: ${requestId.slice(0, 8)})` : ''

  if (!response.ok) {
    const err = await response.json().catch(() => ({}))
    throw new Error((err.error || `Worker API error: ${response.status}`) + refTag)
  }

  const intent = await response.json()
  if (requestId) intent._request_id = requestId

  // CL-059: surface fallback intents (Worker validation gate caught a bad
  // Haiku response). Log so devs can grep, but render them through normal
  // pipeline — ops/unknown.js handles the user-facing 'I didn't understand'.
  if (intent._fallback) {
    console.warn('[intent-client] fallback intent received', {
      request_id: requestId,
      reason: intent._fallback_reason,
    })
  }

  // Phase 4: Accept both legacy format (intent.type) and pipeline format (intent.pipeline)
  if (!intent.type && !Array.isArray(intent.pipeline) && !intent.intent_type) {
    throw new Error('Invalid intent from Worker: missing type and pipeline' + refTag)
  }

  // Handle context_action from AI response
  if (intent.context_action === 'reset') {
    globeContext.resetChain()
  } else if (intent.context_action === 'branch') {
    globeContext.pushBranch()
  } else if (intent.context_action === 'continue') {
    globeContext.advanceChain()
  }

  // Record the exchange for future context
  addToHistory('user', userText)
  addToHistory('assistant', JSON.stringify(intent))
  globeContext.pushHistory('user', userText)
  globeContext.pushHistory('assistant', JSON.stringify(intent))
  globeContext.incrementQueryIndex()

  return intent
}

/**
 * Extract ISO-3 country code from user text using simple patterns.
 * Used for client-side heuristic chain reset detection.
 */
function extractCountryFromText(text) {
  // Common country name → ISO-3 mappings for quick extraction
  const COUNTRY_PATTERNS = [
    [/\b(?:united\s+states|usa|america|u\.?s\.?a?\.?)\b/i, 'USA'],
    [/\bjapan\b/i, 'JPN'],
    [/\bchina\b/i, 'CHN'],
    [/\bindia\b/i, 'IND'],
    [/\bfrance\b/i, 'FRA'],
    [/\bgermany\b/i, 'DEU'],
    [/\bbrazil\b/i, 'BRA'],
    [/\b(?:united\s+kingdom|britain|england|uk)\b/i, 'GBR'],
    [/\bmexico\b/i, 'MEX'],
    [/\bcanada\b/i, 'CAN'],
    [/\baustralia\b/i, 'AUS'],
    [/\bitaly\b/i, 'ITA'],
    [/\bspain\b/i, 'ESP'],
    [/\brussia\b/i, 'RUS'],
    [/\bsouth\s+korea\b/i, 'KOR'],
    [/\bnigeria\b/i, 'NGA'],
    [/\bsouth\s+africa\b/i, 'ZAF'],
    [/\begypt\b/i, 'EGY'],
    [/\bturkey\b/i, 'TUR'],
    [/\balgeria\b/i, 'DZA'],
    [/\bindonesia\b/i, 'IDN'],
    [/\bpakistan\b/i, 'PAK'],
  ]
  for (const [regex, iso3] of COUNTRY_PATTERNS) {
    if (regex.test(text)) return iso3
  }
  return null
}

/**
 * Create a simple fallback intent for when all API methods fail.
 * Handles basic "go to X" commands using string matching.
 */
function createFallbackIntent(text) {
  const lower = text.toLowerCase().trim()

  // "go to X" or "fly to X" or "show me X" or "take me to X"
  const goToMatch = lower.match(/(?:go|fly|navigate|take me|zoom)\s+to\s+(.+)/)
  if (goToMatch) {
    const location = goToMatch[1].trim()
    return {
      type: 'navigation',
      target: { dataset: 'countries', location },
      operation: { action: 'flyTo' },
      visualization: { type: 'outline' },
      voiceResponse: { shouldSpeak: false },
      _fallback: true,
    }
  }

  // "in [continent], highlight/show countries with/over/above [number] people"
  const filterMatch = lower.match(
    /(?:in\s+)?(\w[\w\s]*?)\s*,?\s*(?:highlight|show|find)\s+.*?(?:countries|nations)\s+.*?(?:over|above|more than|greater than|>\s*)\s*([\d,]+)/
  )
  if (filterMatch) {
    const location = filterMatch[1].trim()
    const value = Number(filterMatch[2].replace(/,/g, ''))
    return {
      type: 'filter',
      target: { dataset: 'countries', location },
      operation: {
        action: 'filterByAttribute',
        attribute: 'POP_EST',
        filter: { operator: '>', value },
      },
      visualization: { type: 'fill', colorScheme: 'sequential', colorRamp: 'oranges' },
      voiceResponse: {
        shouldSpeak: true,
        template: `Here are the countries in ${location} with over ${value.toLocaleString()} people.`,
      },
      _fallback: true,
    }
  }

  // "show [continent] countries by population" / "show me the countries of [continent] by population"
  const choroplethMatch = lower.match(
    /(?:show\s+(?:me\s+)?(?:the\s+)?)?(?:countries\s+(?:of|in)\s+(\w[\w\s]*?)|(\w[\w\s]*?)\s+countries)\s+by\s+(population|gdp|income)/
  )
  if (choroplethMatch) {
    const location = (choroplethMatch[1] || choroplethMatch[2]).trim()
    const attrMap = { population: 'POP_EST', gdp: 'GDP_MD', income: 'INCOME_GRP' }
    const attribute = attrMap[choroplethMatch[3]] || 'POP_EST'
    return {
      type: 'choropleth',
      target: { dataset: 'countries', location },
      operation: { action: 'choropleth', attribute },
      visualization: { type: 'choropleth', colorScheme: 'sequential', colorRamp: 'YlOrRd' },
      voiceResponse: { shouldSpeak: false },
      _fallback: true,
    }
  }

  // "show X" commands
  const showMatch = lower.match(/show\s+(?:me\s+)?(.+)/)
  if (showMatch) {
    return {
      type: 'show',
      target: { dataset: 'countries', location: showMatch[1].trim() },
      operation: { action: 'showAll' },
      visualization: { type: 'fill', colorScheme: 'categorical' },
      voiceResponse: { shouldSpeak: false },
      _fallback: true,
    }
  }

  // Unknown — treat as navigation
  return {
    type: 'navigation',
    target: { dataset: 'countries', location: text },
    operation: { action: 'flyTo' },
    visualization: { type: 'outline' },
    voiceResponse: { shouldSpeak: false },
    _fallback: true,
  }
}

export default parseIntent
